/* ========================================================================
   DRIVE IQ — FIND STOPS V28.7 / GOOGLE ASK HANDOFF
   Shopify asset: drive-iq-find-stops.js

   Loads after drive-iq-drive-mode.js.

   V27 Accurate Nearby Stop Authority:
   - Makes physical proximity the first ranking gate for generic stop/category scans.
   - Uses route detour as a secondary tiebreaker, not permission to skip a much closer stop.
   - Adds first-class Pharmacy scanning with Walgreens, Kroger Pharmacy, CVS, Walmart Pharmacy,
     Rite Aid, local pharmacy, drugstore, and prescription-pharmacy discovery.
   - Preserves each scan point's real nearby/ahead/far lane instead of labeling every result "ahead".
   - Routes generic typed category searches into the richer category scan engine.
   - Keeps all scan markers/passive ownership contracts intact.

   V26 Route-Centered Scan + Verified Waypoint Draw:
   - Keeps Stop IQ scan view centered on the active route corridor and recommended stop.
   - Prevents all-result bounds from pulling the map into an empty midpoint.
   - Verifies that DriveIQRouteEngine accepted geometry through the selected stop.
   - Emits modular redraw events only after live waypoint geometry is confirmed.
   - Falls back to the exact rebuilt GeoJSON source without letting old-route events overwrite it.

   V25 Modular Waypoint Route Promotion:
   - Promotes the rebuilt GPS -> stop -> final destination route into DriveIQRouteEngine immediately.
   - Emits modular route events so DriveModeBoot redraws the added side leg without reload.
   - Updates the live map source as a final visual backstop.
   - Preserves the original final destination, Stop IQ pins, camera, voice, and ranking.

   V24 Stop IQ Live Map Authority:
   - Removes the exact hard-fail still present inside scanStops().
   - Resolves the map from Drive Mode, shared map globals, or bridge APIs.
   - Queues the selected category and retries automatically for up to six seconds.
   - Preserves the active route, destination, pin, camera, and ranking logic.

   V22 Stop IQ Closest Authority:
   - Makes the closest valid exact-brand match the default first result.
   - Balances live distance, ahead/behind direction, route-return cost,
     exact-name confidence, and practical access.
   - Prevents a farther route-ahead result from outranking a much closer
     useful stop without a clear route advantage.
   - Adds explanation labels such as Closest, Ahead on Route, and Lowest Detour.
   - Updates cockpit copy from “best route fit” to “closest useful stop.”
   - Keeps the original destination intact when adding the selected stop.

   V21 Stop IQ Search Authority:
   - Evolves Find Stops into a clear Stop Along the Way experience.
   - Adds a visible search field for brands and businesses such as Walmart,
     Target, Walgreens, Starbucks, grocery stores, pharmacies, and restaurants.
   - Adds Walmart as a high-value one-tap shortcut.
   - Ranks route-ahead, low-detour results above generic nearby matches.
   - Preserves the final destination when inserting the selected stop.
   - Labels the Drive Mode action clearly instead of relying on an ambiguous plus.
   - Keeps all existing district, cluster, pin, card, and route rebuild behavior.

   V20 polish:
   - Adds District Intelligence Foundation across Food, Grocery, Fuel, Coffee, Shopping, Hotel, Wellness, and Scenic scans.
   - Adds District Score foundation so clusters explain why they are recommended, not just what exists.
   - Adds category-specific district titles, subtitles, badges, and CTAs for best access / best variety / quick return.
   - Adds stronger hotel, fuel, grocery, coffee, and shopping scan language while preserving V19 map calmness.

   V19 polish kept:
   - Adds Commercial Corridor Intelligence: Restaurant Row, Shopping District, Fuel Corridor, Coffee Corridor, and Wellness District naming.
   - Adds V19 Route Impact prediction with added minutes / miles style trust copy before and after adding stops.
   - Adds stronger Brand Hierarchy Engine so the best recognizable anchor names the district, not just the first result.
   - Adds Preferred Stops foundation via diq_preferred_stops_v1 for future personal ranking.
   - Tightens Scan View camera clamp so stop scans zoom out less and keep route context.

   V18 polish kept:
   - Adds Food District Expansion Engine: district cards now show a stronger title, corridor-style subtitle, top choices list, and one-tap ADD actions.
   - Adds corridor preview foundation with human labels like Nearby Food District, Route Food District, and Destination Food District.
   - Adds smarter district preview language: brand anchors, brand subtitles, route-return copy, and "Top choices" presentation.
   - Refines cluster scoring and display so major brands and easy-return stops feel like intentional recommendations instead of raw pins.

   V17 polish kept:
   - Adds Food District Preview Engine so clusters show real brand anchors like Chick-fil-A +2, McDonald's +3, or brand subtitles instead of generic Food District copy.
   - Fixes DOM cluster bubble taps so tapping the bubble opens the expandable cluster list instead of the generic Add Stop card.
   - Adds stronger major-brand ranking and preview helpers for commercial corridors.
   - Improves cluster card subtitles with actual brand names, easy-access copy, and best-access ordering.

   V16 polish kept:
   - Adds route-aware Food District behavior so nearby restaurants cluster more intelligently instead of fighting the destination pin.
   - Suppresses generic Food results when a known brand is already in the same commercial pocket.
   - Improves cluster radius and cluster cards for dense restaurant corridors.
   - Refines stop-card copy so QUICK STOP / EASY ACCESS labels are not repeated.
   - Fits Scan View to display clusters instead of every raw restaurant point, keeping the camera calmer.

   V15 polish:
   - Adds Apple/Google-style zoom behavior: full bubbles up close, compact bubbles mid-zoom, anchor dots only when zoomed out.
   - Adds Stop Quality labels: BEST STOP, QUICK STOP, EASY ACCESS, MINIMAL DETOUR, SHORT DETOUR, OFF CORRIDOR.
   - Upgrades cluster language from generic food counts to Food Options / Food District style cards.
   - Expands brand-aware food discovery for Chick-fil-A, McDonald's, Whataburger, Waffle House, Wendy's, Captain D's, Sonic, Taco Bell, Burger King, Arby's, Popeyes, Zaxby's, Raising Cane's, Starbucks, Dunkin, and more.
   - Strengthens Featured Local Pick presentation.

   V14 polish kept:
   - Adds brand-aware food cluster intelligence so Chick-fil-A, McDonald's, Whataburger, Waffle House, Wendy's, and Captain D's surface ahead of generic food.
   - Anchors clusters to the highest-ranked real POI instead of the average center.
   - Cluster cards now show actual business names with one-tap ADD buttons.
   - Keeps gentle Scan View camera so food scans do not zoom way out over the city.

   V13 polish kept:
   - Adds Smart Cluster Engine so dense food/commercial areas show as clean +N clusters.
   - Adds cluster tap expansion with selectable stop options.
   - Adds gentler Scan View camera: free view opens without zooming way out over the city.
   - Keeps True Stop Dot System, anchored dots, floating bubbles, connector stems, and ADD STOP waypoint behavior.

   V12 polish kept:
   - Adds True Stop Dot System: the exact POI coordinate is now a tiny anchored dot.
   - Emoji bubbles float above the dot with a connector stem, so users can trust the real location.
   - Adds zoom-aware bubble shrinking/hiding to reduce drifting illusion when zoomed out.
   - Keeps ADD STOP waypoint behavior.

   V11 polish kept:
   - Adds Stop Pin Anchor Lock so scan pins stay glued to true POI coordinates.
   - Removes screen-space marker offsets that made pins appear to wander during zoom.
   - Keeps badge/crown/card visual polish while protecting location trust.
   - Keeps ADD STOP waypoint behavior.

   V10 polish kept:
   - Adds smarter marker collision spacing.
   - Adds route-aware ranking boost for stops close to the route.
   - Reduces generic Food fallback by using stronger type badges.
   - Moves trophy higher.
   - Adds tap pulse when a stop pin is selected.
   - Keeps ADD STOP waypoint behavior.

   V9 upgrade kept:
   - Tapping a stop opens a tiny Drive IQ stop card.
   - Adds ADD STOP action.
   - ADD STOP rebuilds route as GPS -> stop -> destination using Geoapify.
   - Saves waypoint and updated route back into Drive Mode localStorage.
   - Keeps Follow Lock paused until user chooses Follow Lock again.

   V8 polish kept:
   - Reduces glow so food pins do not overpower the route.
   - Moves trophy upward and shrinks it so it does not collide with the best pin.
   - Adds tiny brand/food-type badge under each pin.
   - Adds simple spread offsets to reduce vertical clumping.
   - Keeps food scan visible, fun, and map-first.

   V7 fix kept:
   - Stop results now surface using DOM Mapbox markers instead of relying on Mapbox symbol emoji support.
   - Food icons are real HTML emojis with glow, z-index, tap handlers, and crown marker.
   - Keeps the GeoJSON source optional but the visible result no longer depends on Mapbox text rendering.

   V6 fixes kept:
   - Food pins become obvious scan rewards.
   - Stronger emoji markers with contrast discs.
   - Shows top 6 only so the map stays clean.
   - Best stop gets crown-only pulse/glow.
   - Adds optional voice summary if Drive Mode exposes speech later.

   V5 fixes kept:
   - Food scan now looks like food, not debug labels.
   - Removed floating GOOD/TRUSTED/LIMITED map text.
   - BEST AHEAD becomes a tiny crown-only marker.
   - Pins stay clean, tappable, and map-first.

   V4 upgrades kept:
   - Premium category icons instead of generic letters.
   - Confidence glow + best-stop crown.
   - Larger tap targets.
   - Route-side emphasis.
   - Stronger stop intelligence labels.

   V3 foundation kept:
   - Search current GPS area first.
   - Then search short route-ahead corridor.
   - Then search farther route-ahead corridor.
   - Broader food search terms/categories.
   - Better empty state.
   - Stop info on pin click.
   - Best Stop Ahead label.
   - Improved Route IQ card text.
   - Keeps cockpit/follow logic isolated.
   ES5-safe.
======================================================================== */
(function(){
"use strict";

/* ============================================================================
   V200 PASSIVE OWNERSHIP CONTRACT
   - Search/rank/filter/render POIs: ALLOWED
   - Stop cards and stop markers: ALLOWED
   - Reading live route/fix for ranking: ALLOWED
   - Route rebuild/install/promotion: FORBIDDEN
   - Active route GeoJSON source mutation: FORBIDDEN
   - route:installed / route:ready / route:geometry emits: FORBIDDEN
   - map fit/ease/recenter/follow changes: FORBIDDEN
   - page reload/navigation: FORBIDDEN
   - recurring watchdog/poller timers: FORBIDDEN

   The selected stop is handed to the active Drive Mode/native bridge.
   ============================================================================ */


if(window.__DRIVE_IQ_FIND_STOPS_V200_PASSIVE__){ return; }
window.__DRIVE_IQ_FIND_STOPS_V200_PASSIVE__ = true;

var GEOAPIFY_KEY = String(window.DRIVE_IQ_GEOAPIFY_KEY || window.SLX_GEOAPIFY_KEY || "");
var activeStops = [];
var scanBusy = false;
var scanToken = 0;
var currentScanType = "stops";
var stopMarkers = [];
var activeDisplayStops = [];
var selectedStop = null;
var stopCardEl = null;
var stopSearchQuery = "";
var stopSearchResults = [];
var stopSearchInputEl = null;
var stopSearchButtonEl = null;

/* V28 ASK IQ provider bridge state */
var askPendingRequestId = "";
var askPendingQuery = "";
var askPendingIntent = null;
var askPendingOriginalQuery = "";
var askBridgeBound = false;

/* V24 live-map readiness authority */
var pendingStopScanType = "";
var pendingStopScanTimer = null;
var pendingStopScanAttempts = 0;
var pendingStopScanMaxAttempts = 24;
var pendingStopScanDelayMs = 250;

var LS = {
  ROUTE_GEO:"diq_route_geo_v1",
  ROUTE_INTEL:"diq_route_intel_v1",
  DEST:"diq_dest_v1",
  LAST_FIX:"diq_last_gps_v1",
  DRIVE_PAYLOAD:"diq_drive_payload_v1",
  ADDED_STOP:"diq_added_stop_v1",
  PREFERRED_STOPS:"diq_preferred_stops_v1",
  DISTRICT_MEMORY:"diq_district_memory_v1"
};

function byId(id){ return document.getElementById(id); }

function finite(v){ return typeof v === "number" && isFinite(v); }

function safeParse(v){
  try{ return JSON.parse(String(v || "")); }
  catch(e){ return null; }
}

function lsGet(k){
  try{ return localStorage.getItem(k); }
  catch(e){ return null; }
}

function lsSet(k,v){
  try{ localStorage.setItem(k,String(v)); }
  catch(e){}
}

function lsJSON(k,v){
  try{ localStorage.setItem(k,JSON.stringify(v)); }
  catch(e){}
}

function toast(text){
  var el = byId("diqToast");
  if(!el){ return; }
  el.textContent = String(text || "Ready");
  el.className = "diqToast show";
  window.setTimeout(function(){ el.className = "diqToast"; },1500);
}

function setText(id,text){
  var el = byId(id);
  if(el){ el.textContent = String(text || ""); }
}


function escapeHtml(value){
  return String(value || "")
    .replace(/&/g,"&amp;")
    .replace(/</g,"&lt;")
    .replace(/>/g,"&gt;")
    .replace(/"/g,"&quot;")
    .replace(/'/g,"&#39;");
}

function getMap(){
  var live = null;

  try{
    live =
      window.DRIVE_IQ_DRIVE_MAP ||
      window.DRIVE_IQ_MAP ||
      window.DRIVE_IQ_START_MAP ||
      window.map ||
      null;

    if(live){ return live; }
  }catch(e1){}

  try{
    if(window.DRIVE_IQ_DRIVE_MODE &&
       typeof window.DRIVE_IQ_DRIVE_MODE.getMap === "function"){
      live = window.DRIVE_IQ_DRIVE_MODE.getMap();
      if(live){ return live; }
    }
  }catch(e2){}

  try{
    if(window.DRIVE_IQ_INCIDENT_BRIDGE &&
       typeof window.DRIVE_IQ_INCIDENT_BRIDGE.getMap === "function"){
      live = window.DRIVE_IQ_INCIDENT_BRIDGE.getMap();
      if(live){ return live; }
    }
  }catch(e3){}

  return null;
}

function getRouteGeo(){
  var apiRoute = window.DRIVE_IQ_DRIVE_MODE && typeof window.DRIVE_IQ_DRIVE_MODE.getRoute === "function" ? window.DRIVE_IQ_DRIVE_MODE.getRoute() : null;
  var payload;
  var route;

  if(apiRoute && apiRoute.features && apiRoute.features.length){
    return apiRoute;
  }

  payload = safeParse(lsGet("diq_drive_payload_v1"));

  if(payload && payload.route && payload.route.features && payload.route.features.length){
    return payload.route;
  }

  route = safeParse(lsGet("diq_route_geo_v1"));

  if(route && route.features && route.features.length){
    return route;
  }

  return null;
}

function getRouteCoords(){
  var route = getRouteGeo();
  var feature;

  if(!route || !route.features || !route.features.length){ return null; }

  feature = route.features[0];

  if(!feature || !feature.geometry || !feature.geometry.coordinates){ return null; }

  return feature.geometry.coordinates;
}

function getCurrentFix(){
  var apiFix = window.DRIVE_IQ_DRIVE_MODE && typeof window.DRIVE_IQ_DRIVE_MODE.getFix === "function" ? window.DRIVE_IQ_DRIVE_MODE.getFix() : null;
  var saved;

  if(apiFix && finite(Number(apiFix.lat)) && finite(Number(apiFix.lng))){
    return { lat:Number(apiFix.lat), lng:Number(apiFix.lng) };
  }

  saved = safeParse(lsGet("diq_last_gps_v1"));

  if(saved && finite(Number(saved.lat)) && finite(Number(saved.lng))){
    return { lat:Number(saved.lat), lng:Number(saved.lng) };
  }

  return null;
}

function milesBetween(a,b){
  var R = 3958.8;
  var dLat = (Number(b[1]) - Number(a[1])) * Math.PI / 180;
  var dLng = (Number(b[0]) - Number(a[0])) * Math.PI / 180;
  var lat1 = Number(a[1]) * Math.PI / 180;
  var lat2 = Number(b[1]) * Math.PI / 180;
  var x =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.sin(dLng/2) * Math.sin(dLng/2) * Math.cos(lat1) * Math.cos(lat2);

  return R * 2 * Math.atan2(Math.sqrt(x),Math.sqrt(1 - x));
}

function fmtMiles(mi){
  var n = Number(mi);
  if(!finite(n)){ return "nearby"; }
  if(n < 0.1){ return "0.1 mi"; }
  if(n >= 100){ return String(Math.round(n)) + " mi"; }
  return String(Math.round(n * 10) / 10) + " mi";
}

function stopType(type){
  type = String(type || "").toLowerCase();

  if(type === "gas" || type === "diesel"){ return "fuel"; }
  if(type === "walmart"){ return "walmart"; }
  if(type === "grocery" || type === "groceries"){ return "grocery"; }
  if(type === "pharmacy" || type === "pharmacies" || type === "drugstore" || type === "drugstores"){ return "pharmacy"; }
  if(type === "restroom" || type === "restrooms" || type === "bathroom" || type === "bathrooms"){ return "restroom"; }
  if(type === "quiet" || type === "sleep" || type === "rest"){ return "quiet"; }
  if(type === "ev" || type === "charger" || type === "charging"){ return "ev"; }
  if(type === "repair" || type === "service"){ return "repair"; }
  if(type === "shopping" || type === "shop" || type === "shops" || type === "boutique" || type === "boutiques" || type === "mall" || type === "gift" || type === "giftshop" || type === "gift shop" || type === "outlet" || type === "outlets" || type === "tanger" || type === "factory store" || type === "local shopping" || type === "local shops" || type === "scenic shopping"){ return "shopping"; }
  if(type === "scenic" || type === "view" || type === "views" || type === "park" || type === "trail" || type === "trails" || type === "overlook" || type === "photo" || type === "photos"){ return "scenic"; }
  if(type === "wellness" || type === "smoothie" || type === "smoothies" || type === "juice" || type === "nutrition" || type === "protein" || type === "shake" || type === "shakes" || type === "healthy" || type === "energy"){ return "wellness"; }

  if(type === "fuel" || type === "food" || type === "coffee" || type === "wellness" || type === "shopping" || type === "scenic" || type === "parking" || type === "hotel" || type === "pharmacy" || type === "restroom" || type === "quiet" || type === "ev" || type === "repair" || type === "walmart"){ return type; }

  return "stops";
}

function searchTexts(type){
  type = stopType(type);

  if(type === "fuel"){
    return [
      "loves truck stop",
      "love's",
      "pilot travel center",
      "flying j",
      "shell",
      "circle k",
      "murphy usa",
      "exxon",
      "bp",
      "marathon",
      "chevron",
      "valero",
      "gas station",
      "fuel",
      "diesel"
    ];
  }
  if(type === "food"){
    return [
      "chick-fil-a",
      "chick fil a",
      "chickfila",
      "mcdonalds",
      "mcdonald's",
      "whataburger",
      "waffle house",
      "wendy's",
      "wendys",
      "captain d's",
      "captain ds",
      "sonic drive-in",
      "sonic",
      "taco bell",
      "burger king",
      "arby's",
      "arbys",
      "popeyes",
      "zaxby's",
      "zaxbys",
      "raising cane's",
      "raising canes",
      "kfc",
      "subway",
      "starbucks",
      "dunkin",
      "pizza hut",
      "domino's",
      "dominos",
      "papa john's",
      "fast food",
      "restaurant",
      "food",
      "cafe",
      "coffee"
    ];
  }
  if(type === "coffee"){
    return [
      "starbucks",
      "dunkin",
      "dutch bros",
      "7 brew",
      "scooter's coffee",
      "coffee",
      "cafe",
      "espresso",
      "coffee shop",
      "local coffee"
    ];
  }
  if(type === "wellness"){
    return [
      "smoothie king",
      "tropical smoothie cafe",
      "smoothie shop",
      "smoothies",
      "juice bar",
      "juice and smoothie",
      "nutrition shake",
      "protein shake",
      "nutrition club",
      "loaded tea",
      "energy tea",
      "herbalife nutrition",
      "meal replacement shake",
      "acai bowl",
      "açaí bowl",
      "wellness shots",
      "clean juice",
      "planet smoothie",
      "jamba juice",
      "playa bowls",
      "vitality bowls",
      "nekter juice",
      "smoothie cafe",
      "nutrition spot",
      "nutrition shop"
    ];
  }
  if(type === "shopping"){
    return [
      "target",
      "walmart",
      "tanger outlets",
      "tanger outlets southaven",
      "outlets of mississippi",
      "outlet mall",
      "outlet center",
      "outlet shops",
      "premium outlets",
      "factory stores",
      "tj maxx",
      "marshalls",
      "ross",
      "best buy",
      "academy sports",
      "boutique",
      "local boutique",
      "women's boutique",
      "gift shop",
      "gift boutique",
      "souvenir shop",
      "local shops",
      "shopping mall",
      "shopping center",
      "town square shops",
      "main street shops",
      "clothing boutique",
      "jewelry boutique",
      "home decor shop",
      "antique shop",
      "vintage shop",
      "bookstore",
      "record store",
      "candle shop",
      "toy shop",
      "art gallery shop"
    ];
  }
  if(type === "scenic"){
    return [
      "park",
      "trail",
      "scenic view",
      "overlook",
      "river view",
      "lake",
      "photo spot",
      "nature preserve",
      "greenway",
      "walking trail",
      "tourist attraction",
      "landmark"
    ];
  }
  if(type === "pharmacy"){
    return [
      "walgreens",
      "walgreens pharmacy",
      "kroger pharmacy",
      "cvs pharmacy",
      "cvs",
      "walmart pharmacy",
      "rite aid",
      "pharmacy",
      "drugstore",
      "prescription pharmacy",
      "local pharmacy"
    ];
  }
  if(type === "parking"){ return ["parking"]; }
  if(type === "grocery"){
    return [
      "walmart supercenter",
      "walmart neighborhood market",
      "kroger",
      "aldi",
      "target grocery",
      "sam's club",
      "sams club",
      "costco",
      "supermarket",
      "grocery",
      "grocery store",
      "market"
    ];
  }
  if(type === "hotel"){
    return [
      "hampton inn",
      "holiday inn",
      "marriott",
      "courtyard",
      "hilton",
      "hyatt",
      "fairfield inn",
      "comfort inn",
      "la quinta",
      "best western",
      "motel 6",
      "hotel",
      "motel",
      "lodging"
    ];
  }
  if(type === "restroom"){ return ["restroom","public restroom","gas station restroom","family restroom"]; }
  if(type === "quiet"){ return ["park","rest area","quiet parking","hotel","motel"]; }
  if(type === "ev"){ return ["ev charger","charging station"]; }
  if(type === "repair"){ return ["auto repair","service station","tire repair"]; }

  return ["restaurant","gas station","parking","restroom"];
}


function isWellnessName(name){
  var s = String(name || "").toLowerCase();

  if(s.indexOf("smoothie") > -1){ return true; }
  if(s.indexOf("juice") > -1){ return true; }
  if(s.indexOf("nutrition") > -1){ return true; }
  if(s.indexOf("protein") > -1){ return true; }
  if(s.indexOf("shake") > -1){ return true; }
  if(s.indexOf("acai") > -1 || s.indexOf("açaí") > -1){ return true; }
  if(s.indexOf("wellness") > -1){ return true; }
  if(s.indexOf("loaded tea") > -1){ return true; }
  if(s.indexOf("tea bomb") > -1){ return true; }
  if(s.indexOf("mega tea") > -1){ return true; }
  if(s.indexOf("herbalife") > -1){ return true; }
  if(s.indexOf("smoothie king") > -1){ return true; }
  if(s.indexOf("tropical smoothie") > -1){ return true; }
  if(s.indexOf("planet smoothie") > -1){ return true; }
  if(s.indexOf("jamba") > -1){ return true; }
  if(s.indexOf("clean juice") > -1){ return true; }
  if(s.indexOf("playa bowls") > -1){ return true; }
  if(s.indexOf("vitality bowls") > -1){ return true; }
  if(s.indexOf("juice it up") > -1){ return true; }
  if(s.indexOf("nekter") > -1){ return true; }
  if(s.indexOf("smoothie bar") > -1){ return true; }
  if(s.indexOf("juice bar") > -1){ return true; }
  if(s.indexOf("nutrition club") > -1){ return true; }
  if(s.indexOf("nutrition spot") > -1){ return true; }
  if(s.indexOf("nutrition shop") > -1){ return true; }
  if(s.indexOf("nutrition bar") > -1){ return true; }
  if(s.indexOf("energy tea") > -1){ return true; }
  if(s.indexOf("loaded teas") > -1){ return true; }
  if(s.indexOf("meal replacement") > -1){ return true; }
  if(s.indexOf("refresher") > -1 && s.indexOf("coffee") === -1){ return true; }
  if(s.indexOf("fit") > -1 && s.indexOf("nutrition") > -1){ return true; }

  return false;
}

function isExcludedShoppingName(name){
  var s = String(name || "").toLowerCase();

  /*
    Shopping in Drive IQ should feel local/lifestyle first,
    not errands, gas, grocery, pharmacy, emergency, or big-box default.
  */
  if(s.indexOf("fire department") > -1 || s.indexOf("police") > -1 || s.indexOf("sheriff") > -1 || s.indexOf("city hall") > -1){ return true; }
  if(s.indexOf("hospital") > -1 || s.indexOf("clinic") > -1 || s.indexOf("urgent care") > -1){ return true; }
  if(s.indexOf("circle k") > -1 || s.indexOf("shell") > -1 || s.indexOf("exxon") > -1 || s.indexOf("bp") > -1 || s.indexOf("marathon") > -1 || s.indexOf("chevron") > -1 || s.indexOf("valero") > -1){ return true; }
  if(s.indexOf("kroger") > -1 || s.indexOf("aldi") > -1 || s.indexOf("walmart") > -1 || s.indexOf("sam's club") > -1 || s.indexOf("sams club") > -1 || s.indexOf("costco") > -1){ return true; }
  if(s.indexOf("walgreens") > -1 || s.indexOf("cvs") > -1 || s.indexOf("rite aid") > -1 || s.indexOf("pharmacy") > -1){ return true; }
  if(s.indexOf("dollar general") > -1 || s.indexOf("family dollar") > -1 || s.indexOf("dollar tree") > -1){ return true; }
  if(s.indexOf("autozone") > -1 || s.indexOf("o'reilly") > -1 || s.indexOf("advance auto") > -1 || s.indexOf("napa auto") > -1){ return true; }
  if(s.indexOf("bank") > -1 || s.indexOf("atm") > -1 || s.indexOf("insurance") > -1){ return true; }
  if(s.indexOf("church") > -1 || s.indexOf("school") > -1 || s.indexOf("academy") > -1){ return true; }

  return false;
}

function isShoppingName(name){
  var s = String(name || "").toLowerCase();

  if(isExcludedShoppingName(s)){ return false; }

  if(s.indexOf("tanger") > -1){ return true; }
  if(s.indexOf("outlets of mississippi") > -1){ return true; }
  if(s.indexOf("premium outlet") > -1 || s.indexOf("premium outlets") > -1){ return true; }
  if(s.indexOf("outlet mall") > -1 || s.indexOf("outlet center") > -1 || s.indexOf("outlet centre") > -1){ return true; }
  if(s.indexOf("outlet shops") > -1 || s.indexOf("factory store") > -1 || s.indexOf("factory stores") > -1){ return true; }

  if(s.indexOf("boutique") > -1){ return true; }
  if(s.indexOf("gift") > -1){ return true; }
  if(s.indexOf("souvenir") > -1){ return true; }
  if(s.indexOf("mall") > -1){ return true; }
  if(s.indexOf("shopping center") > -1 || s.indexOf("shopping centre") > -1){ return true; }
  if(s.indexOf("shops at") > -1 || s.indexOf("the shops") > -1){ return true; }
  if(s.indexOf("marketplace") > -1 || s.indexOf("market place") > -1){ return true; }
  if(s.indexOf("town square") > -1 || s.indexOf("main street") > -1){ return true; }
  if(s.indexOf("clothing") > -1 || s.indexOf("apparel") > -1 || s.indexOf("fashion") > -1){ return true; }
  if(s.indexOf("jewelry") > -1 || s.indexOf("jewelers") > -1){ return true; }
  if(s.indexOf("home decor") > -1 || s.indexOf("home décor") > -1 || s.indexOf("decor") > -1){ return true; }
  if(s.indexOf("antique") > -1 || s.indexOf("vintage") > -1 || s.indexOf("thrift") > -1 || s.indexOf("consignment") > -1){ return true; }
  if(s.indexOf("bookstore") > -1 || s.indexOf("books") > -1){ return true; }
  if(s.indexOf("record store") > -1 || s.indexOf("vinyl") > -1){ return true; }
  if(s.indexOf("candle") > -1 || s.indexOf("toy shop") > -1 || s.indexOf("toy store") > -1){ return true; }
  if(s.indexOf("florist") > -1 || s.indexOf("flower shop") > -1){ return true; }
  if(s.indexOf("art gallery") > -1 || s.indexOf("gallery") > -1){ return true; }
  if(s.indexOf("outlet") > -1){ return true; }

  /* Local shop naming signals. */
  if(s.indexOf(" shop") > -1 || s.indexOf(" shoppe") > -1){ return true; }

  return false;
}

function categories(type){
  type = stopType(type);

  if(type === "fuel"){ return "service.vehicle.fuel"; }
  if(type === "food"){ return "catering.restaurant,catering.fast_food,catering.cafe"; }
  if(type === "coffee"){ return "catering.cafe"; }
  if(type === "wellness"){ return "catering.cafe,catering.restaurant"; }
  if(type === "shopping"){ return "commercial.shopping_mall,commercial.clothing,commercial.gift_and_souvenir,commercial.books,commercial.antiques,commercial.art"; }
  if(type === "scenic"){ return "leisure.park,tourism.attraction,natural,entertainment.museum"; }
  if(type === "parking"){ return "parking"; }
  if(type === "grocery"){ return "commercial.supermarket,commercial.food_and_drink"; }
  if(type === "pharmacy"){ return "healthcare.pharmacy"; }
  if(type === "hotel"){ return "accommodation.hotel"; }
  if(type === "restroom"){ return "amenity.toilet,service.vehicle.fuel"; }
  if(type === "quiet"){ return "leisure.park,accommodation.hotel,parking"; }
  if(type === "ev"){ return "service.vehicle.charging_station"; }
  if(type === "repair"){ return "service.vehicle.repair,service.vehicle.tyres"; }

  return "catering.restaurant,service.vehicle.fuel,parking,amenity.toilet";
}

function iconForFoodName(name){
  var s = String(name || "").toLowerCase();

  if(s.indexOf("coffee") > -1 || s.indexOf("starbucks") > -1 || s.indexOf("dunkin") > -1 || s.indexOf("cafe") > -1 || s.indexOf("espresso") > -1){ return "☕"; }
  if(s.indexOf("pizza") > -1 || s.indexOf("domino") > -1 || s.indexOf("papa john") > -1 || s.indexOf("little caes") > -1 || s.indexOf("marco") > -1){ return "🍕"; }
  if(s.indexOf("taco") > -1 || s.indexOf("mexican") > -1 || s.indexOf("burrito") > -1 || s.indexOf("chipotle") > -1 || s.indexOf("moe") > -1){ return "🌮"; }
  if(s.indexOf("chicken") > -1 || s.indexOf("chick") > -1 || s.indexOf("kfc") > -1 || s.indexOf("popeyes") > -1 || s.indexOf("zaxby") > -1 || s.indexOf("wing") > -1){ return "🍗"; }
  if(s.indexOf("subway") > -1 || s.indexOf("sandwich") > -1 || s.indexOf("deli") > -1 || s.indexOf("jimmy john") > -1 || s.indexOf("jersey mike") > -1){ return "🥪"; }
  if(isWellnessName(s) || s.indexOf("bowl") > -1 || s.indexOf("tea") > -1){ return "🥤"; }
  if(s.indexOf("donut") > -1 || s.indexOf("doughnut") > -1 || s.indexOf("bakery") > -1 || s.indexOf("krispy") > -1){ return "🍩"; }
  if(s.indexOf("burger") > -1 || s.indexOf("mcdonald") > -1 || s.indexOf("wendy") > -1 || s.indexOf("sonic") > -1 || s.indexOf("five guys") > -1 || s.indexOf("whataburger") > -1 || s.indexOf("burger king") > -1){ return "🍔"; }

  return "🍴";
}


function brandShortName(name,type){
  var s = String(name || "").toLowerCase();

  if(stopType(type) === "food"){
    if(s.indexOf("mcdonald") > -1){ return "McD"; }
    if(s.indexOf("wendy") > -1){ return "Wendy"; }
    if(s.indexOf("sonic") > -1){ return "Sonic"; }
    if(s.indexOf("burger king") > -1){ return "BK"; }
    if(s.indexOf("taco bell") > -1){ return "Taco"; }
    if(s.indexOf("chipotle") > -1){ return "Chipotle"; }
    if(s.indexOf("subway") > -1){ return "Subway"; }
    if(s.indexOf("domino") > -1){ return "Domino"; }
    if(s.indexOf("pizza hut") > -1){ return "Pizza"; }
    if(s.indexOf("starbucks") > -1){ return "Coffee"; }
    if(s.indexOf("dunkin") > -1){ return "Dunkin"; }
    if(s.indexOf("kfc") > -1){ return "KFC"; }
    if(s.indexOf("popeyes") > -1){ return "Chicken"; }
    if(s.indexOf("chick") > -1){ return "Chicken"; }
    if(s.indexOf("donut") > -1 || s.indexOf("doughnut") > -1){ return "Donut"; }
    if(s.indexOf("mexican") > -1 || s.indexOf("taco") > -1){ return "Taco"; }
    if(s.indexOf("pizza") > -1){ return "Pizza"; }
    if(s.indexOf("coffee") > -1 || s.indexOf("cafe") > -1){ return "Coffee"; }

    if(s.indexOf("grill") > -1 || s.indexOf("restaurant") > -1 || s.indexOf("diner") > -1){ return "Eat"; }
    return "Food";
  }

  if(stopType(type) === "fuel"){ return "Fuel"; }
  if(stopType(type) === "coffee"){ return "Coffee"; }
  if(stopType(type) === "wellness"){
    if(s.indexOf("smoothie") > -1){ return "Smoothie"; }
    if(s.indexOf("juice") > -1){ return "Juice"; }
    if(s.indexOf("nutrition") > -1){ return "Nutrition"; }
    if(s.indexOf("protein") > -1 || s.indexOf("shake") > -1){ return "Shake"; }
    if(s.indexOf("acai") > -1 || s.indexOf("açaí") > -1 || s.indexOf("bowl") > -1){ return "Açaí"; }
    if(s.indexOf("tea") > -1){ return "Tea"; }
    return "Wellness";
  }
  if(stopType(type) === "shopping"){
    if(s.indexOf("tanger") > -1){ return "Tanger"; }
    if(s.indexOf("outlet") > -1){ return "Outlet"; }
    if(s.indexOf("factory store") > -1){ return "Outlet"; }
    if(s.indexOf("boutique") > -1){ return "Boutique"; }
    if(s.indexOf("gift") > -1 || s.indexOf("souvenir") > -1){ return "Gift"; }
    if(s.indexOf("mall") > -1){ return "Mall"; }
    if(s.indexOf("shopping center") > -1 || s.indexOf("shops") > -1){ return "Shops"; }
    if(s.indexOf("clothing") > -1 || s.indexOf("apparel") > -1 || s.indexOf("fashion") > -1){ return "Style"; }
    if(s.indexOf("jewelry") > -1 || s.indexOf("jewelers") > -1){ return "Jewelry"; }
    if(s.indexOf("antique") > -1 || s.indexOf("vintage") > -1){ return "Vintage"; }
    if(s.indexOf("book") > -1){ return "Books"; }
    if(s.indexOf("decor") > -1){ return "Decor"; }
    return "Local Shop";
  }
  if(stopType(type) === "scenic"){
    if(s.indexOf("trail") > -1){ return "Trail"; }
    if(s.indexOf("park") > -1){ return "Park"; }
    if(s.indexOf("river") > -1 || s.indexOf("lake") > -1){ return "View"; }
    if(s.indexOf("overlook") > -1){ return "Overlook"; }
    return "Scenic";
  }
  if(stopType(type) === "restroom"){ return "Restroom"; }
  if(stopType(type) === "parking"){ return "Park"; }

  return prettyType(type);
}

function markerOffsetForIndex(index){
  /*
    DRIVE IQ V11 — STOP PIN ANCHOR LOCK

    Older versions used screen-space offsets to spread pins apart.
    That made pins look like they were drifting away from the real stop
    when the user zoomed or panned.

    V11 keeps every stop marker anchored to the true POI coordinate.
    Badges, crowns, cards, and labels may float visually inside the marker,
    but the marker itself does not move away from the stop.
  */
  return [0,0];
}

function markerOffsetForStop(stop,index){
  /*
    Location trust beats collision spacing.

    If several stops overlap, that is more honest than making a business
    appear a block or two away from where it actually is.
  */
  return [0,0];
}

function iconForType(type,name){
  type = stopType(type);

  if(type === "fuel"){ return "⛽"; }
  if(type === "food"){ return iconForFoodName(name); }
  if(type === "coffee"){ return "☕"; }
  if(type === "wellness"){ return "🥤"; }
  if(type === "shopping"){ return "🛍️"; }
  if(type === "scenic"){ return "🧭"; }
  if(type === "parking"){ return "P"; }
  if(type === "walmart"){ return "🛒"; }
  if(type === "grocery"){ return "🛒"; }
  if(type === "pharmacy"){ return "💊"; }
  if(type === "hotel"){ return "🛏"; }
  if(type === "restroom"){ return "🚻"; }
  if(type === "quiet"){ return "😴"; }
  if(type === "ev"){ return "⚡"; }
  if(type === "repair"){ return "🔧"; }

  return "•";
}

function letter(type,name){
  return iconForType(type,name);
}

function color(type){
  type = stopType(type);

  if(type === "fuel"){ return "#28ffbe"; }
  if(type === "food"){ return "#ffce69"; }
  if(type === "coffee"){ return "#b6ffeb"; }
  if(type === "wellness"){ return "#ff77d9"; }
  if(type === "shopping"){ return "#c78cff"; }
  if(type === "scenic"){ return "#67d8ff"; }
  if(type === "parking"){ return "#00e5ff"; }
  if(type === "grocery"){ return "#58a6ff"; }
  if(type === "pharmacy"){ return "#7ee7ff"; }
  if(type === "hotel"){ return "#d9fbff"; }
  if(type === "restroom"){ return "#28ffbe"; }
  if(type === "quiet"){ return "#9ddcff"; }
  if(type === "ev"){ return "#b6ffeb"; }
  if(type === "repair"){ return "#ffce69"; }

  return "#00e5ff";
}

function confidenceForStop(stop,index){
  var score = 72;
  var lane = stop ? String(stop.lane || "") : "";
  var dist = stop && finite(Number(stop.distMiles)) ? Number(stop.distMiles) : null;
  var type = stopType(stop && stop.type);

  if(index === 0){ score += 14; }
  if(lane === "nearby"){ score += 8; }
  if(lane === "ahead"){ score += 5; }
  if(dist !== null && dist <= 2.5){ score += 7; }
  if(dist !== null && dist > 12){ score -= 5; }
  if(type === "restroom"){ score += 3; }
  if(type === "fuel" || type === "coffee"){ score += 2; }
  if(type === "wellness"){ score += 3; }
  if(type === "shopping" || type === "scenic"){ score += 2; }

  return Math.max(58,Math.min(96,Math.round(score)));
}

function confidenceLabel(score){
  score = Number(score);
  if(!finite(score)){ return "WATCH"; }
  if(score >= 86){ return "TRUSTED"; }
  if(score >= 74){ return "GOOD"; }
  return "LIMITED";
}

function glowScaleForStop(stop,index){
  var score = confidenceForStop(stop,index);
  if(index === 0){ return 1.55; }
  if(score >= 86){ return 1.28; }
  if(score >= 74){ return 1.14; }
  return 1.0;
}

function prettyType(type){
  type = stopType(type);

  if(type === "fuel"){ return "Fuel"; }
  if(type === "food"){ return "Food"; }
  if(type === "coffee"){ return "Coffee"; }
  if(type === "wellness"){ return "Wellness"; }
  if(type === "shopping"){ return "Shopping"; }
  if(type === "scenic"){ return "Scenic"; }
  if(type === "parking"){ return "Parking"; }
  if(type === "walmart"){ return "Walmart"; }
  if(type === "grocery"){ return "Grocery"; }
  if(type === "pharmacy"){ return "Pharmacy"; }
  if(type === "hotel"){ return "Hotel"; }
  if(type === "restroom"){ return "Restroom"; }
  if(type === "quiet"){ return "Quiet Stop"; }
  if(type === "ev"){ return "EV Charging"; }
  if(type === "repair"){ return "Repair"; }

  return "Stop";
}

function laneLabel(lane){
  lane = String(lane || "").toLowerCase();

  if(lane === "nearby"){ return "Nearby"; }
  if(lane === "ahead"){ return "Ahead"; }
  if(lane === "far"){ return "Farther ahead"; }

  return "Near route";
}


function stopRouteReturnLabel(stop){
  var routeDist = stop && finite(Number(stop.routeDistMiles)) ? Number(stop.routeDistMiles) : nearestRouteDistanceToStop(stop);
  var dist = stop && finite(Number(stop.distMiles)) ? Number(stop.distMiles) : null;

  if(routeDist !== null && finite(Number(routeDist))){
    if(routeDist <= 0.35){ return "EASY ACCESS"; }
    if(routeDist <= 0.75){ return "QUICK RETURN"; }
    if(routeDist <= 1.4){ return "MINIMAL DETOUR"; }
    if(routeDist <= 2.5){ return "SHORT DETOUR"; }
    return "OFF CORRIDOR";
  }

  if(dist !== null && dist <= 1.2){ return "QUICK STOP"; }
  return "ROUTE WATCH";
}

function stopQualityLabel(stop,index){
  var t = stopType(stop && stop.type);
  var routeLabel = stopRouteReturnLabel(stop);
  var dist = stop && finite(Number(stop.distMiles)) ? Number(stop.distMiles) : null;

  if(stop && stop.sponsor){ return "🔥 FEATURED LOCAL PICK"; }
  if(index === 0){ return "🏆 BEST STOP"; }
  if(routeLabel === "EASY ACCESS"){ return "🛣 EASY ACCESS"; }
  if(routeLabel === "QUICK RETURN"){ return "⚡ QUICK RETURN"; }
  if(dist !== null && dist <= 1.5){ return "⚡ QUICK STOP"; }
  if(t === "food" && foodBrandRank(stop && stop.name) >= 100){ return "🍔 MAJOR BRAND"; }
  if(routeLabel === "MINIMAL DETOUR"){ return "🚗 MINIMAL DETOUR"; }
  if(routeLabel === "SHORT DETOUR"){ return "↩ SHORT DETOUR"; }

  return routeLabel;
}

function majorFoodBrandName(name){
  var s = String(name || "").toLowerCase();

  if(s.indexOf("chick-fil-a") > -1 || s.indexOf("chick fil a") > -1 || s.indexOf("chickfila") > -1){ return "Chick-fil-A"; }
  if(s.indexOf("mcdonald") > -1){ return "McDonald's"; }
  if(s.indexOf("whataburger") > -1){ return "Whataburger"; }
  if(s.indexOf("waffle house") > -1){ return "Waffle House"; }
  if(s.indexOf("wendy") > -1){ return "Wendy's"; }
  if(s.indexOf("captain d") > -1 || s.indexOf("captain ds") > -1){ return "Captain D's"; }
  if(s.indexOf("taco bell") > -1){ return "Taco Bell"; }
  if(s.indexOf("sonic") > -1){ return "Sonic"; }
  if(s.indexOf("burger king") > -1){ return "Burger King"; }
  if(s.indexOf("arby") > -1){ return "Arby's"; }
  if(s.indexOf("raising cane") > -1 || s.indexOf("canes") > -1){ return "Raising Cane's"; }
  if(s.indexOf("popeyes") > -1){ return "Popeyes"; }
  if(s.indexOf("zaxby") > -1){ return "Zaxby's"; }
  if(s.indexOf("starbucks") > -1){ return "Starbucks"; }
  if(s.indexOf("dunkin") > -1){ return "Dunkin"; }
  if(s.indexOf("subway") > -1){ return "Subway"; }

  return "";
}

function majorCategoryBrandName(name,type){
  var s = String(name || "").toLowerCase();
  var t = stopType(type);

  if(t === "food"){ return majorFoodBrandName(name); }

  if(t === "coffee"){
    if(s.indexOf("starbucks") > -1){ return "Starbucks"; }
    if(s.indexOf("dunkin") > -1){ return "Dunkin"; }
    if(s.indexOf("7 brew") > -1){ return "7 Brew"; }
    if(s.indexOf("dutch bros") > -1){ return "Dutch Bros"; }
    if(s.indexOf("scooter") > -1){ return "Scooter's"; }
  }

  if(t === "grocery"){
    if(s.indexOf("walmart supercenter") > -1){ return "Walmart Supercenter"; }
    if(s.indexOf("walmart") > -1){ return "Walmart"; }
    if(s.indexOf("kroger") > -1){ return "Kroger"; }
    if(s.indexOf("aldi") > -1){ return "Aldi"; }
    if(s.indexOf("target") > -1){ return "Target"; }
    if(s.indexOf("sam's club") > -1 || s.indexOf("sams club") > -1){ return "Sam's Club"; }
    if(s.indexOf("costco") > -1){ return "Costco"; }
  }

  if(t === "pharmacy"){
    if(s.indexOf("walgreens") > -1){ return "Walgreens"; }
    if(s.indexOf("kroger") > -1){ return "Kroger Pharmacy"; }
    if(s.indexOf("cvs") > -1){ return "CVS Pharmacy"; }
    if(s.indexOf("walmart") > -1){ return "Walmart Pharmacy"; }
    if(s.indexOf("rite aid") > -1){ return "Rite Aid"; }
    if(s.indexOf("pharmacy") > -1){ return "Pharmacy"; }
  }

  if(t === "fuel"){
    if(s.indexOf("love") > -1){ return "Love's"; }
    if(s.indexOf("pilot") > -1){ return "Pilot"; }
    if(s.indexOf("flying j") > -1){ return "Flying J"; }
    if(s.indexOf("shell") > -1){ return "Shell"; }
    if(s.indexOf("circle k") > -1){ return "Circle K"; }
    if(s.indexOf("murphy") > -1){ return "Murphy"; }
    if(s.indexOf("exxon") > -1){ return "Exxon"; }
    if(s.indexOf("bp") > -1){ return "BP"; }
    if(s.indexOf("marathon") > -1){ return "Marathon"; }
  }

  if(t === "hotel"){
    if(s.indexOf("hampton") > -1){ return "Hampton"; }
    if(s.indexOf("holiday inn") > -1){ return "Holiday Inn"; }
    if(s.indexOf("marriott") > -1 || s.indexOf("courtyard") > -1 || s.indexOf("fairfield") > -1){ return "Marriott"; }
    if(s.indexOf("hilton") > -1){ return "Hilton"; }
    if(s.indexOf("hyatt") > -1){ return "Hyatt"; }
    if(s.indexOf("comfort") > -1){ return "Comfort Inn"; }
    if(s.indexOf("la quinta") > -1){ return "La Quinta"; }
    if(s.indexOf("best western") > -1){ return "Best Western"; }
  }

  if(t === "shopping"){
    if(s.indexOf("tanger") > -1){ return "Tanger"; }
    if(s.indexOf("target") > -1){ return "Target"; }
    if(s.indexOf("walmart") > -1){ return "Walmart"; }
    if(s.indexOf("tj maxx") > -1){ return "TJ Maxx"; }
    if(s.indexOf("marshalls") > -1){ return "Marshalls"; }
    if(s.indexOf("ross") > -1){ return "Ross"; }
    if(s.indexOf("best buy") > -1){ return "Best Buy"; }
    if(s.indexOf("outlet") > -1){ return "Outlets"; }
  }

  if(t === "wellness"){
    if(s.indexOf("smoothie king") > -1){ return "Smoothie King"; }
    if(s.indexOf("tropical smoothie") > -1){ return "Tropical Smoothie"; }
    if(s.indexOf("nutrition") > -1){ return "Nutrition"; }
    if(s.indexOf("juice") > -1){ return "Juice Bar"; }
  }

  return "";
}

function categoryBrandRank(stop){
  var t = stopType(stop && stop.type);
  var s = String(stop && stop.name ? stop.name : "").toLowerCase();

  if(t === "food"){ return foodBrandRank(stop && stop.name); }

  if(t === "grocery"){
    if(s.indexOf("walmart supercenter") > -1){ return 118; }
    if(s.indexOf("kroger") > -1){ return 110; }
    if(s.indexOf("target") > -1){ return 106; }
    if(s.indexOf("aldi") > -1){ return 102; }
    if(s.indexOf("sam") > -1 || s.indexOf("costco") > -1){ return 100; }
    if(s.indexOf("walmart") > -1){ return 96; }
  }

  if(t === "pharmacy"){
    if(s.indexOf("walgreens") > -1){ return 118; }
    if(s.indexOf("kroger") > -1 && s.indexOf("pharmacy") > -1){ return 116; }
    if(s.indexOf("cvs") > -1){ return 114; }
    if(s.indexOf("walmart") > -1 && s.indexOf("pharmacy") > -1){ return 110; }
    if(s.indexOf("rite aid") > -1){ return 106; }
    if(s.indexOf("pharmacy") > -1 || s.indexOf("drug") > -1){ return 92; }
  }

  if(t === "fuel"){
    if(s.indexOf("love") > -1){ return 122; }
    if(s.indexOf("pilot") > -1 || s.indexOf("flying j") > -1){ return 118; }
    if(s.indexOf("murphy") > -1){ return 100; }
    if(s.indexOf("shell") > -1 || s.indexOf("exxon") > -1 || s.indexOf("chevron") > -1){ return 92; }
    if(s.indexOf("circle k") > -1){ return 86; }
  }

  if(t === "coffee"){
    if(s.indexOf("starbucks") > -1){ return 115; }
    if(s.indexOf("dunkin") > -1){ return 104; }
    if(s.indexOf("7 brew") > -1){ return 100; }
    if(s.indexOf("scooter") > -1 || s.indexOf("dutch bros") > -1){ return 96; }
  }

  if(t === "hotel"){
    if(s.indexOf("hampton") > -1 || s.indexOf("holiday inn") > -1){ return 108; }
    if(s.indexOf("marriott") > -1 || s.indexOf("hilton") > -1 || s.indexOf("hyatt") > -1){ return 112; }
    if(s.indexOf("comfort") > -1 || s.indexOf("la quinta") > -1 || s.indexOf("best western") > -1){ return 94; }
  }

  if(t === "shopping"){
    if(s.indexOf("tanger") > -1 || s.indexOf("outlet") > -1){ return 118; }
    if(s.indexOf("target") > -1){ return 108; }
    if(s.indexOf("best buy") > -1){ return 100; }
    if(s.indexOf("tj maxx") > -1 || s.indexOf("marshalls") > -1 || s.indexOf("ross") > -1){ return 96; }
    if(s.indexOf("boutique") > -1 || s.indexOf("gift") > -1){ return 92; }
  }

  if(t === "wellness"){
    if(s.indexOf("smoothie king") > -1){ return 108; }
    if(s.indexOf("tropical smoothie") > -1){ return 104; }
    if(s.indexOf("nutrition") > -1 || s.indexOf("juice") > -1){ return 92; }
  }

  return 0;
}

function uniqueClusterBrandNamesByType(stops,type,limit){
  var names = [];
  var seen = {};
  var i;
  var nm;

  limit = finite(Number(limit)) ? Number(limit) : 4;
  if(!stops || !stops.length){ return names; }

  for(i = 0; i < stops.length && names.length < limit; i++){
    nm = majorCategoryBrandName(stops[i] && stops[i].name,type || (stops[i] && stops[i].type)) || String(stops[i] && stops[i].name ? stops[i].name : "");
    nm = nm.replace(/^\s+|\s+$/g,"");
    if(nm && !seen[nm.toLowerCase()]){
      seen[nm.toLowerCase()] = true;
      names.push(nm);
    }
  }

  return names;
}

function districtScore(stops,type){
  var t = stopType(type || (stops && stops.length ? stops[0].type : currentScanType));
  var list = stops ? stops.slice(0) : [];
  var first;
  var impact;
  var score = 62;
  var brandPoints = 0;
  var i;

  if(!list.length){ return 50; }

  list.sort(sortClusterStops);
  first = list[0];
  impact = routeImpact(first);

  if(list.length >= 2){ score += 7; }
  if(list.length >= 4){ score += 7; }
  if(list.length >= 6){ score += 5; }

  if(impact.addedMinutes <= 1){ score += 14; }
  else if(impact.addedMinutes <= 2){ score += 10; }
  else if(impact.addedMinutes <= 4){ score += 6; }
  else if(impact.addedMinutes >= 8){ score -= 8; }

  for(i = 0; i < Math.min(list.length,5); i++){
    brandPoints += categoryBrandRank(list[i]) / 24;
  }

  score += Math.min(18,brandPoints);

  if(t === "grocery" && list.length >= 3){ score += 4; }
  if(t === "hotel" && list.length >= 3){ score += 4; }
  if(t === "fuel" && list.length >= 2){ score += 4; }
  if(t === "shopping" && list.length >= 3){ score += 5; }

  return Math.max(58,Math.min(98,Math.round(score)));
}

function districtScorePhrase(stops,type){
  var score = districtScore(stops,type);
  if(score >= 92){ return "Best district"; }
  if(score >= 86){ return "Strong stop"; }
  if(score >= 78){ return "Good route fit"; }
  return "Route option";
}



function majorBrandCount(stops){
  var seen = {};
  var count = 0;
  var i;
  var nm;

  if(!stops || !stops.length){ return 0; }

  for(i = 0; i < stops.length; i++){
    nm = majorFoodBrandName(stops[i] && stops[i].name);
    if(nm && !seen[nm]){
      seen[nm] = true;
      count += 1;
    }
  }

  return count;
}

function isGenericFoodStop(stop){
  var name = String(stop && stop.name ? stop.name : "").toLowerCase();
  var t = stopType(stop && stop.type);

  if(t !== "food"){ return false; }
  if(stop && stop.sponsor){ return false; }
  if(majorFoodBrandName(name)){ return false; }

  if(name === "food"){ return true; }
  if(name === "restaurant"){ return true; }
  if(name === "fast food"){ return true; }
  if(name.indexOf("restaurant") > -1 && name.length <= 18){ return true; }
  if(name.indexOf("food") > -1 && name.length <= 18){ return true; }

  return false;
}

function nearbyMajorFoodBrand(stop,stops,maxMiles){
  var i;
  var other;
  var d;

  if(!stop || !stops || !stops.length){ return false; }
  maxMiles = finite(Number(maxMiles)) ? Number(maxMiles) : 0.38;

  for(i = 0; i < stops.length; i++){
    other = stops[i];
    if(!other || other === stop){ continue; }
    if(stopType(other.type) !== "food"){ continue; }
    if(!majorFoodBrandName(other.name)){ continue; }
    if(!finite(Number(stop.lat)) || !finite(Number(stop.lng)) || !finite(Number(other.lat)) || !finite(Number(other.lng))){ continue; }

    d = milesBetween([Number(stop.lng),Number(stop.lat)],[Number(other.lng),Number(other.lat)]);
    if(d <= maxMiles){ return true; }
  }

  return false;
}

function suppressGenericFoodStops(stops,type){
  var out = [];
  var i;
  var stop;
  var t = stopType(type);

  if(t !== "food" || !stops || !stops.length){ return stops || []; }

  for(i = 0; i < stops.length; i++){
    stop = stops[i];
    if(!stop){ continue; }

    if(isGenericFoodStop(stop) && nearbyMajorFoodBrand(stop,stops,0.42)){
      continue;
    }

    out.push(stop);
  }

  return out;
}

function stopNearDestination(stop,maxMiles){
  var dest = getDestination();
  if(!dest || !stop){ return false; }
  if(!finite(Number(stop.lat)) || !finite(Number(stop.lng))){ return false; }

  maxMiles = finite(Number(maxMiles)) ? Number(maxMiles) : 0.18;

  return milesBetween([Number(stop.lng),Number(stop.lat)],[Number(dest.lng),Number(dest.lat)]) <= maxMiles;
}

function foodDistrictHeadline(stops){
  var names = [];
  var seen = {};
  var i;
  var nm;

  if(!stops || !stops.length){ return "Food District"; }

  for(i = 0; i < stops.length && names.length < 4; i++){
    nm = majorFoodBrandName(stops[i] && stops[i].name) || String(stops[i] && stops[i].name ? stops[i].name : "");
    nm = nm.replace(/^\s+|\s+$/g,"");
    if(nm && !seen[nm.toLowerCase()]){
      seen[nm.toLowerCase()] = true;
      names.push(nm);
    }
  }

  if(names.length){ return names.join(" • "); }
  return "Best access first";
}

function stopTag(stop,index){
  var score = confidenceForStop(stop,index);
  var t = stopType(stop && stop.type);
  var q = stopQualityLabel(stop,index);

  if(stop && stop.searchReason){ return String(stop.searchReason); }
  if(stop && stop.sponsor){ return "🔥 FEATURED LOCAL PICK"; }
  if(index === 0){ return "🏆 BEST STOP"; }
  if(t === "restroom" && score >= 82){ return "🚻 " + q; }
  if(t === "wellness"){ return "🥤 " + q; }
  if(t === "shopping"){ return "🛍️ " + q; }
  if(t === "scenic"){ return "🧭 " + q; }
  if(t === "quiet"){ return "😴 QUIET WATCH"; }

  return q;
}

function stopInfoLine(stop,index){
  var type = prettyType(stop && stop.type);
  var distance = stop && finite(Number(stop.distMiles)) ? fmtMiles(stop.distMiles) : "nearby";
  var tag = stop && stop.searchReason
    ? String(stop.searchReason)
    : stopTag(stop,index);
  var routeLabel = stopRouteReturnLabel(stop);
  var routeCopy = "";

  if(stop && stop.sponsor){
    return "🔥 Featured Local Pick • " + String(stop.offerShort || stop.offer || "Local offer");
  }

  if(routeLabel === "EASY ACCESS"){ routeCopy = "↩ easy route return"; }
  else if(routeLabel === "QUICK RETURN"){ routeCopy = "↩ quick route return"; }
  else if(routeLabel === "MINIMAL DETOUR"){ routeCopy = "minimal detour"; }
  else if(routeLabel === "SHORT DETOUR"){ routeCopy = "short detour"; }
  else if(routeLabel === "OFF CORRIDOR"){ routeCopy = "off corridor"; }
  else if(routeLabel === "QUICK STOP"){ routeCopy = "nearby"; }
  else{ routeCopy = String(routeLabel || "route watch").toLowerCase(); }

  if(tag === routeLabel || tag === "⚡ " + routeLabel || tag === "🛣 " + routeLabel){
    return tag + " • " + type + " • " + distance + " • " + routeCopy;
  }

  return tag + " • " + type + " • " + distance + " • " + routeCopy;
}

function updateStopInfo(stop,index){
  var name = String(stop && stop.name ? stop.name : "Stop");
  var line = stopInfoLine(stop,index);

  if(stop && stop.sponsor){
    setText("diqRouteIntelText",name);
    setText("diqRouteIntelSub",String(stop.offerShort || stop.offer || "Featured Local Pick"));
    setText("diqSigArrival",name);
    setText("diqSigRoute","Featured Local Pick");
    toast(name + " • " + String(stop.offerShort || stop.offer || "Featured Local Pick"));
    return;
  }

  setText("diqRouteIntelText",name);
  setText("diqRouteIntelSub",line);
  setText("diqSigArrival",name);
  setText("diqSigRoute",stopTag(stop,index));

  toast(name + " • " + line);
}

function bestStopSummary(stop,type){
  if(!stop){ return "No " + stopType(type) + " found nearby"; }

  return "Best " + prettyType(type) + ": " + String(stop.name || "Stop") + " • " + fmtMiles(stop.distMiles);
}

function nearestRouteIndex(coords,fix){
  var base = 0;
  var best = 999999;
  var step;
  var i;
  var d;

  if(!coords || !coords.length || !fix){ return 0; }

  step = Math.max(1,Math.floor(coords.length / 450));

  for(i = 0; i < coords.length; i += step){
    d = milesBetween([fix.lng,fix.lat],coords[i]);
    if(d < best){
      best = d;
      base = i;
    }
  }

  return base;
}

function routeDistanceMiles(coords,startIndex,endIndex){
  var total = 0;
  var i;

  if(!coords || coords.length < 2){ return 0; }

  startIndex = Math.max(0,Math.min(coords.length - 1,Math.round(startIndex || 0)));
  endIndex = Math.max(0,Math.min(coords.length - 1,Math.round(endIndex || 0)));

  if(endIndex <= startIndex){ return 0; }

  for(i = startIndex; i < endIndex; i++){
    total += milesBetween(coords[i],coords[i + 1]);
  }

  return total;
}

function routePointAtMiles(coords,baseIndex,targetMiles){
  var total = 0;
  var i;
  var seg;

  if(!coords || !coords.length){ return null; }

  baseIndex = Math.max(0,Math.min(coords.length - 1,Math.round(baseIndex || 0)));

  if(targetMiles <= 0){
    return { lng:Number(coords[baseIndex][0]), lat:Number(coords[baseIndex][1]), index:baseIndex, lane:"nearby" };
  }

  for(i = baseIndex; i < coords.length - 1; i++){
    seg = milesBetween(coords[i],coords[i + 1]);
    total += seg;

    if(total >= targetMiles){
      return { lng:Number(coords[i + 1][0]), lat:Number(coords[i + 1][1]), index:i + 1, lane:"ahead" };
    }
  }

  return {
    lng:Number(coords[coords.length - 1][0]),
    lat:Number(coords[coords.length - 1][1]),
    index:coords.length - 1,
    lane:"far"
  };
}


function nearestRouteDistanceToStop(stop){
  var coords = getRouteCoords();
  var best = null;
  var step;
  var i;
  var d;

  if(!stop || !coords || !coords.length){ return null; }

  step = Math.max(1,Math.floor(coords.length / 520));

  for(i = 0; i < coords.length; i += step){
    d = milesBetween([Number(stop.lng),Number(stop.lat)],coords[i]);
    if(best === null || d < best){ best = d; }
  }

  return best;
}

function scanPoints(){
  var coords = getRouteCoords();
  var fix = getCurrentFix();
  var base = 0;
  var out = [];
  var p;
  var targets;
  var i;

  if(fix){
    out.push({ lng:fix.lng, lat:fix.lat, lane:"nearby", radius:12000 });
  }

  if(!coords || !coords.length){
    return out;
  }

  base = nearestRouteIndex(coords,fix);

  targets = [3,8,15,25,45,75,100];

  for(i = 0; i < targets.length; i++){
    p = routePointAtMiles(coords,base,targets[i]);
    if(p){
      p.lane = targets[i] <= 5 ? "nearby" : (targets[i] <= 25 ? "ahead" : "far");
      p.radius = targets[i] <= 5
        ? 6500
        : (targets[i] <= 25 ? 8500 : 12000);
      out.push(p);
    }
  }

  return out;
}

function wellnessRadius(point,type){
  var r = Number(point && point.radius ? point.radius : 7000);

  if(stopType(type) === "wellness"){
    if(String(point && point.lane || "") === "nearby"){ return Math.max(r,26000); }
    if(String(point && point.lane || "") === "ahead"){ return Math.max(r,20000); }
    return Math.max(r,18000);
  }

  return r;
}

function placesUrl(point,type){
  return "https://api.geoapify.com/v2/places?categories=" +
    encodeURIComponent(categories(type)) +
    "&filter=" + encodeURIComponent("circle:" + String(point.lng) + "," + String(point.lat) + "," + String(wellnessRadius(point,type))) +
    "&bias=" + encodeURIComponent("proximity:" + String(point.lng) + "," + String(point.lat)) +
    "&limit=12&apiKey=" + encodeURIComponent(GEOAPIFY_KEY);
}

function fallbackUrl(point,type,text){
  return "https://api.geoapify.com/v1/geocode/search?text=" +
    encodeURIComponent(text) +
    "&filter=" + encodeURIComponent("circle:" + String(point.lng) + "," + String(point.lat) + "," + String(wellnessRadius(point,type))) +
    "&bias=" + encodeURIComponent("proximity:" + String(point.lng) + "," + String(point.lat)) +
    "&limit=10&format=geojson&apiKey=" + encodeURIComponent(GEOAPIFY_KEY);
}

function normalizeFeature(feature,type,lane){
  var p;
  var lat;
  var lng;
  var fix;
  var dist = null;
  var name;
  var id;

  if(!feature || !feature.properties){ return null; }

  p = feature.properties;
  lat = Number(p.lat);
  lng = Number(p.lon);

  if(!finite(lat) || !finite(lng)){ return null; }

  name = String(p.name || p.address_line1 || p.formatted || searchTexts(type)[0]);
  id = String(p.place_id || (p.datasource && p.datasource.raw && p.datasource.raw.osm_id) || (name + "_" + String(lat) + "_" + String(lng)));

  if(stopType(type) === "wellness"){
    if(!isWellnessName(String(name) + " " + String(p.formatted || "") + " " + String(p.address_line1 || ""))){
      return null;
    }
  }

  if(stopType(type) === "shopping"){
    if(!isShoppingName(String(name) + " " + String(p.formatted || "") + " " + String(p.address_line1 || ""))){
      return null;
    }
  }

  fix = getCurrentFix();
  if(fix){
    dist = milesBetween([fix.lng,fix.lat],[lng,lat]);
  }

  return {
    id:id,
    name:name,
    type:stopType(type),
    lat:lat,
    lng:lng,
    distMiles:dist,
    lane:String(lane || "ahead"),
    infoLabel:laneLabel(lane || "ahead"),
    routeDistMiles:null,
    address:String(p.formatted || p.address_line2 || ""),
    providerCategories:(p.categories && p.categories.join ? p.categories.join(" ") : String(p.categories || "")),
    providerCuisine:String(
      (p.datasource && p.datasource.raw && (
        p.datasource.raw.cuisine ||
        p.datasource.raw.food ||
        p.datasource.raw.amenity
      )) || ""
    ),
    source:"stop-iq-places-v28-5"
  };
}



/* =========================================================
   DRIVE IQ V28 — ASK IQ PROVIDER BRIDGE
   Search authority stays here. ASK IQ owns conversation/ranking/voice.
========================================================= */

function askKnownChain(name){
  var s = String(name || "").toLowerCase();

  return /chick[- ]?fil[- ]?a|mcdonald|starbucks|taco bell|wendy|burger king|subway|whataburger|waffle house|sonic|popeyes|zaxby|raising cane|dunkin|kfc|pizza hut|domino|papa john|walmart|target|costco|sam'?s club|kroger|aldi|walgreens|cvs|rite aid|hampton inn|holiday inn|marriott|hilton|hyatt|best western|la quinta|shell|exxon|chevron|circle k|pilot|flying j|love'?s/.test(s);
}

function askDestinationPoint(){
  var d = safeParse(lsGet(LS.DEST)) || null;
  var lat;
  var lng;

  if(!d){ return null; }

  lat = finite(Number(d.lat)) ? Number(d.lat) :
    (finite(Number(d.latitude)) ? Number(d.latitude) : null);
  lng = finite(Number(d.lng)) ? Number(d.lng) :
    (finite(Number(d.lon)) ? Number(d.lon) :
      (finite(Number(d.longitude)) ? Number(d.longitude) : null));

  if(!finite(lat) || !finite(lng)){ return null; }

  return {lat:lat,lng:lng};
}

function askDecorateStop(stop){
  var out = stop || {};
  var routeDist;
  var dest;
  var destMiles;
  var chain;

  if(finite(Number(out.distMiles))){
    out.distanceMiles = Number(out.distMiles);
  }

  routeDist = finite(Number(out.routeDistMiles))
    ? Number(out.routeDistMiles)
    : nearestRouteDistanceToStop(out);

  if(finite(Number(routeDist))){
    out.routeDistMiles = Number(routeDist);
    out.isAlongRoute = Number(routeDist) <= 1.5;

    /*
      Interstate practicality:
      a stop can be far ahead and still be an excellent choice when it sits
      close to the active route corridor.
    */
    out.routeAheadPractical =
      (String(out.lane || "") === "ahead" || String(out.lane || "") === "far") &&
      Number(routeDist) <= 1.5;

    /*
      Conservative route-impact estimate for spoken guidance.
      It is only used when the provider does not already expose detour minutes.
      Approximation: leave/rejoin route at roughly 30 mph.
    */
    if(!finite(Number(out.detourMinutes)) && Number(routeDist) <= 3){
      out.detourMinutes = Math.max(1,Math.round(Number(routeDist) * 4));
      out.detourEstimate = true;
    }
  }

  dest = askDestinationPoint();
  if(dest && finite(Number(out.lat)) && finite(Number(out.lng))){
    destMiles = milesBetween(
      [Number(out.lng),Number(out.lat)],
      [Number(dest.lng),Number(dest.lat)]
    );

    if(finite(Number(destMiles))){
      out.destinationDistanceMiles = Number(destMiles);
      out.nearDestination = Number(destMiles) <= 4;
    }
  }

  chain = askKnownChain(out.name);
  out.isChain = chain;
  out.isLocal = !chain;

  return out;
}

function askDecorateStops(stops){
  var list = (stops || []).slice(0);
  var i;

  for(i = 0; i < list.length; i++){
    list[i] = askDecorateStop(list[i]);
  }

  return list;
}

function askProviderQuery(detail){
  var intent = detail && detail.intent ? detail.intent : {};
  var named = String(intent.namedPlace || "").replace(/^\s+|\s+$/g,"");
  var category = String(intent.category || "").toLowerCase();
  var original = String(detail && detail.query || "").replace(/^\s+|\s+$/g,"");
  var raw = original;

  if(named){ return named; }

  /*
    Preserve specific cuisine / food-type intent BEFORE falling back to the
    broad category. Otherwise "find pizza near me" becomes just "food" and a
    nearby Walmart or convenience store can incorrectly win.
  */
  raw = raw
    .replace(/\b(find|show|give|tell)\s+(me\s+)?/ig,"")
    .replace(/\b(the\s+)?(best|good|great|quick|fast|closest|local)\b/ig,"")
    .replace(/\b(ahead|nearby|near me|on my route|along my route|on the way|near my destination|at my destination)\b/ig,"")
    .replace(/\s+/g," ")
    .replace(/^\s+|\s+$/g,"");

  if(/\b(pizza|pizzeria)\b/i.test(original)){ return "pizza"; }
  if(/\b(mexican|taqueria|burrito|taco)\b/i.test(original)){ return "Mexican restaurant"; }
  if(/\b(burger|hamburger)\b/i.test(original)){ return "burger restaurant"; }
  if(/\b(chicken|wings|wing)\b/i.test(original)){ return "chicken restaurant"; }
  if(/\b(italian)\b/i.test(original)){ return "Italian restaurant"; }
  if(/\b(sushi|japanese)\b/i.test(original)){ return "sushi restaurant"; }
  if(/\b(chinese)\b/i.test(original)){ return "Chinese restaurant"; }
  if(/\b(bbq|barbecue)\b/i.test(original)){ return "barbecue restaurant"; }
  if(/\b(steak|steakhouse)\b/i.test(original)){ return "steakhouse"; }
  if(/\b(seafood)\b/i.test(original)){ return "seafood restaurant"; }

  if(category === "coffee"){ return "coffee"; }
  if(category === "gas"){ return "gas"; }
  if(category === "food"){ return raw || "restaurant"; }
  if(category === "restroom"){ return "restroom"; }
  if(category === "parking"){ return "parking"; }
  if(category === "grocery"){ return "grocery"; }
  if(category === "hotel"){ return "hotel"; }
  if(category === "dessert"){ return "dessert"; }

  return raw || original || "food";
}

function deliverAskResults(stops){
  var requestId = String(askPendingRequestId || "");
  var list;

  if(!requestId){ return false; }

  list = askDecorateStops(stops || []);

  if(askPendingIntent){
    list = filterAskSpecificTypedResults(
      list,
      askPendingOriginalQuery || askPendingQuery,
      askPendingIntent
    );
  }

  askPendingRequestId = "";
  askPendingQuery = "";
  askPendingIntent = null;
  askPendingOriginalQuery = "";

  try{
    if(window.DRIVE_IQ_ASK &&
       typeof window.DRIVE_IQ_ASK.receiveResults === "function"){
      window.DRIVE_IQ_ASK.receiveResults(list,{
        requestId:requestId,
        source:"find-stops-v28-ask-bridge"
      });
      return true;
    }
  }catch(e1){}

  try{
    window.dispatchEvent(new CustomEvent("driveiq:ask-provider-results",{
      detail:{
        requestId:requestId,
        source:"find-stops-v28-ask-bridge",
        results:list
      }
    }));
    return true;
  }catch(e2){}

  return false;
}

function handleAskSearch(event){
  var detail = event && event.detail ? event.detail : {};
  var requestId = String(detail.requestId || "");
  var query = askProviderQuery(detail);

  if(!requestId || !query){ return; }

  askPendingRequestId = requestId;
  askPendingQuery = query;
  askPendingIntent = detail.intent || null;
  askPendingOriginalQuery = String(detail.query || query);

  /*
    Specific cuisine requests need the Places/category engine, not only the
    geocoding text endpoint. This prevents false "no match" results for real
    restaurants whose names do not contain the cuisine word.
  */
  if(askIsSpecificCuisineRequest(detail.query || query,detail.intent || null)){
    scanAskSpecificCuisine(detail.query || query,detail.intent || null);
    return;
  }

  scanTypedStop(query);
}

function handleAskRouteConfirmed(event){
  var detail = event && event.detail ? event.detail : {};
  var place = detail.place || null;

  /*
    This event is emitted only after ASK IQ's explicit Add Stop confirmation.
    The existing passive addStopToRoute() bridge remains the sole route handoff.
  */
  if(place){
    addStopToRoute(place);
  }
}

function bindAskIqBridge(){
  if(askBridgeBound){ return; }
  askBridgeBound = true;

  window.addEventListener("driveiq:ask-search",function(event){
    // V29: Google Places is the sole ASK search provider when installed.
    // Find Stops still owns the explicit route-confirmed stop handoff.
    if(window.DRIVE_IQ_ASK_PLACES &&
       typeof window.DRIVE_IQ_ASK_PLACES.search === "function"){
      return;
    }
    handleAskSearch(event);
  },false);
  window.addEventListener("driveiq:ask-route-confirmed",handleAskRouteConfirmed,false);
}

/* =========================================================
   DRIVE IQ V21 — STOP IQ TYPED SEARCH
========================================================= */

function stopSearchCategoryForQuery(query){
  var q = String(query || "").toLowerCase();

  if(/walmart|kroger|aldi|costco|sam'?s club|supermarket|grocery/.test(q)){
    return "grocery";
  }
  if(/target|best buy|tj maxx|marshalls|ross|mall|outlet|boutique|shopping/.test(q)){
    return "shopping";
  }
  if(/walgreens|cvs|rite aid|pharmacy|drugstore/.test(q)){
    return "pharmacy";
  }
  if(/starbucks|dunkin|coffee|cafe|espresso/.test(q)){
    return "coffee";
  }
  if(/shell|exxon|bp|circle k|love'?s|pilot|flying j|fuel|gas/.test(q)){
    return "fuel";
  }
  if(/restaurant|food|pizza|burger|chicken|taco|mexican|burrito|taqueria|breakfast|lunch|dinner/.test(q)){
    return "food";
  }
  if(/hotel|motel|inn|lodging/.test(q)){
    return "hotel";
  }
  if(/parking/.test(q)){
    return "parking";
  }
  if(/restroom|bathroom|toilet/.test(q)){
    return "restroom";
  }

  return "stops";
}

function normalizeSearchFeature(feature,query,lane){
  var p;
  var coords;
  var lat;
  var lng;
  var fix;
  var dist = null;
  var name;
  var id;
  var category;

  if(!feature){ return null; }

  p = feature.properties || {};
  coords = feature.geometry && feature.geometry.coordinates
    ? feature.geometry.coordinates
    : [];

  lat = finite(Number(p.lat)) ? Number(p.lat) :
    (coords.length >= 2 ? Number(coords[1]) : null);
  lng = finite(Number(p.lon)) ? Number(p.lon) :
    (coords.length >= 2 ? Number(coords[0]) : null);

  if(!finite(lat) || !finite(lng)){ return null; }

  name = String(
    p.name ||
    p.address_line1 ||
    p.formatted ||
    query ||
    "Stop"
  );

  id = String(
    p.place_id ||
    (p.datasource && p.datasource.raw && p.datasource.raw.osm_id) ||
    (name + "_" + String(lat) + "_" + String(lng))
  );

  fix = getCurrentFix();
  if(fix){
    dist = milesBetween([fix.lng,fix.lat],[lng,lat]);
  }

  category = stopSearchCategoryForQuery(query);

  return {
    id:id,
    name:name,
    type:stopType(category),
    searchCategory:category,
    searchQuery:String(query || ""),
    lat:lat,
    lng:lng,
    distMiles:dist,
    lane:String(lane || "ahead"),
    infoLabel:laneLabel(lane || "ahead"),
    routeDistMiles:null,
    address:String(p.formatted || p.address_line2 || ""),
    city:String(p.city || p.town || p.village || ""),
    state:String(p.state || ""),
    providerResultType:String(p.result_type || ""),
    providerRank:Number(p.rank && p.rank.confidence || 0),
    providerCategories:(p.categories && p.categories.join ? p.categories.join(" ") : String(p.categories || "")),
    providerCuisine:String(
      (p.datasource && p.datasource.raw && (
        p.datasource.raw.cuisine ||
        p.datasource.raw.food ||
        p.datasource.raw.amenity
      )) || ""
    ),
    source:"stop-iq-search-v21"
  };
}

function typedSearchUrl(point,query){
  return "https://api.geoapify.com/v1/geocode/search?text=" +
    encodeURIComponent(String(query || "")) +
    "&filter=" +
    encodeURIComponent(
      "circle:" +
      String(point.lng) + "," +
      String(point.lat) + "," +
      String(Math.max(9000,Number(point.radius || 8000)))
    ) +
    "&bias=" +
    encodeURIComponent(
      "proximity:" +
      String(point.lng) + "," +
      String(point.lat)
    ) +
    "&limit=12&format=geojson&apiKey=" +
    encodeURIComponent(GEOAPIFY_KEY);
}

function stopSearchNameScore(stop,query){
  var q = String(query || "").toLowerCase().trim();
  var name = String(stop && stop.name || "").toLowerCase();
  var score = 0;

  if(!q || !name){ return 0; }

  if(name === q){ score += 500; }
  else if(name.indexOf(q) === 0){ score += 320; }
  else if(name.indexOf(q) >= 0){ score += 180; }

  if(q === "walmart"){
    if(name.indexOf("walmart supercenter") >= 0){ score += 260; }
    else if(name.indexOf("walmart neighborhood market") >= 0){ score += 235; }
    else if(name.indexOf("walmart") >= 0){ score += 210; }
  }

  return score;
}

function stopSearchDistanceScore(stop){
  var dist = finite(Number(stop && stop.distMiles))
    ? Number(stop.distMiles)
    : 999;
  var score = 0;

  if(dist <= 0.50){ score += 420; }
  else if(dist <= 1.00){ score += 360; }
  else if(dist <= 2.00){ score += 285; }
  else if(dist <= 4.00){ score += 205; }
  else if(dist <= 8.00){ score += 115; }
  else if(dist <= 15.00){ score += 45; }
  else{ score -= Math.min(180,Math.round((dist - 15) * 5)); }

  return score;
}

function stopSearchRouteScore(stop){
  var routeDist = nearestRouteDistanceToStop(stop);
  var lane = String(stop && stop.lane || "");
  var score = 0;

  stop.routeDistMiles = routeDist;

  /*
    Route fit matters, but it is now a modifier rather than the lead
    authority. The closest useful stop should normally win.
  */
  if(routeDist !== null){
    if(routeDist <= 0.30){ score += 110; }
    else if(routeDist <= 0.70){ score += 85; }
    else if(routeDist <= 1.25){ score += 55; }
    else if(routeDist <= 2.50){ score += 25; }
    else if(routeDist > 5.00){ score -= 45; }
  }

  if(lane === "ahead"){ score += 65; }
  else if(lane === "nearby"){ score += 40; }
  else if(lane === "far"){ score += 10; }

  return score;
}

function stopSearchPracticalPenalty(stop){
  var dist = finite(Number(stop && stop.distMiles))
    ? Number(stop.distMiles)
    : 999;
  var routeDist = finite(Number(stop && stop.routeDistMiles))
    ? Number(stop.routeDistMiles)
    : nearestRouteDistanceToStop(stop);
  var lane = String(stop && stop.lane || "");
  var penalty = 0;

  /*
    Do not let a farther "ahead" result beat a dramatically closer
    practical option unless the closer stop is clearly off corridor.
  */
  if(dist <= 2 && routeDist !== null && routeDist <= 2.5){
    penalty -= 35;
  }

  if(lane === "far" && dist > 8){
    penalty += 45;
  }

  if(routeDist !== null && routeDist > 4.5){
    penalty += 55;
  }

  return penalty;
}

function stopSearchCompositeScore(stop,query){
  return (
    stopSearchNameScore(stop,query) +
    stopSearchDistanceScore(stop) +
    stopSearchRouteScore(stop) -
    stopSearchPracticalPenalty(stop)
  );
}

function sortTypedStops(query){
  return function(a,b){
    var generic = !!genericStopCategoryQuery(query);
    var aScore = stopSearchCompositeScore(a,query);
    var bScore = stopSearchCompositeScore(b,query);
    var aDist = Number(a && a.distMiles);
    var bDist = Number(b && b.distMiles);
    var at;
    var bt;

    if(generic){
      at = practicalDistanceTier(a);
      bt = practicalDistanceTier(b);
      if(at !== bt){ return at - bt; }
    }

    if(bScore !== aScore){ return bScore - aScore; }

    if(finite(aDist) && finite(bDist) && aDist !== bDist){
      return aDist - bDist;
    }

    return Number(a && a.routeDistMiles || 999) -
      Number(b && b.routeDistMiles || 999);
  };
}

function stopSearchReason(stop,index,list){
  var dist = finite(Number(stop && stop.distMiles))
    ? Number(stop.distMiles)
    : null;
  var routeDist = finite(Number(stop && stop.routeDistMiles))
    ? Number(stop.routeDistMiles)
    : nearestRouteDistanceToStop(stop);
  var lane = String(stop && stop.lane || "");
  var closest = true;
  var lowestDetour = true;
  var i;
  var other;
  var otherDist;
  var otherRoute;

  list = list || [];

  for(i = 0; i < list.length; i++){
    other = list[i];
    if(!other || other === stop){ continue; }

    otherDist = finite(Number(other.distMiles))
      ? Number(other.distMiles)
      : null;
    otherRoute = finite(Number(other.routeDistMiles))
      ? Number(other.routeDistMiles)
      : nearestRouteDistanceToStop(other);

    if(dist !== null && otherDist !== null && otherDist < dist - 0.05){
      closest = false;
    }

    if(routeDist !== null && otherRoute !== null && otherRoute < routeDist - 0.05){
      lowestDetour = false;
    }
  }

  if(index === 0 && closest){ return "📍 CLOSEST"; }
  if(closest){ return "📍 CLOSEST"; }
  if(lane === "ahead" && routeDist !== null && routeDist <= 1.25){
    return "🚗 AHEAD ON ROUTE";
  }
  if(lowestDetour && routeDist !== null){
    return "↩ LOWEST DETOUR";
  }
  if(routeDist !== null && routeDist <= 0.70){
    return "🛣 EASY RETURN";
  }

  return "ROUTE OPTION";
}

function stopSearchResultSummary(stop,index,list){
  var reason = stopSearchReason(stop,index,list);
  var distance = finite(Number(stop && stop.distMiles))
    ? fmtMiles(stop.distMiles)
    : "nearby";
  var routeLabel = stopRouteReturnLabel(stop);

  return reason + " • " + distance + " • " +
    String(routeLabel || "route watch").toLowerCase();
}

function stopSearchClosestVoice(stop,query){
  var q = String(query || "stop").trim();
  var name = String(stop && stop.name || q);
  var distance = finite(Number(stop && stop.distMiles))
    ? fmtMiles(stop.distMiles)
    : "nearby";
  var lane = String(stop && stop.lane || "");

  if(lane === "ahead"){
    return "Closest " + q + " is " + distance + " ahead at " + name + ".";
  }

  return "Closest " + q + " is " + distance + " away at " + name + ".";
}

function genericStopCategoryQuery(query){
  var q = String(query || "").toLowerCase().replace(/\s+/g," ").trim();

  if(/^(pharmacy|pharmacies|drugstore|drugstores)$/.test(q)){ return "pharmacy"; }
  if(/^(grocery|groceries|grocery store|supermarket)$/.test(q)){ return "grocery"; }
  if(/^(fuel|gas|gas station|diesel)$/.test(q)){ return "fuel"; }
  if(/^(coffee|cafe|coffee shop)$/.test(q)){ return "coffee"; }
  if(/^(food|restaurant|restaurants|fast food)$/.test(q)){ return "food"; }
  if(/^(hotel|hotels|motel|motels|lodging)$/.test(q)){ return "hotel"; }
  if(/^(parking|parking lot)$/.test(q)){ return "parking"; }
  if(/^(restroom|restrooms|bathroom|bathrooms|toilet|toilets)$/.test(q)){ return "restroom"; }
  if(/^(shopping|shops|stores)$/.test(q)){ return "shopping"; }
  if(/^(wellness|smoothie|nutrition)$/.test(q)){ return "wellness"; }
  if(/^(scenic|attractions|attraction)$/.test(q)){ return "scenic"; }

  return "";
}


function askSpecificFoodTerms(query,intent){
  var q=String(query||"").toLowerCase();
  var category=String(intent&&intent.category||"").toLowerCase();

  if(category==="mexican" || /\bmexican|taqueria|burrito|taco\b/.test(q)){
    return ["mexican","taqueria","burrito","taco"];
  }
  if(/\bpizza|pizzeria\b/.test(q)){return ["pizza","pizzeria","italian"];}
  if(/\bburger|hamburger\b/.test(q)){return ["burger","hamburger"];}
  if(/\bchicken|wings|wing\b/.test(q)){return ["chicken","wings","wing"];}
  if(/\bitalian\b/.test(q)){return ["italian","pizza","pizzeria"];}
  if(/\bsushi|japanese\b/.test(q)){return ["sushi","japanese"];}
  if(/\bchinese\b/.test(q)){return ["chinese"];}
  if(/\bbbq|barbecue\b/.test(q)){return ["bbq","barbecue"];}
  if(/\bsteak|steakhouse\b/.test(q)){return ["steak","steakhouse"];}
  if(/\bseafood\b/.test(q)){return ["seafood"];}
  return [];
}

function askStopSemanticText(stop){
  return String([
    stop&&stop.name,
    stop&&stop.address,
    stop&&stop.providerCategories,
    stop&&stop.providerCuisine,
    stop&&stop.type
  ].join(" ")).toLowerCase();
}

function askKnownCuisineMatch(stop,query,intent){
  var text=askStopSemanticText(stop);
  var q=String(query||"").toLowerCase();
  var cat=String(intent&&intent.category||"").toLowerCase();

  if(/\bpizza|pizzeria\b/.test(q)){
    return /pizza|pizzeria|domino|papa john|little caesar|marco'?s|pizza hut|hungry howie|jets pizza|jet'?s pizza/.test(text);
  }

  if(cat==="mexican" || /\bmexican|taqueria|burrito|taco\b/.test(q)){
    return /mexican|taqueria|burrito|taco|chipotle|moe'?s|quesadilla|cantina|cocina|el [a-z]|la [a-z]/.test(text);
  }

  if(/\bburger|hamburger\b/.test(q)){
    return /burger|hamburger|five guys|whataburger|wendy|burger king|mcdonald/.test(text);
  }

  if(/\bchicken|wings|wing\b/.test(q)){
    return /chicken|wing|chick-fil-a|chick fil a|popeyes|zaxby|raising cane|kfc/.test(text);
  }

  if(/\bsushi|japanese\b/.test(q)){ return /sushi|japanese|hibachi|ramen/.test(text); }
  if(/\bchinese\b/.test(q)){ return /chinese|china|wok|szechuan|sichuan|hunan/.test(text); }
  if(/\bbbq|barbecue\b/.test(q)){ return /bbq|barbecue|bar-b-q|smokehouse|smoke house/.test(text); }
  if(/\bsteak|steakhouse\b/.test(q)){ return /steak|steakhouse/.test(text); }
  if(/\bseafood\b/.test(q)){ return /seafood|fish|oyster|shrimp|crab/.test(text); }
  if(/\bitalian\b/.test(q)){ return /italian|trattoria|ristorante|pasta|pizza/.test(text); }

  return true;
}

function askObviousNonRestaurant(stop){
  var text=askStopSemanticText(stop);

  return /walmart|target|costco|sam'?s club|kroger|aldi|walgreens|cvs|rite aid|pharmacy|drugstore|home depot|lowe'?s|best buy|dollar general|family dollar|dollar tree|autozone|o'?reilly|advance auto|shell|exxon|chevron|circle k|pilot|flying j|love'?s|gas station|fuel station|supermarket|grocery store/.test(text);
}

function filterAskSpecificTypedResults(stops,query,intent){
  var original=String(askPendingOriginalQuery||query||"").toLowerCase().trim();
  var terms=askSpecificFoodTerms(original,intent);
  var named=String(intent&&intent.namedPlace||"").toLowerCase().trim();
  var filtered;

  if(named){
    var wanted=named.replace(/['’]/g,"");
    filtered=(stops||[]).filter(function(stop){
      return askStopSemanticText(stop).replace(/['’]/g,"").indexOf(wanted)>=0;
    });
    return filtered;
  }

  if(!terms.length){return stops;}

  /*
    A specific provider query such as "Mexican restaurant" may correctly return
    "El Porton" or "La Hacienda" even though the business name does not contain
    the word "Mexican". Do not throw those away. Instead reject obvious
    non-restaurant false positives (Walmart, grocery, pharmacy, fuel, etc.) and
    preserve the provider's cuisine-query ordering for the remaining places.
  */
  filtered=(stops||[]).filter(function(stop){
    if(askObviousNonRestaurant(stop)){ return false; }
    return askKnownCuisineMatch(stop,original,intent);
  });

  return filtered;
}


function askIsSpecificCuisineRequest(query,intent){
  var q=String(query||"").toLowerCase();
  var cat=String(intent&&intent.category||"").toLowerCase();

  if(cat==="mexican"){ return true; }

  return /\bpizza|pizzeria|mexican|taqueria|burrito|taco|burger|hamburger|chicken|wings|wing|italian|sushi|japanese|chinese|bbq|barbecue|steak|steakhouse|seafood\b/.test(q);
}

function askCuisineSearchTerm(query,intent){
  var q=String(query||"").toLowerCase();
  var cat=String(intent&&intent.category||"").toLowerCase();

  if(cat==="mexican" || /\bmexican|taqueria|burrito|taco\b/.test(q)){ return "Mexican restaurant"; }
  if(/\bpizza|pizzeria\b/.test(q)){ return "pizza restaurant"; }
  if(/\bburger|hamburger\b/.test(q)){ return "burger restaurant"; }
  if(/\bchicken|wings|wing\b/.test(q)){ return "chicken restaurant"; }
  if(/\bitalian\b/.test(q)){ return "Italian restaurant"; }
  if(/\bsushi|japanese\b/.test(q)){ return "sushi restaurant"; }
  if(/\bchinese\b/.test(q)){ return "Chinese restaurant"; }
  if(/\bbbq|barbecue\b/.test(q)){ return "barbecue restaurant"; }
  if(/\bsteak|steakhouse\b/.test(q)){ return "steakhouse"; }
  if(/\bseafood\b/.test(q)){ return "seafood restaurant"; }

  return String(query||"restaurant");
}

function scanAskSpecificCuisine(query,intent){
  var q=String(query||"").replace(/\s+/g," ").trim();
  var term=askCuisineSearchTerm(q,intent);
  var points=scanPoints();
  var pending=0;
  var all=[];
  var token;
  var i;

  if(scanBusy){
    toast("Stop search already running");
    return;
  }

  if(!GEOAPIFY_KEY){
    deliverAskResults([]);
    toast("Missing Geoapify key");
    return;
  }

  if(!points.length){
    deliverAskResults([]);
    toast("No route corridor yet");
    return;
  }

  stopSearchQuery=q;
  currentScanType="food";
  scanBusy=true;
  scanToken+=1;
  token=scanToken;

  closeStopSheet();

  setText("diqModePill","ASK IQ");
  setText("diqSub","Searching for " + term);
  setText("diqSigTraffic","Searching " + term);
  setText("diqSigRoute","Cuisine match first");

  function addPlaceFeatures(data,lane){
    var features=data&&data.features?data.features:[];
    var k;
    var stop;

    for(k=0;k<features.length;k++){
      stop=normalizeFeature(features[k],"food",lane);
      if(stop){ all.push(stop); }
    }
  }

  function addTextFeatures(data,lane){
    var features=data&&data.features?data.features:[];
    var k;
    var stop;

    for(k=0;k<features.length;k++){
      stop=normalizeSearchFeature(features[k],term,lane);
      if(stop){ all.push(stop); }
    }
  }

  function finishOne(){
    pending-=1;
    if(pending>0){ return; }
    finish();
  }

  function finish(){
    if(token!==scanToken){ return; }

    scanBusy=false;
    all=dedupeStops(all);
    all=filterAskSpecificTypedResults(all,q,intent);
    all.sort(function(a,b){
      var aRoute = finite(Number(a && a.routeDistMiles))
        ? Number(a.routeDistMiles)
        : nearestRouteDistanceToStop(a);
      var bRoute = finite(Number(b && b.routeDistMiles))
        ? Number(b.routeDistMiles)
        : nearestRouteDistanceToStop(b);
      var aPractical = aRoute !== null && finite(Number(aRoute)) && Number(aRoute) <= 1.5 ? 0 : 1;
      var bPractical = bRoute !== null && finite(Number(bRoute)) && Number(bRoute) <= 1.5 ? 0 : 1;
      var aDist = finite(Number(a && a.distMiles)) ? Number(a.distMiles) : 999;
      var bDist = finite(Number(b && b.distMiles)) ? Number(b.distMiles) : 999;

      if(aPractical !== bPractical){ return aPractical - bPractical; }

      /*
        Once both are practical route-ahead matches, favor the closer one.
        Distance is useful here, but it does not invalidate a 25+ mile result.
      */
      if(aDist !== bDist){ return aDist - bDist; }

      return Number(aRoute || 999) - Number(bRoute || 999);
    });
    all=all.slice(0,10);

    for(i=0;i<all.length;i++){
      all[i].routeDistMiles=nearestRouteDistanceToStop(all[i]);
      all[i].confidence=confidenceForStop(all[i],i);
      all[i].confidenceLabel=confidenceLabel(all[i].confidence);
      all[i].tag=stopSearchReason(all[i],i,all);
    }

    stopSearchResults=all.slice(0);
    deliverAskResults(all);
    drawStopPins(all);

    if(all.length){
      setText("diqSigTraffic",String(all.length)+" cuisine matches");
      setText("diqSigArrival",String(all[0].name||term));
      setText("diqSigRoute","Best relevant match first");
      setText("diqRouteIntelText","ASK IQ • "+term);
      setText("diqRouteIntelSub",stopInfoLine(all[0],0));
      fitStops(activeDisplayStops&&activeDisplayStops.length?activeDisplayStops:all);
    }else{
      setText("diqModePill","COCKPIT");
      setText("diqSub","No strong "+term+" match found");
      setText("diqSigTraffic","No cuisine match");
      setText("diqSigRoute","Try another search");
    }
  }

  for(i=0;i<points.length;i++){
    (function(point){
      var lane=String(point&&point.lane||"ahead");

      /*
        1) Places/category API discovers real restaurants around each route point.
        2) Targeted text search helps with named/cuisine-specific POIs.
        Results are merged, deduped, then cuisine-filtered.
      */
      pending+=1;
      fetchJson(placesUrl(point,"food"),function(data){
        addPlaceFeatures(data,lane);
        finishOne();
      });

      pending+=1;
      fetchJson(fallbackUrl(point,"food",term),function(data){
        addTextFeatures(data,lane);
        finishOne();
      });
    })(points[i]);
  }

  window.setTimeout(function(){
    if(token===scanToken && scanBusy){
      pending=0;
      finish();
    }
  },9500);
}

function scanTypedStop(query){
  var q = String(query || "").replace(/\s+/g," ").trim();
  var points;
  var pending = 0;
  var all = [];
  var token;
  var i;

  if(q.length < 2){
    toast("Type a business or stop");
    return;
  }

  /*
    V27: generic category searches should use the full category engine, which
    combines Geoapify category results with brand/name fallback searches.
    Exact brand/business searches continue through typed search.
  */
  var genericCategory = genericStopCategoryQuery(q);
  if(genericCategory && !askPendingRequestId){
    scanStops(genericCategory);
    return;
  }

  if(scanBusy){
    toast("Stop search already running");
    return;
  }

  if(!GEOAPIFY_KEY){
    toast("Missing Geoapify key");
    return;
  }

  if(!getRouteGeo()){
    toast("Start a route first");
    return;
  }

  points = scanPoints();

  if(!points.length){
    toast("No route corridor yet");
    return;
  }

  stopSearchQuery = q;
  currentScanType = stopSearchCategoryForQuery(q);
  scanBusy = true;
  scanToken += 1;
  token = scanToken;

  closeStopSheet();

  setText("diqModePill","STOP IQ");
  setText("diqSub","Finding the closest " + q);
  setText("diqSigTraffic","Searching " + q);
  setText("diqSigArrival","Looking ahead");
  setText("diqSigRoute","Closest useful stop first");
  toast("Finding the closest " + q);

  function addFeatures(data,lane){
    var features = data && data.features ? data.features : [];
    var k;
    var stop;

    for(k = 0; k < features.length; k++){
      stop = normalizeSearchFeature(features[k],q,lane);
      if(stop){ all.push(stop); }
    }
  }

  function done(){
    pending -= 1;
    if(pending > 0){ return; }

    if(token !== scanToken){ return; }

    scanBusy = false;
    all = dedupeStops(all);

    if(askPendingRequestId){
      all = filterAskSpecificTypedResults(all,q,askPendingIntent);
    }

    all.sort(sortTypedStops(q));
    all = all.slice(0,8);

    for(i = 0; i < all.length; i++){
      all[i].confidence = confidenceForStop(all[i],i);
      all[i].confidenceLabel = confidenceLabel(all[i].confidence);
      all[i].searchReason = stopSearchReason(all[i],i,all);
      all[i].tag = all[i].searchReason;
      all[i].infoLabel = all[i].searchReason;
      all[i].searchSummary = stopSearchResultSummary(all[i],i,all);
    }

    stopSearchResults = all.slice(0);
    var deliveredToAsk = deliverAskResults(all);
    drawStopPins(all);

    if(all.length){
      setText("diqSigTraffic",String(all.length) + " matches");
      setText("diqSigArrival",String(all[0].name || q));
      setText("diqRouteIntelText","Stop IQ • " + q);
      setText(
        "diqRouteIntelSub",
        stopInfoLine(all[0],0)
      );
      setText("diqSigRoute","Closest useful stop first");
      toast("Closest " + q + ": " + String(all[0].name || q));
      fitStops(activeDisplayStops && activeDisplayStops.length
        ? activeDisplayStops
        : all);
      if(!deliveredToAsk){
        try{
          if(window.DRIVE_IQ_DRIVE_MODE &&
             typeof window.DRIVE_IQ_DRIVE_MODE.speak === "function"){
            window.DRIVE_IQ_DRIVE_MODE.speak(
              stopSearchClosestVoice(all[0],q)
            );
          }else{
            maybeSpeakScanSummary(all,currentScanType);
          }
        }catch(eVoice){
          maybeSpeakScanSummary(all,currentScanType);
        }
      }
    }else{
      if(!deliveredToAsk){ deliverAskResults([]); }
      setText("diqModePill","COCKPIT");
      setText("diqSub","No " + q + " found along this route");
      setText("diqSigTraffic","No matches");
      setText("diqSigRoute","Try another search");
      toast("No " + q + " found along this route");
    }
  }

  for(i = 0; i < points.length; i++){
    pending += 1;
    fetchJson(
      typedSearchUrl(points[i],q),
      (function(lane){
        return function(data){
          addFeatures(data,lane);
          done();
        };
      })(String(points[i].lane || "ahead"))
    );
  }

  window.setTimeout(function(){
    if(token === scanToken && scanBusy){
      pending = 0;
      done();
    }
  },9500);
}

function injectStopIqStyles(){
  var style;

  if(byId("diqStopIqSearchStyles")){ return; }

  style = document.createElement("style");
  style.id = "diqStopIqSearchStyles";
  style.textContent = [
    ".diqStopIqSearchWrap{",
      "display:flex;gap:8px;margin:10px 0 12px;",
      "padding:0 2px;",
    "}",
    ".diqStopIqSearchInput{",
      "flex:1;min-width:0;height:46px;padding:0 14px;",
      "border-radius:15px;border:1px solid rgba(35,229,255,.40);",
      "background:linear-gradient(180deg,rgba(5,13,23,.98),rgba(8,20,31,.96));color:#fff;",
      "font-size:15px;font-weight:800;outline:none;",
    "}",
    ".diqStopIqSearchInput::placeholder{color:rgba(183,220,232,.72);}",
    ".diqStopIqSearchInput:focus{border-color:rgba(35,229,255,.85);box-shadow:0 0 0 3px rgba(35,229,255,.10),0 0 18px rgba(35,229,255,.10);}",
    ".diqStopIqSearchButton{",
      "height:46px;padding:0 13px;border-radius:15px;",
      "border:1px solid rgba(60,255,196,.35);",
      "background:linear-gradient(135deg,#20e6a0,#23c9ff);",
      "color:#041017;font-size:13px;font-weight:1000;",
    "}",
    ".diqStopIqTitle{",
      "font-size:18px;font-weight:1000;color:#fff;",
      "margin:2px 0 2px;",
    "}",
    ".diqStopIqSubtitle{",
      "font-size:11px;line-height:1.35;color:#9fb7c8;",
      "margin-bottom:4px;",
    "}",
    ".diqStopIqWalmart{",
      "border-color:rgba(255,210,64,.45)!important;",
      "background:rgba(255,190,32,.12)!important;",
    "}"
  ].join("");

  try{ document.head.appendChild(style); }catch(e){}
}

function ensureStopIqSearch(){
  var sheet = byId("diqStopSheet");
  var grid;
  var wrap;
  var title;
  var subtitle;
  var input;
  var button;
  var close;

  if(!sheet){ return false; }

  injectStopIqStyles();

  grid = sheet.querySelector(".diqStopGrid");

  if(!sheet.querySelector(".diqStopIqTitle")){
    title = document.createElement("div");
    title.className = "diqStopIqTitle";
    title.textContent = "Stop Along the Way";

    subtitle = document.createElement("div");
    subtitle.className = "diqStopIqSubtitle";
    var activeDest = getDestination();
    var destName = activeDest && (
      activeDest.name ||
      activeDest.label ||
      activeDest.address
    );

    subtitle.textContent = destName
      ? "Along your route to " + String(destName) + ". Closest useful stop appears first."
      : "Search a business or choose a quick stop. Closest useful stop appears first.";

    if(grid){
      sheet.insertBefore(subtitle,grid);
      sheet.insertBefore(title,subtitle);
    }else{
      sheet.appendChild(title);
      sheet.appendChild(subtitle);
    }
  }

  wrap = sheet.querySelector(".diqStopIqSearchWrap");

  if(!wrap){
    wrap = document.createElement("div");
    wrap.className = "diqStopIqSearchWrap";

    input = document.createElement("input");
    input.type = "search";
    input.className = "diqStopIqSearchInput";
    input.placeholder = "Search any business or place…";
    input.setAttribute("aria-label","Search for a stop along the route");
    input.autocomplete = "off";
    input.spellcheck = false;

    button = document.createElement("button");
    button.type = "button";
    button.className = "diqStopIqSearchButton";
    button.textContent = "FIND";

    wrap.appendChild(input);
    wrap.appendChild(button);

    if(grid){
      sheet.insertBefore(wrap,grid);
    }else{
      sheet.appendChild(wrap);
    }

    stopSearchInputEl = input;
    stopSearchButtonEl = button;

    button.onclick = function(){
      scanTypedStop(input.value);
    };

    input.addEventListener("keydown",function(event){
      if(event && (event.key === "Enter" || event.keyCode === 13)){
        if(event.preventDefault){ event.preventDefault(); }
        scanTypedStop(input.value);
      }
    },false);
  }else{
    stopSearchInputEl = wrap.querySelector("input");
    stopSearchButtonEl = wrap.querySelector("button");
  }

  ensureStopChipAfter(
    "coffee",
    "walmart",
    "🛒 Walmart"
  );
  ensureStopChipAfter(
    "walmart",
    "pharmacy",
    "💊 Pharmacy"
  );

  var pharmacy = sheet.querySelector('[data-stop="pharmacy"]');
  if(pharmacy){
    pharmacy.onclick = function(){
      scanTypedStop("Pharmacy");
    };
    pharmacy.__diqFindStopsBound = true;
  }

  var walmart = sheet.querySelector('[data-stop="walmart"]');
  if(walmart){
    walmart.className =
      String(walmart.className || "") +
      " diqStopIqWalmart";
    walmart.onclick = function(){
      scanTypedStop("Walmart");
    };
    walmart.__diqFindStopsBound = true;
  }

  close = byId("diqStopSheetClose");
  if(close){
    close.setAttribute("aria-label","Close Stop IQ");
  }

  return true;
}

function relabelStopIqEntryPoints(){
  var btn = byId("diqFindStops");
  var nodes;
  var i;
  var text;

  if(btn){
    btn.textContent = "STOP ALONG THE WAY";
    btn.setAttribute("aria-label","Stop Along the Way");
  }

  try{
    nodes = document.querySelectorAll(
      "[data-add-stop],.diqAddStopButton,.diqAddStop,.diqStopPlus"
    );
  }catch(e){
    nodes = [];
  }

  for(i = 0; i < nodes.length; i++){
    text = String(nodes[i].textContent || "").trim();

    if(text === "+" || !text){
      nodes[i].textContent = "ADD STOP";
      nodes[i].setAttribute("aria-label","Add Stop");
    }
  }
}

/* =========================================================
   DRIVE IQ — FEATURED LOCAL PICKS V1
   Sponsored/local partners that merge into relevant stop scans.
   Example locked: Avellino's in Southaven, BOGO appetizers on Saturdays.
========================================================= */

function sponsoredLocalPartners(){
  return [{
    id:"sponsored_avellinos_southaven",
    name:"Avellino's",
    type:"food",
    lat:34.9650,
    lng:-89.9846,
    city:"Southaven",
    radiusMiles:18,
    active:true,
    sponsor:true,
    badge:"Featured Local Pick",
    sponsorBadge:"Saturdays",
    offer:"BOGO appetizers every Saturday",
    offerShort:"BOGO Appetizers Saturdays",
    reason:"Local Italian favorite near Goodman Road",
    phone:"662-349-9968",
    priority:98,
    source:"featured_local_pick"
  }];
}

function scanPointNearStop(points,stop){
  var i;
  var radius = Number(stop && stop.radiusMiles ? stop.radiusMiles : 12);

  if(!points || !points.length || !stop){ return false; }

  for(i = 0; i < points.length; i++){
    if(!points[i]){ continue; }
    if(!finite(Number(points[i].lat)) || !finite(Number(points[i].lng))){ continue; }
    if(milesBetween([Number(points[i].lng),Number(points[i].lat)],[Number(stop.lng),Number(stop.lat)]) <= radius){
      return true;
    }
  }

  return false;
}

function routeNearStop(stop){
  var d;
  var radius = Number(stop && stop.radiusMiles ? stop.radiusMiles : 12);

  if(!stop){ return false; }

  d = nearestRouteDistanceToStop(stop);
  if(d !== null && d <= radius){ return true; }

  return false;
}

function currentFixNearStop(stop){
  var fix = getCurrentFix();
  var radius = Number(stop && stop.radiusMiles ? stop.radiusMiles : 12);

  if(!fix || !stop){ return false; }

  return milesBetween([Number(fix.lng),Number(fix.lat)],[Number(stop.lng),Number(stop.lat)]) <= radius;
}

function sponsoredStopToStop(partner){
  var fix = getCurrentFix();
  var dist = null;

  if(fix){
    dist = milesBetween([fix.lng,fix.lat],[Number(partner.lng),Number(partner.lat)]);
  }

  return {
    id:String(partner.id),
    name:String(partner.name),
    type:stopType(partner.type),
    lat:Number(partner.lat),
    lng:Number(partner.lng),
    distMiles:dist,
    lane:"nearby",
    infoLabel:"Featured",
    routeDistMiles:null,
    sponsor:true,
    featured:true,
    badge:String(partner.badge || "Featured Local Pick"),
    sponsorBadge:String(partner.sponsorBadge || "Featured"),
    offer:String(partner.offer || ""),
    offerShort:String(partner.offerShort || partner.offer || ""),
    reason:String(partner.reason || ""),
    phone:String(partner.phone || ""),
    priority:Number(partner.priority || 90),
    source:String(partner.source || "featured_local_pick")
  };
}

function mergeSponsoredStops(stops,type,points){
  var out = stops || [];
  var partners = sponsoredLocalPartners();
  var t = stopType(type);
  var i;
  var p;

  if(t !== "food"){ return out; }

  for(i = 0; i < partners.length; i++){
    p = partners[i];
    if(!p || !p.active){ continue; }
    if(stopType(p.type) !== t){ continue; }

    if(currentFixNearStop(p) || routeNearStop(p) || scanPointNearStop(points,p)){
      out.push(sponsoredStopToStop(p));
    }
  }

  return out;
}

function shouldKeepRouteRelevantStop(stop,type){
  var t = stopType(type || (stop && stop.type));
  var routeDist;
  var dist;
  var lane;

  if(!stop){ return false; }
  if(stop.sponsor){ return true; }

  /*
    Food should feel route-aware without becoming too strict.
    We want useful options along the corridor, not only a single featured result.
  */
  if(t === "food"){
    routeDist = nearestRouteDistanceToStop(stop);
    dist = finite(Number(stop.distMiles)) ? Number(stop.distMiles) : 9999;
    lane = String(stop.lane || "");

    if(routeDist === null){ return true; }
    stop.routeDistMiles = routeDist;

    if(routeDist <= 4.5){ return true; }
    if(lane === "ahead" && routeDist <= 6.0){ return true; }
    if(dist <= 5.0 && routeDist <= 7.0){ return true; }

    return false;
  }

  return true;
}

function filterRouteRelevantStops(stops,type){
  var out = [];
  var i;

  if(!stops || !stops.length){ return out; }

  for(i = 0; i < stops.length; i++){
    if(shouldKeepRouteRelevantStop(stops[i],type)){
      out.push(stops[i]);
    }
  }

  return out;
}

function relaxFoodResults(stops){
  var out = [];
  var i;
  var stop;
  var routeDist;
  var dist;

  if(!stops || !stops.length){ return out; }

  for(i = 0; i < stops.length; i++){
    stop = stops[i];
    if(!stop){ continue; }

    if(stop.sponsor){
      out.push(stop);
      continue;
    }

    routeDist = nearestRouteDistanceToStop(stop);
    dist = finite(Number(stop.distMiles)) ? Number(stop.distMiles) : 9999;

    if(routeDist !== null){ stop.routeDistMiles = routeDist; }

    if(routeDist === null || routeDist <= 7.0 || dist <= 8.0){
      out.push(stop);
    }
  }

  if(!out.length){
    return stops;
  }

  return out;
}

function laneRank(lane){
  if(lane === "nearby"){ return 0; }
  if(lane === "ahead"){ return 1; }
  return 2;
}

function dedupeStops(stops){
  var seen = {};
  var out = [];
  var i;
  var s;
  var key;

  for(i = 0; i < stops.length; i++){
    s = stops[i];
    if(!s){ continue; }

    key = String(Number(s.lat).toFixed(4)) + "," + String(Number(s.lng).toFixed(4)) + "|" + String(s.name || "").toLowerCase();

    if(seen[key]){ continue; }

    seen[key] = true;
    out.push(s);
  }

  return out;
}

function scoreStop(stop){
  var score = 0;
  var lane = stop ? String(stop.lane || "") : "";
  var dist = stop && finite(Number(stop.distMiles)) ? Number(stop.distMiles) : 9999;
  var routeDist = nearestRouteDistanceToStop(stop);

  if(lane === "nearby"){ score += 52; }
  else if(lane === "ahead"){ score += 42; }
  else{ score += 22; }

  if(dist <= 1.5){ score += 32; }
  else if(dist <= 4){ score += 24; }
  else if(dist <= 10){ score += 14; }
  else if(dist <= 25){ score += 7; }
  else{ score -= Math.min(24,Math.round(dist / 6)); }

  if(routeDist !== null){
    stop.routeDistMiles = routeDist;

    if(routeDist <= 0.25){ score += 34; }
    else if(routeDist <= 0.6){ score += 24; }
    else if(routeDist <= 1.1){ score += 13; }
    else if(routeDist > 2.5){ score -= 14; }
  }

  if(stopType(stop && stop.type) === "food" && !(stop && stop.sponsor)){
    if(routeDist !== null){
      if(routeDist <= 0.5){ score += 34; }
      else if(routeDist <= 1.0){ score += 22; }
      else if(routeDist <= 2.0){ score += 14; }
      else if(routeDist <= 4.0){ score += 4; }
      else if(routeDist > 6.0){ score -= 60; }
      else if(routeDist > 4.0){ score -= 25; }
    }
  }

  if(stop && stop.sponsor){
    score += Number(stop.priority || 90);
    if(stop.offer){ score += 18; }
  }

  if(stopType(stop && stop.type) === "food"){
    score += foodBrandRank(stop && stop.name) / 3;
    score += preferredStopBoost(stop);
  }else{
    score += categoryBrandRank(stop) / 4;
  }

  if(stopType(stop && stop.type) === "shopping"){
    var shopName = String(stop && stop.name ? stop.name : "").toLowerCase();
    if(shopName.indexOf("tanger") > -1){ score += 42; }
    else if(shopName.indexOf("outlet") > -1){ score += 30; }
    else if(shopName.indexOf("mall") > -1 || shopName.indexOf("shopping center") > -1){ score += 18; }
    else if(shopName.indexOf("boutique") > -1 || shopName.indexOf("gift") > -1){ score += 14; }
  }

  score += confidenceForStop(stop,1) / 5;

  return score;
}

function practicalDistanceTier(stop){
  var d = stop && finite(Number(stop.distMiles)) ? Number(stop.distMiles) : 9999;
  if(d <= 0.75){ return 0; }
  if(d <= 1.50){ return 1; }
  if(d <= 2.50){ return 2; }
  if(d <= 4.00){ return 3; }
  if(d <= 6.00){ return 4; }
  if(d <= 10.00){ return 5; }
  return 6;
}

function sortStops(a,b){
  var at = practicalDistanceTier(a);
  var bt = practicalDistanceTier(b);
  var as;
  var bs;
  var ad;
  var bd;

  /*
    V27: proximity is the first authority for category scans. A stop in a
    materially closer distance tier wins before route-detour scoring.
  */
  if(at !== bt){ return at - bt; }

  as = scoreStop(a);
  bs = scoreStop(b);
  if(as !== bs){ return bs - as; }

  ad = finite(Number(a.distMiles)) ? Number(a.distMiles) : 9999;
  bd = finite(Number(b.distMiles)) ? Number(b.distMiles) : 9999;
  return ad - bd;
}

function removeLayer(map,id){
  try{ if(map && map.getLayer(id)){ map.removeLayer(id); } }catch(e){}
}

function removeSource(map,id){
  try{ if(map && map.getSource(id)){ map.removeSource(id); } }catch(e){}
}



function getDestination(){
  var payload = safeParse(lsGet(LS.DRIVE_PAYLOAD));
  var dest = null;

  if(payload && payload.destination){
    dest = payload.destination;
  }else{
    dest = safeParse(lsGet(LS.DEST));
  }

  if(dest && finite(Number(dest.lat)) && finite(Number(dest.lng))){
    return {
      label:String(dest.label || dest.name || "Destination"),
      lat:Number(dest.lat),
      lng:Number(dest.lng)
    };
  }

  return null;
}

function fmtTime(seconds){
  var m = Math.round(Number(seconds || 0) / 60);
  var h;
  var r;

  if(!finite(m) || m <= 0){ return "--"; }

  h = Math.floor(m / 60);
  r = m % 60;

  if(h <= 0){ return String(r) + " min"; }
  if(r <= 0){ return String(h) + " hr"; }

  return String(h) + " hr " + String(r) + " min";
}

function routeFC(feature){
  return { type:"FeatureCollection", features:[feature] };
}

function buildWaypointRoute(stop,done){
  var fix = getCurrentFix();
  var dest = getDestination();
  var waypoints;
  var url;

  if(!stop || !finite(Number(stop.lat)) || !finite(Number(stop.lng))){
    done(null,"No stop selected");
    return;
  }

  if(!fix){
    done(null,"GPS not ready");
    return;
  }

  if(!dest){
    done(null,"Destination not found");
    return;
  }

  if(!GEOAPIFY_KEY){
    done(null,"Missing Geoapify key");
    return;
  }

  waypoints =
    String(fix.lat) + "," + String(fix.lng) + "|" +
    String(stop.lat) + "," + String(stop.lng) + "|" +
    String(dest.lat) + "," + String(dest.lng);

  url = "https://api.geoapify.com/v1/routing?waypoints=" +
    encodeURIComponent(waypoints) +
    "&mode=drive&details=instruction_details&format=geojson&apiKey=" +
    encodeURIComponent(GEOAPIFY_KEY);

  fetchJson(url,function(data){
    var features = data && data.features ? data.features : [];
    if(!features.length){
      done(null,"Route update failed");
      return;
    }

    done(features[0],null);
  });
}

function saveWaypointRoute(stop,feature){
  /*
    V200 PASSIVE:
    Find Stops never writes active route geometry, route intelligence, or the
    drive payload. Route installation belongs only to drive-iq-drive-mode.js.
  */
  try{
    if(stop){ lsJSON(LS.ADDED_STOP,stop); }
  }catch(e){}
  return !!(stop && feature);
}

function flattenRouteCoordinates(value,out){
  var i;

  out = out || [];
  if(!value){ return out; }

  if(Object.prototype.toString.call(value) === "[object Array]"){
    if(
      value.length >= 2 &&
      finite(Number(value[0])) &&
      finite(Number(value[1]))
    ){
      out.push([Number(value[0]),Number(value[1])]);
      return out;
    }

    for(i = 0; i < value.length; i++){
      flattenRouteCoordinates(value[i],out);
    }
  }

  return out;
}

function featureRouteCoordinates(feature){
  try{
    return flattenRouteCoordinates(
      feature && feature.geometry ? feature.geometry.coordinates : null,
      []
    );
  }catch(e){
    return [];
  }
}

function liveRouteContainsStop(engine,stop){
  var coords = [];
  var i;
  var d;
  var best = 9999;

  if(!engine || !stop){ return false; }

  try{
    if(typeof engine.getCoordinates === "function"){
      coords = flattenRouteCoordinates(engine.getCoordinates() || [],[]);
    }
  }catch(e1){
    coords = [];
  }

  if(!coords.length){ return false; }

  for(i = 0; i < coords.length; i++){
    d = milesBetween(
      [Number(coords[i][0]),Number(coords[i][1])],
      [Number(stop.lng),Number(stop.lat)]
    );
    if(finite(Number(d)) && Number(d) < best){ best = Number(d); }
  }

  return best <= 0.22;
}

function setExactWaypointRouteSource(routeGeo){
  /* V200 PASSIVE: active route rendering is owned by drive-iq-drive-mode.js. */
  return false;
}

function promoteWaypointRouteToModularDrive(stop,feature){
  /*
    V200 PASSIVE:
    Deliberately forbidden. This provider may discover/rank/render stops, but
    it may not install routes, emit route-installed/ready/geometry events, or
    mutate the active route source.
  */
  return false;
}


function speakSmall(line){
  try{
    if(window.DRIVE_IQ_DRIVE_MODE && typeof window.DRIVE_IQ_DRIVE_MODE.speak === "function"){
      window.DRIVE_IQ_DRIVE_MODE.speak(line);
      return;
    }
  }catch(e1){}

  try{
    if(window.speechSynthesis && window.SpeechSynthesisUtterance){
      var u = new SpeechSynthesisUtterance(String(line || ""));
      u.lang = "en-US";
      u.rate = 0.94;
      u.pitch = 1;
      u.volume = 1;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    }
  }catch(e2){}
}

function removeStopCard(){
  if(stopCardEl && stopCardEl.parentNode){
    try{ stopCardEl.parentNode.removeChild(stopCardEl); }catch(e){}
  }

  stopCardEl = null;
}

function stopCardCss(){
  return [
    "position:fixed",
    "left:18px",
    "right:18px",
    "bottom:calc(150px + env(safe-area-inset-bottom,0px))",
    "z-index:999999",
    "border-radius:24px",
    "background:linear-gradient(180deg,rgba(5,12,22,.96),rgba(5,12,22,.88))",
    "border:1px solid rgba(255,206,105,.34)",
    "box-shadow:0 22px 46px rgba(0,0,0,.50),0 0 32px rgba(255,206,105,.14)",
    "backdrop-filter:blur(18px)",
    "-webkit-backdrop-filter:blur(18px)",
    "padding:14px",
    "color:#fff",
    "font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif",
    "pointer-events:auto"
  ].join(";");
}

function cardButtonCss(kind){
  var bg = kind === "primary" ? "linear-gradient(135deg,#28ffbe,#00e5ff)" : "rgba(255,255,255,.08)";
  var color = kind === "primary" ? "#061018" : "#fff";
  var border = kind === "primary" ? "1px solid rgba(182,255,235,.65)" : "1px solid rgba(255,255,255,.15)";

  return [
    "min-height:46px",
    "border-radius:18px",
    "border:" + border,
    "background:" + bg,
    "color:" + color,
    "font-size:13px",
    "font-weight:1000",
    "letter-spacing:.55px",
    "cursor:pointer",
    "box-shadow:0 12px 26px rgba(0,0,0,.35)",
    "text-transform:uppercase"
  ].join(";");
}

function showStopCard(stop,index){
  var name;
  var line;
  var icon;
  var card;
  var top;
  var title;
  var sub;
  var actions;
  var addBtn;
  var closeBtn;

  removeStopCard();

  selectedStop = stop;

  name = String(stop && stop.name ? stop.name : "Stop");
  line = stopInfoLine(stop,index);
  if(stop && stop.sponsor){
    line = String(stop.offerShort || stop.offer || "Featured Local Pick");
  }
  icon = letter(stop && stop.type,stop && stop.name);

  card = document.createElement("div");
  card.id = "diqStopMiniCard";
  card.style.cssText = stopCardCss();

  top = document.createElement("div");
  top.style.cssText = "display:grid;grid-template-columns:54px 1fr;gap:12px;align-items:center;margin-bottom:12px;";

  var iconBox = document.createElement("div");
  iconBox.textContent = icon;
  iconBox.style.cssText = [
    "width:54px",
    "height:54px",
    "border-radius:20px",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "font-size:27px",
    "background:radial-gradient(circle at 35% 26%,rgba(255,255,255,.92),rgba(255,255,255,.42) 34%,#ffce69 76%)",
    "border:3px solid #061018",
    "box-shadow:0 0 24px rgba(255,206,105,.36),0 10px 22px rgba(0,0,0,.35)"
  ].join(";");

  var textWrap = document.createElement("div");
  textWrap.style.cssText = "min-width:0;";

  title = document.createElement("div");
  title.textContent = name;
  title.style.cssText = "font-size:17px;font-weight:1000;line-height:1.1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;";

  sub = document.createElement("div");
  sub.textContent = line;
  sub.style.cssText = stop && stop.sponsor ?
    "margin-top:5px;font-size:12px;font-weight:850;color:rgba(255,255,255,.78);line-height:1.25;white-space:normal;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;" :
    "margin-top:5px;font-size:12px;font-weight:750;color:rgba(255,255,255,.72);line-height:1.25;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;";

  textWrap.appendChild(title);
  textWrap.appendChild(sub);
  top.appendChild(iconBox);
  top.appendChild(textWrap);

  actions = document.createElement("div");
  actions.style.cssText = "display:grid;grid-template-columns:1.25fr .75fr;gap:10px;";

  addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.textContent = "ADD TO ROUTE";
  addBtn.style.cssText = cardButtonCss("primary");
  addBtn.onclick = function(ev){
    if(ev && ev.stopPropagation){ ev.stopPropagation(); }
    addStopToRoute(stop);
  };

  closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.textContent = "CANCEL";
  closeBtn.style.cssText = cardButtonCss("secondary");
  closeBtn.onclick = function(ev){
    if(ev && ev.stopPropagation){ ev.stopPropagation(); }
    removeStopCard();
  };

  actions.appendChild(addBtn);
  actions.appendChild(closeBtn);

  card.appendChild(top);
  card.appendChild(actions);

  document.body.appendChild(card);
  stopCardEl = card;
}

function addStopToRoute(stop){
  var payload;
  var sent = false;

  if(!stop){ return false; }

  payload = {
    name:String(stop.name || "Stop"),
    address:String(stop.address || stop.formatted || ""),
    lat:Number(stop.lat),
    lng:Number(stop.lng),
    type:String(stop.type || currentScanType || "stop"),
    source:"find-stops-v200-passive"
  };

  if(!finite(payload.lat) || !finite(payload.lng)){
    toast("Stop location unavailable");
    return false;
  }

  setText("diqSub","Adding stop…");
  setText("diqSigRoute","Adding stop");
  toast("Adding stop…");

  /*
    Route mutation is delegated to the active Drive Mode/native bridge.
    Find Stops itself never rebuilds, redraws, replaces, or stores route geometry.
  */
  try{
    if(window.DriveIQNative && typeof window.DriveIQNative.addStop === "function"){
      window.DriveIQNative.addStop(payload);
      sent = true;
    }
  }catch(e1){}

  try{
    if(!sent && window.webkit && window.webkit.messageHandlers &&
       window.webkit.messageHandlers.driveIQAddStop){
      window.webkit.messageHandlers.driveIQAddStop.postMessage(payload);
      sent = true;
    }
  }catch(e2){}

  try{
    if(!sent){
      window.dispatchEvent(new CustomEvent("driveiq:addstop",{detail:payload}));
      sent = true;
    }
  }catch(e3){}

  if(sent){
    try{ rememberPreferredStop(stop); }catch(e4){}
    setText("diqRouteIntelText","Stop Selected");
    setText("diqRouteIntelSub",String(stop.name || "Stop"));
    setText("diqSub","Stop selected • route update requested");
    toast("Stop selected ✓");
    removeStopCard();
    return true;
  }

  setText("diqSigRoute","Add stop unavailable");
  toast("Could not add stop");
  return false;
}

function clearStopMarkers(){
  var i;

  for(i = 0; i < stopMarkers.length; i++){
    try{ stopMarkers[i].remove(); }catch(e){}
  }

  stopMarkers = [];
}


function markerCssText(stop,index){
  /*
    DRIVE IQ V12 — TRUE STOP DOT SYSTEM

    The marker root is a 1px anchor placed at the exact POI coordinate.
    The tiny dot is the trusted real location. The emoji bubble floats above
    the dot with a connector stem so zooming out does not make the stop feel
    like it moved away from its business/location.
  */
  return [
    "width:1px",
    "height:1px",
    "position:relative",
    "overflow:visible",
    "pointer-events:auto",
    "transform:translateZ(0)",
    "user-select:none",
    "-webkit-user-select:none"
  ].join(";");
}

function stopBubbleCssText(stop,index){
  var best = index === 0;
  var sponsored = stop && stop.sponsor ? true : false;
  var c = sponsored ? "#ffce69" : color(stop && stop.type);
  var size = sponsored ? 56 : (best ? 54 : 48);
  var glow = sponsored ? 28 : (best ? 22 : 14);
  var y = sponsored || best ? -76 : -68;

  return [
    "position:absolute",
    "left:0",
    "top:" + y + "px",
    "width:" + size + "px",
    "height:" + size + "px",
    "margin-left:" + Math.round(size / -2) + "px",
    "border-radius:999px",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "background:radial-gradient(circle at 35% 26%,rgba(255,255,255,.92),rgba(255,255,255,.42) 34%," + c + " 76%)",
    "border:" + (sponsored || best ? 4 : 3) + "px solid #061018",
    "box-shadow:0 0 " + glow + "px " + (sponsored ? "rgba(255,206,105,.78)" : "rgba(255,206,105,.58)") + ",0 10px 22px rgba(0,0,0,.44),inset 0 0 0 2px rgba(255,255,255,.18)",
    "color:#061018",
    "font-size:" + (best ? 25 : 21) + "px",
    "line-height:1",
    "font-weight:1000",
    "cursor:pointer",
    "z-index:4",
    "transform-origin:50% 100%",
    "transition:transform .18s ease,opacity .18s ease,filter .16s ease",
    "will-change:transform,opacity"
  ].join(";");
}

function stopDotCssText(stop,index){
  var best = index === 0;
  var sponsored = stop && stop.sponsor ? true : false;
  var c = sponsored ? "#ffce69" : color(stop && stop.type);
  var size = best || sponsored ? 15 : 12;

  return [
    "position:absolute",
    "left:0",
    "top:0",
    "width:" + size + "px",
    "height:" + size + "px",
    "margin-left:" + Math.round(size / -2) + "px",
    "margin-top:" + Math.round(size / -2) + "px",
    "border-radius:999px",
    "background:" + c,
    "border:3px solid #061018",
    "box-shadow:0 0 0 2px rgba(255,255,255,.34),0 0 18px " + c + ",0 6px 16px rgba(0,0,0,.45)",
    "z-index:6",
    "pointer-events:none"
  ].join(";");
}

function stopStemCssText(stop,index){
  var best = index === 0;
  var sponsored = stop && stop.sponsor ? true : false;
  var c = sponsored ? "rgba(255,206,105,.72)" : "rgba(255,206,105,.55)";
  var h = sponsored || best ? 56 : 48;

  return [
    "position:absolute",
    "left:0",
    "top:-" + h + "px",
    "width:2px",
    "height:" + h + "px",
    "margin-left:-1px",
    "background:linear-gradient(180deg,rgba(255,255,255,.08)," + c + ",rgba(255,255,255,.10))",
    "box-shadow:0 0 8px " + c,
    "border-radius:999px",
    "z-index:2",
    "pointer-events:none",
    "opacity:.82",
    "transition:opacity .18s ease,transform .18s ease"
  ].join(";");
}

function pulseMarker(el){
  var target;
  if(!el){ return; }

  try{
    target = el.querySelector ? el.querySelector(".diqStopBubble") : null;
    if(!target){ target = el; }
    target.style.transition = "transform .16s ease, filter .16s ease";
    target.style.transform = String(target.__diqBaseTransform || "") + " scale(1.14)";
    target.style.filter = "brightness(1.13) saturate(1.12)";

    window.setTimeout(function(){
      try{
        target.style.transform = String(target.__diqBaseTransform || "");
        target.style.filter = "";
      }catch(e){}
    },170);
  }catch(e2){}
}

function makeStopMarkerElement(stop,index){
  var el = document.createElement("div");
  var bubble = document.createElement("div");
  var icon = document.createElement("div");
  var badge;
  var crown;
  var pulse;
  var stem;
  var dot;

  el.className = "diqDomStopMarker" + (index === 0 ? " best" : "");
  el.setAttribute("data-stop-id",String(stop && stop.id ? stop.id : ""));
  el.style.cssText = markerCssText(stop,index);

  stem = document.createElement("div");
  stem.className = "diqStopStem";
  stem.style.cssText = stopStemCssText(stop,index);
  el.appendChild(stem);

  dot = document.createElement("div");
  dot.className = "diqStopAnchorDot";
  dot.style.cssText = stopDotCssText(stop,index);
  el.appendChild(dot);

  bubble.className = "diqStopBubble";
  bubble.style.cssText = stopBubbleCssText(stop,index);

  icon.className = "diqDomStopIcon";
  icon.textContent = stop && stop.__diqCluster ? clusterMarkerText(stop) : letter(stop && stop.type,stop && stop.name);
  icon.style.cssText = [
    "position:relative",
    "z-index:3",
    "filter:drop-shadow(0 1px 1px rgba(0,0,0,.35))",
    "pointer-events:none"
  ].join(";");

  bubble.appendChild(icon);

  badge = document.createElement("div");
  badge.className = "diqStopBadge";
  badge.textContent = stop && stop.__diqCluster ? clusterBadgeText(stop) : (stop && stop.sponsor ? String(stop.sponsorBadge || "Featured") : brandShortName(stop && stop.name, stop && stop.type));
  badge.style.cssText = [
    "position:absolute",
    "left:50%",
    "bottom:-19px",
    "transform:translateX(-50%)",
    "max-width:78px",
    "padding:3px 7px",
    "border-radius:999px",
    "background:rgba(5,12,22,.92)",
    "border:1px solid rgba(255,206,105,.48)",
    "box-shadow:0 8px 18px rgba(0,0,0,.40)",
    "color:#fff",
    "font-size:9px",
    "font-weight:1000",
    "letter-spacing:.25px",
    "white-space:nowrap",
    "overflow:hidden",
    "text-overflow:ellipsis",
    "z-index:5",
    "pointer-events:none",
    "transition:opacity .18s ease"
  ].join(";");
  bubble.appendChild(badge);

  if(index === 0){
    pulse = document.createElement("div");
    pulse.className = "diqStopPulse";
    pulse.style.cssText = [
      "position:absolute",
      "inset:-8px",
      "border-radius:999px",
      "border:2px solid rgba(255,206,105,.62)",
      "box-shadow:0 0 22px rgba(255,206,105,.32)",
      "z-index:1",
      "pointer-events:none"
    ].join(";");
    bubble.insertBefore(pulse,icon);

    crown = document.createElement("div");
    crown.className = "diqStopCrown";
    crown.textContent = "🏆";
    crown.style.cssText = [
      "position:absolute",
      "left:50%",
      "top:-36px",
      "transform:translateX(-50%)",
      "font-size:17px",
      "line-height:1",
      "z-index:6",
      "filter:drop-shadow(0 2px 3px rgba(0,0,0,.85))",
      "pointer-events:none",
      "transition:opacity .18s ease"
    ].join(";");
    bubble.appendChild(crown);
  }

  bubble.onclick = function(ev){
    if(ev && ev.stopPropagation){ ev.stopPropagation(); }
    pulseMarker(el);
    if(stop && stop.__diqCluster){ showStopCluster(stop); }
    else{ showStop(stop); }
  };

  el.onclick = function(ev){
    if(ev && ev.stopPropagation){ ev.stopPropagation(); }
    pulseMarker(el);
    if(stop && stop.__diqCluster){ showStopCluster(stop); }
    else{ showStop(stop); }
  };

  el.appendChild(bubble);
  return el;
}

function applyStopMarkerZoomState(){
  var map = getMap();
  var zoom;
  var i;
  var el;
  var bubble;
  var badge;
  var crown;
  var stem;
  var dot;
  var scale;
  var opacity;
  var stemOpacity;
  var stemScale;

  if(!map || !stopMarkers || !stopMarkers.length){ return; }

  try{ zoom = Number(map.getZoom()); }catch(e){ zoom = 14; }
  if(!finite(zoom)){ zoom = 14; }

  /*
    DRIVE IQ V15 — APPLE/GOOGLE STYLE MARKER SCALING
    Up close: full floating bubble.
    Mid zoom: compact bubble.
    Wide zoom: tiny icon.
    Far zoom: trusted anchor dot only.
  */
  if(zoom >= 16){
    scale = 1;
    opacity = 1;
    stemOpacity = 0.82;
    stemScale = 1;
  }else if(zoom >= 14){
    scale = 0.78;
    opacity = 0.94;
    stemOpacity = 0.58;
    stemScale = 0.70;
  }else if(zoom >= 12){
    scale = 0.46;
    opacity = 0.72;
    stemOpacity = 0.26;
    stemScale = 0.42;
  }else{
    scale = 0.18;
    opacity = 0;
    stemOpacity = 0;
    stemScale = 0.18;
  }

  for(i = 0; i < stopMarkers.length; i++){
    try{
      el = stopMarkers[i] && stopMarkers[i].getElement ? stopMarkers[i].getElement() : null;
      if(!el){ continue; }

      bubble = el.querySelector(".diqStopBubble");
      badge = el.querySelector(".diqStopBadge");
      crown = el.querySelector(".diqStopCrown");
      stem = el.querySelector(".diqStopStem");
      dot = el.querySelector(".diqStopAnchorDot");

      if(bubble){
        bubble.__diqBaseTransform = "scale(" + scale + ")";
        bubble.style.transform = bubble.__diqBaseTransform;
        bubble.style.opacity = String(opacity);
        bubble.style.pointerEvents = zoom < 12 ? "none" : "auto";
      }

      if(badge){ badge.style.opacity = zoom < 14 ? "0" : "1"; }
      if(crown){ crown.style.opacity = zoom < 14 ? "0" : "1"; }

      if(stem){
        stem.style.opacity = String(stemOpacity);
        stem.style.transform = "scaleY(" + stemScale + ")";
        stem.style.transformOrigin = "50% 100%";
      }

      if(dot){
        dot.style.opacity = "1";
        dot.style.transform = zoom < 12 ? "scale(1.08)" : "scale(1)";
      }
    }catch(e2){}
  }
}

function bindStopMarkerZoomState(){
  var map = getMap();
  if(!map || map.__diqFindStopsZoomBound){ return; }

  try{
    map.__diqFindStopsZoomBound = true;
    map.on("zoom",applyStopMarkerZoomState);
    map.on("move",applyStopMarkerZoomState);
    map.on("pitch",applyStopMarkerZoomState);
  }catch(e){}
}


function foodBrandRank(name){
  var s = String(name || "").toLowerCase();

  /*
    V19 — Brand Hierarchy Engine.
    Quick-stop scans should anchor districts on the most useful,
    recognizable, predictable brands — not just whichever result arrived first.
  */
  if(s.indexOf("chick-fil-a") > -1 || s.indexOf("chick fil a") > -1 || s.indexOf("chickfila") > -1){ return 155; }
  if(s.indexOf("whataburger") > -1){ return 142; }
  if(s.indexOf("raising cane") > -1 || s.indexOf("canes") > -1){ return 138; }
  if(s.indexOf("waffle house") > -1){ return 136; }
  if(s.indexOf("mcdonald") > -1){ return 132; }
  if(s.indexOf("wendy") > -1){ return 124; }
  if(s.indexOf("captain d") > -1 || s.indexOf("captain ds") > -1){ return 120; }
  if(s.indexOf("taco bell") > -1){ return 116; }
  if(s.indexOf("sonic") > -1){ return 112; }
  if(s.indexOf("burger king") > -1){ return 108; }
  if(s.indexOf("arby") > -1){ return 104; }
  if(s.indexOf("zaxby") > -1){ return 102; }
  if(s.indexOf("popeyes") > -1){ return 100; }
  if(s.indexOf("kfc") > -1){ return 96; }
  if(s.indexOf("starbucks") > -1){ return 94; }
  if(s.indexOf("dunkin") > -1){ return 92; }
  if(s.indexOf("subway") > -1){ return 88; }
  if(s.indexOf("pizza hut") > -1 || s.indexOf("domino") > -1 || s.indexOf("papa john") > -1){ return 84; }
  if(s.indexOf("pizza") > -1){ return 76; }
  if(s.indexOf("burger") > -1 || s.indexOf("chicken") > -1 || s.indexOf("taco") > -1){ return 70; }
  if(s.indexOf("grill") > -1 || s.indexOf("diner") > -1 || s.indexOf("cafe") > -1 || s.indexOf("restaurant") > -1){ return 58; }

  return 22;
}

function uniqueClusterBrandNames(stops,max){
  var names = [];
  var seen = {};
  var i;
  var raw;
  var nm;

  max = finite(Number(max)) ? Number(max) : 4;
  if(!stops || !stops.length){ return names; }

  stops = stops.slice(0);
  stops.sort(sortClusterStops);

  for(i = 0; i < stops.length && names.length < max; i++){
    raw = String(stops[i] && stops[i].name ? stops[i].name : "");
    nm = majorFoodBrandName(raw) || raw;
    nm = nm.replace(/^\s+|\s+$/g,"");
    if(!nm){ continue; }
    if(seen[nm.toLowerCase()]){ continue; }
    seen[nm.toLowerCase()] = true;
    names.push(nm);
  }

  return names;
}

function clusterRouteLabel(stops){
  var list;
  var first;
  var label;

  if(!stops || !stops.length){ return "best access first"; }

  list = stops.slice(0);
  list.sort(sortClusterStops);
  first = list[0];
  label = stopRouteReturnLabel(first);

  if(label === "EASY ACCESS"){ return "easy access"; }
  if(label === "QUICK RETURN"){ return "quick return"; }
  if(label === "MINIMAL DETOUR"){ return "minimal detour"; }
  if(label === "SHORT DETOUR"){ return "short detour"; }
  if(label === "OFF CORRIDOR"){ return "off corridor"; }
  if(label === "QUICK STOP"){ return "quick stop"; }

  return "best access first";
}
function preferredStopBoost(stop){
  var prefs;
  var name;
  var brand;
  var key;

  if(!stop){ return 0; }

  prefs = safeParse(lsGet(LS.PREFERRED_STOPS)) || {};
  name = String(stop.name || "").toLowerCase();
  brand = majorFoodBrandName(stop.name);
  key = String(brand || name).toLowerCase();

  if(prefs[key] && finite(Number(prefs[key]))){ return Math.min(24,Number(prefs[key]) * 4); }
  return 0;
}

function rememberPreferredStop(stop){
  var prefs;
  var name;
  var brand;
  var key;

  if(!stop){ return; }

  prefs = safeParse(lsGet(LS.PREFERRED_STOPS)) || {};
  name = String(stop.name || "").toLowerCase();
  brand = majorFoodBrandName(stop.name);
  key = String(brand || name).toLowerCase();

  if(!key){ return; }

  prefs[key] = Number(prefs[key] || 0) + 1;
  lsJSON(LS.PREFERRED_STOPS,prefs);
}

function routeImpact(stop){
  var routeDist = stop && finite(Number(stop.routeDistMiles)) ? Number(stop.routeDistMiles) : nearestRouteDistanceToStop(stop);
  var dist = stop && finite(Number(stop.distMiles)) ? Number(stop.distMiles) : null;
  var miles = 0;
  var mins = 0;
  var label = "Route Watch";

  if(routeDist !== null && finite(Number(routeDist))){
    if(routeDist <= 0.25){
      label = "Instant Return";
      miles = 0.1;
      mins = 1;
    }else if(routeDist <= 0.75){
      label = "Easy Return";
      miles = 0.3;
      mins = 2;
    }else if(routeDist <= 1.5){
      label = "Quick Return";
      miles = 0.8;
      mins = 4;
    }else if(routeDist <= 2.5){
      label = "Minor Detour";
      miles = 1.5;
      mins = 6;
    }else{
      label = "Off Corridor";
      miles = Math.max(2.0,Math.round(Number(routeDist) * 10) / 10);
      mins = Math.max(8,Math.round(Number(routeDist) * 4));
    }
  }else if(dist !== null && finite(Number(dist))){
    if(dist <= 1.2){
      label = "Quick Stop";
      miles = 0.2;
      mins = 2;
    }else{
      label = "Nearby Option";
      miles = Math.round(Number(dist) * 10) / 10;
      mins = Math.max(3,Math.round(Number(dist) * 3));
    }
  }

  return {
    label:label,
    addedMinutes:mins,
    addedMiles:miles,
    copy:mins <= 1 ? "No meaningful delay" : "+" + String(mins) + " min",
    detail:mins <= 1 ? "No meaningful delay • " + label : "+" + String(mins) + " min • +" + String(Math.round(miles * 10) / 10) + " mi • " + label
  };
}

function commercialDistrictType(stops,type){
  var counts = {};
  var i;
  var t;
  var name;
  var coffee = 0;
  var hotelAirport = 0;
  var bigGrocery = 0;
  var outlet = 0;

  if(!stops || !stops.length){ return "Commercial District"; }

  for(i = 0; i < stops.length; i++){
    t = stopType(stops[i] && stops[i].type);
    name = String(stops[i] && stops[i].name ? stops[i].name : "").toLowerCase();
    counts[t] = Number(counts[t] || 0) + 1;

    if(t === "food" || t === "coffee"){
      if(name.indexOf("starbucks") > -1 || name.indexOf("dunkin") > -1 || name.indexOf("coffee") > -1 || name.indexOf("cafe") > -1){ coffee += 1; }
    }
    if(t === "hotel" && (name.indexOf("airport") > -1 || name.indexOf("inn") > -1 || name.indexOf("hotel") > -1)){ hotelAirport += 1; }
    if(t === "grocery" && (name.indexOf("walmart") > -1 || name.indexOf("kroger") > -1 || name.indexOf("aldi") > -1 || name.indexOf("target") > -1 || name.indexOf("costco") > -1 || name.indexOf("sam") > -1)){ bigGrocery += 1; }
    if(t === "shopping" && (name.indexOf("tanger") > -1 || name.indexOf("outlet") > -1 || name.indexOf("mall") > -1)){ outlet += 1; }
  }

  t = stopType(type || (stops[0] && stops[0].type));

  if(t === "food" && coffee >= 3){ return "Coffee Corridor"; }
  if(t === "food" && Number(counts.food || 0) >= 5){ return "Restaurant Row"; }
  if(t === "food" && Number(counts.food || 0) >= 3){ return "Quick Food District"; }
  if(t === "coffee"){ return Number(counts.coffee || 0) >= 3 ? "Coffee District" : "Coffee Stop Cluster"; }
  if(t === "grocery"){ return bigGrocery >= 2 || Number(counts.grocery || 0) >= 3 ? "Grocery District" : "Grocery Stop"; }
  if(t === "fuel"){ return Number(counts.fuel || 0) >= 3 ? "Fuel Corridor" : "Fuel Stop Cluster"; }
  if(t === "shopping"){
    if(outlet >= 2){ return "Outlet District"; }
    return Number(counts.shopping || 0) >= 3 ? "Shopping District" : "Shopping Stop Cluster";
  }
  if(t === "hotel"){ return hotelAirport >= 2 || Number(counts.hotel || 0) >= 3 ? "Hotel District" : "Hotel Cluster"; }
  if(t === "wellness"){ return Number(counts.wellness || 0) >= 3 ? "Wellness District" : "Wellness Stop Cluster"; }
  if(t === "scenic"){ return Number(counts.scenic || 0) >= 3 ? "Scenic District" : "Scenic Stop Cluster"; }
  if(t === "parking"){ return "Parking Cluster"; }

  return prettyType(t) + " District";
}

function districtAnchorStop(stops,type){
  var list;

  if(!stops || !stops.length){ return null; }

  list = stops.slice(0);
  list.sort(sortClusterStops);
  return list[0];
}

function districtImpactLine(stops){
  var first;
  var impact;

  first = districtAnchorStop(stops,currentScanType);
  impact = routeImpact(first);

  return impact.detail;
}

function clusterScoreLabel(stops,type){
  var first;
  var impact;

  first = districtAnchorStop(stops,type);
  impact = routeImpact(first);

  if(!first){ return "BEST ACCESS"; }
  if(impact.label === "Instant Return"){ return "⚡ INSTANT RETURN"; }
  if(impact.label === "Easy Return"){ return "🛣 EASY RETURN"; }
  if(impact.label === "Quick Return"){ return "↩ QUICK RETURN"; }
  if(impact.label === "Minor Detour"){ return "🚗 MINOR DETOUR"; }
  if(impact.label === "Off Corridor"){ return "OFF CORRIDOR"; }
  return "BEST ACCESS";
}


function clusterCorridorLabel(stops,type){
  var t = stopType(type || (stops && stops.length ? stops[0].type : currentScanType));
  var list;
  var first;
  var nearDest = false;
  var nearFix = false;
  var fix;
  var d;
  var base;

  if(!stops || !stops.length){ return "Nearby " + commercialDistrictType([],t); }

  list = stops.slice(0);
  list.sort(sortClusterStops);
  first = list[0];
  base = commercialDistrictType(list,t);

  if(stopNearDestination(first,0.35)){ nearDest = true; }

  fix = getCurrentFix();
  if(fix && first && finite(Number(first.lat)) && finite(Number(first.lng))){
    d = milesBetween([Number(fix.lng),Number(fix.lat)],[Number(first.lng),Number(first.lat)]);
    if(d <= 1.75){ nearFix = true; }
  }

  if(nearDest){ return "Destination " + base; }
  if(nearFix){ return "Nearby " + base; }

  if(first && String(first.lane || "") === "ahead"){ return "Route " + base; }
  if(first && String(first.lane || "") === "far"){ return "Ahead " + base; }

  return "Route " + base;
}

function clusterTopChoiceLine(stop,index){
  var label;
  var brand;
  var routeLabel;
  var distance;

  if(!stop){ return ""; }

  brand = majorFoodBrandName(stop && stop.name) || String(stop && stop.name ? stop.name : "Stop");
  routeLabel = stopRouteReturnLabel(stop);
  distance = finite(Number(stop && stop.distMiles)) ? fmtMiles(stop.distMiles) : "nearby";

  if(routeLabel === "EASY ACCESS"){ label = "Easy access"; }
  else if(routeLabel === "QUICK RETURN"){ label = "Quick return"; }
  else if(routeLabel === "MINIMAL DETOUR"){ label = "Minimal detour"; }
  else if(routeLabel === "SHORT DETOUR"){ label = "Short detour"; }
  else if(routeLabel === "OFF CORRIDOR"){ label = "Off corridor"; }
  else{ label = "Best access"; }

  return brand + " • " + distance + " • " + label;
}

function clusterPreviewTitle(stops,type){
  var t = stopType(type || (stops && stops.length ? stops[0].type : currentScanType));
  var names;
  var count;
  var corridor;
  var anchor;
  var icon;

  if(!stops || !stops.length){ return "Nearby stops"; }
  count = stops.length;

  stops = stops.slice(0);
  stops.sort(sortClusterStops);
  names = uniqueClusterBrandNamesByType(stops,t,4);
  corridor = commercialDistrictType(stops,t);
  anchor = names.length ? names[0] : "";
  icon = clusterTypeIcon(t,stops[0]);

  if(anchor && count >= 2){
    return icon + " " + anchor + " +" + String(count - 1);
  }

  if(count >= 4){
    return icon + " " + corridor + " (" + String(count) + ")";
  }

  if(count >= 2){
    return icon + " " + corridor;
  }

  return String(stops[0] && stops[0].name ? stops[0].name : prettyType(t));
}

function clusterPreviewSubtitle(stops,type){
  var t = stopType(type || (stops && stops.length ? stops[0].type : currentScanType));
  var names;
  var impact;
  var score;
  var choiceLabel;

  if(!stops || !stops.length){ return "Best access first"; }

  stops = stops.slice(0);
  stops.sort(sortClusterStops);
  impact = districtImpactLine(stops);
  score = districtScore(stops,t);
  names = uniqueClusterBrandNamesByType(stops,t,5);

  if(t === "grocery"){
    choiceLabel = stops.length >= 4 ? "best variety" : "quick grocery";
  }else if(t === "fuel"){
    choiceLabel = "easy fuel return";
  }else if(t === "hotel"){
    choiceLabel = stops.length >= 3 ? "hotel choices" : "quick stay option";
  }else if(t === "shopping"){
    choiceLabel = stops.length >= 4 ? "shopping hub" : "shopping option";
  }else if(t === "coffee"){
    choiceLabel = "coffee run";
  }else if(t === "wellness"){
    choiceLabel = "wellness stop";
  }else if(t === "scenic"){
    choiceLabel = "worth-the-stop";
  }else{
    choiceLabel = "quick stop";
  }

  if(names.length >= 3){ return names.slice(0,3).join(" • ") + " • Score " + String(score) + " • " + impact; }
  if(names.length === 2){ return names[0] + " • " + names[1] + " • Score " + String(score) + " • " + impact; }
  if(names.length === 1){ return names[0] + " • " + choiceLabel + " • Score " + String(score) + " • " + impact; }

  return String(stops.length) + " options • " + choiceLabel + " • Score " + String(score) + " • " + impact;
}

function clusterPreviewBadge(stops,type){
  var t = stopType(type || (stops && stops.length ? stops[0].type : currentScanType));
  var names;
  var score;
  var label;

  if(!stops || !stops.length){ return "OPTIONS"; }

  score = districtScore(stops,t);
  names = uniqueClusterBrandNamesByType(stops,t,2);

  if(score >= 92){ return "SCORE " + String(score); }
  if(score >= 86){ return "BEST FIT"; }

  if(names.length >= 1){
    label = names[0].toUpperCase().slice(0,14);
    if(stops.length > 1){ label += " +" + String(stops.length - 1); }
    return label;
  }

  return commercialDistrictType(stops,t).toUpperCase().slice(0,18);
}

function clusterPreviewCta(stops,type){
  var t = stopType(type || (stops && stops.length ? stops[0].type : currentScanType));
  var score = districtScore(stops,t);
  var phrase = districtScorePhrase(stops,t).toUpperCase();

  if(t === "food"){ return phrase + " • BEST FOOD CHOICES • SCORE " + String(score); }
  if(t === "grocery"){ return phrase + " • BEST GROCERY OPTIONS • SCORE " + String(score); }
  if(t === "fuel"){ return phrase + " • EASY FUEL RETURN • SCORE " + String(score); }
  if(t === "coffee"){ return phrase + " • QUICK COFFEE OPTIONS • SCORE " + String(score); }
  if(t === "shopping"){ return phrase + " • SHOPPING HUB • SCORE " + String(score); }
  if(t === "hotel"){ return phrase + " • HOTEL CHOICES • SCORE " + String(score); }
  if(t === "wellness"){ return phrase + " • WELLNESS OPTIONS • SCORE " + String(score); }
  if(t === "scenic"){ return phrase + " • WORTH-THE-STOP • SCORE " + String(score); }

  return phrase + " • CHOOSE AN OPTION • SCORE " + String(score);
}


function stopClusterRank(stop){
  var rank = 0;
  var t = stopType(stop && stop.type);
  var routeDist = stop && finite(Number(stop.routeDistMiles)) ? Number(stop.routeDistMiles) : nearestRouteDistanceToStop(stop);
  var dist = stop && finite(Number(stop.distMiles)) ? Number(stop.distMiles) : 9999;

  rank += categoryBrandRank(stop);
  if(t === "food"){ rank += foodBrandRank(stop && stop.name) / 3; }
  rank += preferredStopBoost(stop);
  if(stop && stop.sponsor){ rank += 65; }
  if(routeDist !== null && finite(Number(routeDist))){
    if(routeDist <= 0.25){ rank += 45; }
    else if(routeDist <= 0.6){ rank += 34; }
    else if(routeDist <= 1.1){ rank += 22; }
    else if(routeDist <= 2.0){ rank += 10; }
    else{ rank -= 14; }
  }
  if(dist <= 1.5){ rank += 22; }
  else if(dist <= 4){ rank += 15; }
  else if(dist <= 8){ rank += 8; }
  rank += confidenceForStop(stop,1) / 8;

  return rank;
}

function sortClusterStops(a,b){
  var ar = stopClusterRank(a);
  var br = stopClusterRank(b);
  var ad;
  var bd;

  if(ar !== br){ return br - ar; }
  ad = finite(Number(a && a.distMiles)) ? Number(a.distMiles) : 9999;
  bd = finite(Number(b && b.distMiles)) ? Number(b.distMiles) : 9999;
  return ad - bd;
}

function clusterNameForStops(stops,type){
  return clusterPreviewTitle(stops,type);
}

function clusterDistanceLimitMiles(type){
  type = stopType(type || currentScanType);

  if(type === "food"){ return 0.42; }
  if(type === "coffee"){ return 0.24; }
  if(type === "grocery"){ return 0.42; }
  if(type === "fuel"){ return 0.32; }
  if(type === "hotel"){ return 0.45; }
  if(type === "shopping"){ return 0.40; }
  if(type === "wellness"){ return 0.26; }
  if(type === "scenic"){ return 0.30; }

  return 0.22;
}

function clusterTypeIcon(type,stop){
  type = stopType(type || (stop && stop.type));

  if(type === "food"){ return iconForFoodName(stop && stop.name); }
  if(type === "coffee"){ return "☕"; }
  if(type === "grocery"){ return "🛒"; }
  if(type === "fuel"){ return "⛽"; }
  if(type === "hotel"){ return "🛏️"; }
  if(type === "shopping"){ return "🛍️"; }
  if(type === "wellness"){ return "🥤"; }
  if(type === "scenic"){ return "🧭"; }
  if(type === "parking"){ return "P"; }

  return letter(type,stop && stop.name);
}

function clusterMarkerText(cluster){
  var first = cluster && cluster.clusterStops && cluster.clusterStops.length ? cluster.clusterStops[0] : cluster;
  var count = cluster && cluster.clusterStops ? cluster.clusterStops.length : 1;
  var type = stopType(cluster && cluster.type);
  var icon = clusterTypeIcon(cluster && cluster.type,first);

  if(count > 1){
    if(type === "food"){
      if(count >= 3){ return "🍔" + String(count); }
      return "🍴" + String(count);
    }
    if(type === "grocery"){ return "🛒" + String(count); }
    if(type === "fuel"){ return "⛽" + String(count); }
    if(type === "hotel"){ return "🛏️" + String(count); }
    if(type === "coffee"){ return "☕" + String(count); }
    if(type === "shopping"){ return "🛍️" + String(count); }
    if(type === "wellness"){ return "🥤" + String(count); }
    return icon + "+" + String(count - 1);
  }

  return icon;
}

function clusterBadgeText(cluster){
  if(cluster && cluster.clusterStops && cluster.clusterStops.length){
    return clusterPreviewBadge(cluster.clusterStops,cluster.type);
  }
  return brandShortName(cluster && cluster.name, cluster && cluster.type);
}


function makeStopCluster(stops,index){
  var first;
  var cluster;
  var sorted;

  if(!stops || !stops.length){ return null; }

  sorted = stops.slice(0);
  sorted.sort(sortClusterStops);
  first = sorted[0];

  if(!first){ return null; }

  cluster = {
    id:"cluster_" + String(index) + "_" + String(first && first.id ? first.id : "stop"),
    name:clusterNameForStops(sorted,first && first.type),
    type:stopType(first && first.type),
    lat:Number(first.lat),
    lng:Number(first.lng),
    distMiles:first && finite(Number(first.distMiles)) ? Number(first.distMiles) : null,
    lane:first ? String(first.lane || "ahead") : "ahead",
    routeDistMiles:first ? first.routeDistMiles : null,
    confidence:first ? first.confidence : null,
    confidenceLabel:first ? first.confidenceLabel : null,
    tag:"CLUSTER",
    __diqCluster:true,
    clusterAnchorStop:first,
    clusterStops:sorted,
    clusterCorridor:clusterCorridorLabel(sorted,first && first.type),
    clusterSubtitle:clusterPreviewSubtitle(sorted,first && first.type)
  };

  return cluster;
}

function buildStopDisplayItems(stops){
  var source = stops || [];
  var used = {};
  var out = [];
  var i;
  var j;
  var group;
  var base;
  var other;
  var limit;
  var d;

  for(i = 0; i < source.length; i++){
    base = source[i];
    if(!base || used[String(base.id)]){ continue; }

    group = [base];
    used[String(base.id)] = true;
    limit = clusterDistanceLimitMiles(base.type);

    for(j = i + 1; j < source.length; j++){
      other = source[j];
      if(!other || used[String(other.id)]){ continue; }
      if(stopType(other.type) !== stopType(base.type)){ continue; }
      if(base.sponsor || other.sponsor){ continue; }
      if(!finite(Number(base.lat)) || !finite(Number(base.lng)) || !finite(Number(other.lat)) || !finite(Number(other.lng))){ continue; }

      d = milesBetween([Number(base.lng),Number(base.lat)],[Number(other.lng),Number(other.lat)]);
      if(d <= limit){
        group.push(other);
        used[String(other.id)] = true;
      }
    }

    if(group.length >= 3 || ((stopType(base.type) === "food" || stopType(base.type) === "grocery" || stopType(base.type) === "fuel" || stopType(base.type) === "coffee" || stopType(base.type) === "hotel" || stopType(base.type) === "shopping" || stopType(base.type) === "wellness") && group.length >= 2)){
      out.push(makeStopCluster(group,out.length) || base);
    }else{
      for(j = 0; j < group.length; j++){
        out.push(group[j]);
      }
    }
  }

  return out;
}

function showStopCluster(cluster){
  var map = getMap();
  var list = cluster && cluster.clusterStops ? cluster.clusterStops : [];
  var i;
  var card;
  var title;
  var sub;
  var kicker;
  var row;
  var rowText;
  var add;
  var closeBtn;
  var bounds;
  var routeLine;
  var shown;
  var brandName;
  var label;
  var topLabel;
  var mini;
  var section;
  var routeCopy;

  removeStopCard();

  if(!cluster || !list.length){ return; }

  list = list.slice(0);
  list.sort(sortClusterStops);
  shown = Math.min(list.length,8);

  setText("diqRouteIntelText",clusterPreviewTitle(list,cluster.type));
  setText("diqRouteIntelSub",clusterPreviewSubtitle(list,cluster.type));
  setText("diqSigRoute",clusterBadgeText(cluster));
  toast(clusterPreviewTitle(list,cluster.type) + " • " + String(list.length) + " options");

  card = document.createElement("div");
  card.id = "diqStopMiniCard";
  card.style.cssText = stopCardCss();

  kicker = document.createElement("div");
  kicker.textContent = clusterCorridorLabel(list,cluster.type).toUpperCase();
  kicker.style.cssText = [
    "display:inline-flex",
    "align-items:center",
    "max-width:100%",
    "padding:4px 9px",
    "border-radius:999px",
    "background:rgba(255,206,105,.13)",
    "border:1px solid rgba(255,206,105,.32)",
    "color:#ffce69",
    "font-size:9px",
    "font-weight:1000",
    "letter-spacing:.7px",
    "margin-bottom:8px",
    "white-space:nowrap",
    "overflow:hidden",
    "text-overflow:ellipsis"
  ].join(";");
  card.appendChild(kicker);

  title = document.createElement("div");
  title.textContent = clusterPreviewTitle(list,cluster.type);
  title.style.cssText = "font-size:18px;font-weight:1000;line-height:1.1;margin-bottom:5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;";

  sub = document.createElement("div");
  sub.textContent = clusterPreviewSubtitle(list,cluster.type);
  sub.style.cssText = "font-size:12px;font-weight:800;color:rgba(255,255,255,.76);line-height:1.25;margin-bottom:8px;white-space:normal;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;";

  section = document.createElement("div");
  section.textContent = clusterPreviewCta(list,cluster.type);
  section.style.cssText = "font-size:10px;font-weight:1000;letter-spacing:.55px;color:rgba(182,255,235,.88);text-transform:uppercase;margin:8px 0 4px;";

  card.appendChild(title);
  card.appendChild(sub);
  card.appendChild(section);

  for(i = 0; i < shown; i++){
    row = document.createElement("div");
    row.style.cssText = [
      "display:grid",
      "grid-template-columns:1fr 82px",
      "gap:8px",
      "align-items:center",
      "margin-top:7px"
    ].join(";");

    rowText = document.createElement("button");
    rowText.type = "button";
    rowText.setAttribute("data-stop-index",String(i));

    brandName = majorFoodBrandName(list[i] && list[i].name);
    label = brandName || String(list[i].name || "Stop");
    routeCopy = stopRouteReturnLabel(list[i]);
    if(routeCopy === "EASY ACCESS"){ routeLine = "Easy return"; }
    else if(routeCopy === "QUICK RETURN"){ routeLine = "Quick return"; }
    else if(routeCopy === "MINIMAL DETOUR"){ routeLine = "Minimal detour"; }
    else if(routeCopy === "SHORT DETOUR"){ routeLine = "Short detour"; }
    else if(routeCopy === "OFF CORRIDOR"){ routeLine = "Off corridor"; }
    else{ routeLine = "Best access"; }

    topLabel =
      letter(list[i].type,list[i].name) + "  " +
      label + " • " +
      fmtMiles(list[i].distMiles);

    mini = routeLine + " • " + routeImpact(list[i]).copy;

    rowText.innerHTML =
      '<span style="display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' +
      escapeHtml(topLabel) +
      '</span><span style="display:block;margin-top:2px;font-size:10px;font-weight:850;color:rgba(255,255,255,.58);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">↩ ' +
      escapeHtml(mini) +
      '</span>';

    rowText.style.cssText = [
      "width:100%",
      "min-height:48px",
      "border-radius:15px",
      "border:1px solid rgba(255,255,255,.13)",
      "background:rgba(255,255,255,.07)",
      "color:#fff",
      "font-size:12px",
      "font-weight:900",
      "text-align:left",
      "padding:6px 12px",
      "cursor:pointer",
      "overflow:hidden"
    ].join(";");
    rowText.onclick = function(ev){
      var idx;
      if(ev && ev.stopPropagation){ ev.stopPropagation(); }
      idx = Number(this.getAttribute("data-stop-index") || 0);
      if(list[idx]){ showStop(list[idx]); }
    };

    add = document.createElement("button");
    add.type = "button";
    add.setAttribute("data-stop-index",String(i));
    add.textContent = "ADD";
    add.style.cssText = cardButtonCss("primary") + ";min-height:48px;border-radius:15px;font-size:11px;letter-spacing:.4px;";
    add.onclick = function(ev){
      var idx;
      if(ev && ev.stopPropagation){ ev.stopPropagation(); }
      idx = Number(this.getAttribute("data-stop-index") || 0);
      if(list[idx]){ addStopToRoute(list[idx]); }
    };

    row.appendChild(rowText);
    row.appendChild(add);
    card.appendChild(row);
  }

  if(list.length > shown){
    sub = document.createElement("div");
    sub.textContent = "+" + String(list.length - shown) + " more nearby";
    sub.style.cssText = "font-size:11px;font-weight:800;color:rgba(255,255,255,.56);margin-top:8px;text-align:center;";
    card.appendChild(sub);
  }

  closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.textContent = "CLOSE";
  closeBtn.style.cssText = cardButtonCss("secondary") + ";width:100%;margin-top:10px;";
  closeBtn.onclick = function(ev){
    if(ev && ev.stopPropagation){ ev.stopPropagation(); }
    removeStopCard();
  };
  card.appendChild(closeBtn);

  document.body.appendChild(card);
  stopCardEl = card;

  try{
    if(map && window.mapboxgl && list.length){
      bounds = new mapboxgl.LngLatBounds([Number(list[0].lng),Number(list[0].lat)],[Number(list[0].lng),Number(list[0].lat)]);
      for(i = 1; i < list.length; i++){
        bounds.extend([Number(list[i].lng),Number(list[i].lat)]);
      }
      /* V200 PASSIVE: cluster selection does not own camera fit. */
    }
  }catch(e){}
}

function drawStopDomMarkers(stops){
  var map = getMap();
  var i;
  var marker;
  var el;
  var lng;
  var lat;

  clearStopMarkers();

  if(!map || !window.mapboxgl || !stops || !stops.length){ return; }

  bindStopMarkerZoomState();

  for(i = 0; i < stops.length; i++){
    lng = Number(stops[i].lng);
    lat = Number(stops[i].lat);

    if(!finite(lng) || !finite(lat)){ continue; }

    el = makeStopMarkerElement(stops[i],i);

    try{
      marker = new mapboxgl.Marker({
        element:el,
        anchor:"center",
        offset:[0,0],
        rotationAlignment:"viewport",
        pitchAlignment:"viewport"
      })
      .setLngLat([lng,lat])
      .addTo(map);

      /*
        V12 True Stop Dot System:
        The marker root is anchored at the POI coordinate. The dot marks
        the exact stop. The bubble may float above the dot, but the dot never moves.
      */
      try{
        marker.__diqAnchorLng = lng;
        marker.__diqAnchorLat = lat;
      }catch(e2){}

      stopMarkers.push(marker);
    }catch(e){}
  }

  applyStopMarkerZoomState();
}
function clearStopPins(){
  var map = getMap();

  removeStopCard();
  clearStopMarkers();

  removeLayer(map,"diq-stop-best-label");
  removeLayer(map,"diq-stop-label");
  removeLayer(map,"diq-stop-ring");
  removeLayer(map,"diq-stop-core");
  removeLayer(map,"diq-stop-halo-strong");
  removeLayer(map,"diq-stop-halo");
  removeLayer(map,"diq-stop-hit");
  removeSource(map,"diq-stops");
}

function stopFeature(stop,index){
  var conf = confidenceForStop(stop,index);
  var best = index === 0 ? 1 : 0;

  return {
    type:"Feature",
    properties:{
      id:String(stop.id),
      name:String(stop.name),
      type:String(stop.type),
      label:stop && stop.__diqCluster ? clusterMarkerText(stop) : letter(stop.type,stop.name),
      color:color(stop.type),
      miles:finite(Number(stop.distMiles)) ? Math.round(Number(stop.distMiles) * 10) / 10 : null,
      lane:String(stop.lane || "ahead"),
      tag:String(stop.tag || ""),
      sponsor:stop && stop.sponsor ? 1 : 0,
      badge:String(stop && stop.badge ? stop.badge : ""),
      sponsorBadge:String(stop && stop.sponsorBadge ? stop.sponsorBadge : ""),
      offer:String(stop && stop.offer ? stop.offer : ""),
      confidence:conf,
      confidenceLabel:confidenceLabel(conf),
      glowScale:glowScaleForStop(stop,index),
      isBest:best,
      bestLabel:best ? "🏆" : ""
    },
    geometry:{
      type:"Point",
      coordinates:[Number(stop.lng),Number(stop.lat)]
    }
  };
}


function visibleStopLimit(type){
  type = stopType(type || currentScanType);
  if(type === "food"){ return 8; }
  if(type === "wellness"){ return 8; }
  if(type === "shopping"){ return 6; }
  if(type === "scenic"){ return 8; }
  return 8;
}

function maybeSpeakScanSummary(stops,type){
  var line;

  type = stopType(type || currentScanType);

  if(!stops || !stops.length){ return; }

  line = String(Math.min(stops.length,visibleStopLimit(type))) + " " + prettyType(type).toLowerCase() + " options found. Best stop ahead.";

  try{
    if(window.DRIVE_IQ_DRIVE_MODE && typeof window.DRIVE_IQ_DRIVE_MODE.speak === "function"){
      window.DRIVE_IQ_DRIVE_MODE.speak(line);
      return;
    }
  }catch(e1){}

  try{
    if(window.speechSynthesis && window.SpeechSynthesisUtterance){
      var u = new SpeechSynthesisUtterance(line);
      u.lang = "en-US";
      u.rate = 0.94;
      u.pitch = 1;
      u.volume = 1;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    }
  }catch(e2){}
}

function drawStopPins(stops){
  var map = getMap();
  var features = [];
  var i;

  activeStops = (stops || []).slice(0,visibleStopLimit(currentScanType));

  if(!map){ return; }

  clearStopPins();
  activeDisplayStops = [];

  if(!activeStops.length){ return; }

  activeDisplayStops = buildStopDisplayItems(activeStops);
  drawStopDomMarkers(activeDisplayStops);

  for(i = 0; i < activeDisplayStops.length; i++){
    features.push(stopFeature(activeDisplayStops[i],i));
  }

  try{
    map.addSource("diq-stops",{
      type:"geojson",
      data:{ type:"FeatureCollection", features:features }
    });

    map.addLayer({
      id:"diq-stop-halo",
      type:"circle",
      source:"diq-stops",
      paint:{
        "circle-radius":["*",["get","glowScale"],["interpolate",["linear"],["zoom"],8,24,14,39,18,56]],
        "circle-color":["get","color"],
        "circle-opacity":["case",["==",["get","isBest"],1],0.10,0.04],
        "circle-blur":1
      }
    });

    map.addLayer({
      id:"diq-stop-halo-strong",
      type:"circle",
      source:"diq-stops",
      filter:["==",["get","isBest"],1],
      paint:{
        "circle-radius":["interpolate",["linear"],["zoom"],8,38,14,58,18,78],
        "circle-color":["get","color"],
        "circle-opacity":0.05,
        "circle-blur":1
      }
    });

    map.addLayer({
      id:"diq-stop-core",
      type:"circle",
      source:"diq-stops",
      paint:{
        "circle-radius":["case",["==",["get","isBest"],1],["interpolate",["linear"],["zoom"],8,15,14,22,18,28],["interpolate",["linear"],["zoom"],8,12,14,17,18,22]],
        "circle-color":["get","color"],
        "circle-stroke-color":"#061018",
        "circle-stroke-width":["case",["==",["get","isBest"],1],5,4],
        "circle-opacity":0.18
      }
    });

    map.addLayer({
      id:"diq-stop-ring",
      type:"circle",
      source:"diq-stops",
      paint:{
        "circle-radius":["case",["==",["get","isBest"],1],["interpolate",["linear"],["zoom"],8,19,14,28,18,36],["interpolate",["linear"],["zoom"],8,15,14,21,18,27]],
        "circle-color":"rgba(0,0,0,0)",
        "circle-stroke-color":["get","color"],
        "circle-stroke-width":["case",["==",["get","isBest"],1],3.4,2.2],
        "circle-opacity":["case",["==",["get","isBest"],1],0.22,0.12]
      }
    });

    map.addLayer({
      id:"diq-stop-label",
      type:"symbol",
      source:"diq-stops",
      layout:{
        "text-field":["get","label"],
        "text-size":["case",["==",["get","isBest"],1],1,1],
        "text-font":["Open Sans Bold","Arial Unicode MS Bold"],
        "text-allow-overlap":true
      },
      paint:{
        "text-color":"#ffffff",
        "text-halo-color":"#061018",
        "text-halo-width":2.2
      }
    });

    map.addLayer({
      id:"diq-stop-best-label",
      type:"symbol",
      source:"diq-stops",
      filter:["==",["get","isBest"],1],
      layout:{
        "text-field":["get","bestLabel"],
        "text-size":1,
        "text-offset":[0,-2.35],
        "text-font":["Open Sans Bold","Arial Unicode MS Bold"],
        "text-allow-overlap":true
      },
      paint:{
        "text-color":"#ffffff",
        "text-halo-color":"#061018",
        "text-halo-width":1.6
      }
    });

    map.addLayer({
      id:"diq-stop-hit",
      type:"circle",
      source:"diq-stops",
      paint:{
        "circle-radius":["interpolate",["linear"],["zoom"],8,38,14,52,18,66],
        "circle-color":"rgba(0,0,0,0)",
        "circle-opacity":0.01
      }
    });

    bindStopClicks();
  }catch(e){
    toast("Stop pin draw failed");
  }
}

function bindStopClicks(){
  var map = getMap();
  if(!map){ return; }

  try{ map.off("click","diq-stop-hit",onStopClick); }catch(e){}

  try{
    map.on("click","diq-stop-hit",onStopClick);
    map.on("mouseenter","diq-stop-hit",function(){
      try{ map.getCanvas().style.cursor = "pointer"; }catch(e3){}
    });
    map.on("mouseleave","diq-stop-hit",function(){
      try{ map.getCanvas().style.cursor = ""; }catch(e4){}
    });
  }catch(e2){}
}

function onStopClick(e){
  var props;
  var id;
  var i;
  var stop = null;

  if(!e || !e.features || !e.features.length){ return; }

  props = e.features[0].properties || {};
  id = String(props.id || "");

  for(i = 0; i < activeDisplayStops.length; i++){
    if(String(activeDisplayStops[i].id) === id){
      stop = activeDisplayStops[i];
      break;
    }
  }

  if(stop){
    if(stop.__diqCluster){ showStopCluster(stop); }
    else{ showStop(stop); }
  }
}

function showStop(stop){
  var index = -1;
  var i;

  for(i = 0; i < activeStops.length; i++){
    if(String(activeStops[i].id) === String(stop.id)){
      index = i;
      break;
    }
  }

  if(index < 0){ index = 0; }

  updateStopInfo(stop,index);
  showStopCard(stop,index);

  /*
    V200 PASSIVE:
    Selection never calls map.easeTo. CameraAuthority in Drive Mode owns camera.
  */
}

function scanMinZoomForType(type){
  type = stopType(type || currentScanType);

  if(type === "food"){ return 13.35; }
  if(type === "coffee"){ return 13.05; }
  if(type === "wellness"){ return 13.0; }
  if(type === "shopping"){ return 12.65; }
  if(type === "scenic"){ return 12.45; }

  return 12.8;
}

function routeWindowAroundStop(stop,limitEachSide){
  var coords = flattenRouteCoordinates(getRouteCoords() || [],[]);
  var out = [];
  var bestIndex = -1;
  var bestDist = 9999;
  var i;
  var d;
  var from;
  var to;

  limitEachSide = finite(Number(limitEachSide)) ? Number(limitEachSide) : 24;

  if(!stop || !coords.length){ return out; }

  for(i = 0; i < coords.length; i++){
    d = milesBetween(
      [Number(coords[i][0]),Number(coords[i][1])],
      [Number(stop.lng),Number(stop.lat)]
    );

    if(finite(Number(d)) && Number(d) < bestDist){
      bestDist = Number(d);
      bestIndex = i;
    }
  }

  if(bestIndex < 0){ return out; }

  from = Math.max(0,bestIndex - limitEachSide);
  to = Math.min(coords.length - 1,bestIndex + limitEachSide);

  for(i = from; i <= to; i++){
    out.push(coords[i]);
  }

  return out;
}

function fitStops(stops){
  /*
    V200 PASSIVE:
    Find Stops does not call fitBounds or alter Follow/Free View.
    It only publishes a scan intent so the active Drive Mode can decide whether
    and how to move the camera.
  */
  if(!stops || !stops.length){ return; }

  try{
    window.dispatchEvent(new CustomEvent("driveiq:stopscan-results",{
      detail:{
        source:"find-stops-v200-passive",
        type:currentScanType,
        best:stops[0],
        stops:stops.slice(0,8)
      }
    }));
  }catch(e){}

  setText("diqModePill","SCAN");
  setText("diqSub","Recommended " + prettyType(currentScanType) + " stops ready");
}

function fetchJson(url,done){
  var finished = false;
  var timer = window.setTimeout(function(){
    if(finished){ return; }
    finished = true;
    done(null);
  },6500);

  try{
    fetch(url)
      .then(function(res){ return res.json(); })
      .then(function(data){
        if(finished){ return; }
        finished = true;
        window.clearTimeout(timer);
        done(data);
      })
      .catch(function(){
        if(finished){ return; }
        finished = true;
        window.clearTimeout(timer);
        done(null);
      });
  }catch(e){
    if(finished){ return; }
    finished = true;
    window.clearTimeout(timer);
    done(null);
  }
}

function stopIqMapReady(){
  var live = getMap();

  if(!live){ return false; }

  try{
    if(typeof live.getSource === "function" &&
       typeof live.getCenter === "function"){
      return true;
    }
  }catch(e1){}

  try{
    if(typeof live.loaded === "function" && live.loaded()){ return true; }
  }catch(e2){}

  try{
    if(typeof live.isStyleLoaded === "function" && live.isStyleLoaded()){ return true; }
  }catch(e3){}

  return false;
}

function clearPendingStopScan(){
  if(pendingStopScanTimer){
    window.clearTimeout(pendingStopScanTimer);
    pendingStopScanTimer = null;
  }

  pendingStopScanType = "";
  pendingStopScanAttempts = 0;
}

function queueStopScan(type){
  type = stopType(type);
  pendingStopScanType = type;
  pendingStopScanAttempts += 1;

  if(stopIqMapReady()){
    clearPendingStopScan();
    window.setTimeout(function(){
      scanStops(type);
    },0);
    return true;
  }

  if(pendingStopScanAttempts === 1){
    toast("Getting Stop IQ ready");
  }

  if(pendingStopScanAttempts >= pendingStopScanMaxAttempts){
    clearPendingStopScan();
    toast("Stop IQ could not connect to the map");
    return false;
  }

  if(pendingStopScanTimer){
    window.clearTimeout(pendingStopScanTimer);
  }

  pendingStopScanTimer = window.setTimeout(function(){
    pendingStopScanTimer = null;
    queueStopScan(type);
  },pendingStopScanDelayMs);

  return true;
}

function scanStops(type){
  var points;
  var pending = 0;
  var all = [];
  var token;
  var texts;
  var i;
  var j;

  type = stopType(type);
  currentScanType = type;

  if(scanBusy){
    toast("Stop scan already running");
    return;
  }

  if(!GEOAPIFY_KEY){
    toast("Missing Geoapify key");
    return;
  }

  if(!stopIqMapReady()){
    queueStopScan(type);
    return;
  }

  clearPendingStopScan();

  if(!getRouteGeo()){
    toast("Start a route first");
    return;
  }

  points = scanPoints();

  if(type === "wellness"){
    for(i = 0; i < points.length; i++){
      if(points[i]){
        if(String(points[i].lane || "") === "nearby"){ points[i].radius = 26000; }
        else if(String(points[i].lane || "") === "ahead"){ points[i].radius = 20000; }
        else{ points[i].radius = 18000; }
      }
    }
  }

  if(!points.length){
    toast("No route corridor yet");
    return;
  }

  scanBusy = true;
  scanToken += 1;
  token = scanToken;
  texts = searchTexts(type);

  setText("diqModePill","SCAN");
  setText("diqSub",type === "food" ? "Scanning food nearby first" : (type === "wellness" ? "Scanning wellness nearby first" : (type === "shopping" ? "Scanning shopping nearby first" : (type === "scenic" ? "Scanning scenic stops nearby first" : "Scanning " + type + " nearby first"))));
  setText("diqSigTraffic","Scanning " + (type === "wellness" ? "wellness" : type));
  setText("diqSigArrival","Looking nearby");
  setText("diqSigRoute","Finding best stop");
  toast(type === "food" ? "Scanning for food" : (type === "wellness" ? "Scanning wellness stops" : (type === "shopping" ? "Scanning shopping stops" : (type === "scenic" ? "Scanning scenic stops" : "Scanning for " + type))));

  function addFeatures(data,lane){
    var features = data && data.features ? data.features : [];
    var k;
    var stop;

    for(k = 0; k < features.length; k++){
      stop = normalizeFeature(features[k],type,lane);
      if(stop){ all.push(stop); }
    }
  }

  function oneDone(){
    pending -= 1;
    if(pending > 0){ return; }
    finish();
  }

  function finish(){
    if(token !== scanToken){ return; }

    scanBusy = false;

    all = mergeSponsoredStops(all,type,points);
    all = dedupeStops(all);
    all = suppressGenericFoodStops(all,type);

    var unfilteredStops = all.slice(0);
    all = filterRouteRelevantStops(all,type);

    /*
      If route filtering gets too aggressive, especially around sparse areas,
      bring back the best nearby food options while still scoring route proximity.
    */
    if(type === "food" && all.length < 3){
      all = relaxFoodResults(unfilteredStops);
      all = suppressGenericFoodStops(all,type);
    }

    all.sort(sortStops);
    all = all.slice(0,visibleStopLimit(type));

    for(i = 0; i < all.length; i++){
      all[i].routeDistMiles = nearestRouteDistanceToStop(all[i]);
      all[i].confidence = confidenceForStop(all[i],i);
      all[i].confidenceLabel = confidenceLabel(all[i].confidence);
      all[i].tag = stopTag(all[i],i);
    }

    var deliveredToAsk = deliverAskResults(all);
    drawStopPins(all);

    if(all.length){
      setText("diqSigTraffic",type === "food" ? String(all.length) + " food spots" : (type === "wellness" ? String(all.length) + " wellness spots" : (type === "shopping" ? String(all.length) + " shopping spots" : (type === "scenic" ? String(all.length) + " scenic spots" : String(all.length) + " " + type + " found"))));
      setText("diqSigArrival",String(all[0].name || "Best stop"));
      updateStopInfo(all[0],0);
      if(type === "food"){
        if(activeDisplayStops && activeDisplayStops.length && activeDisplayStops[0].__diqCluster){
          setText("diqRouteIntelText",clusterPreviewTitle(activeDisplayStops[0].clusterStops,activeDisplayStops[0].type));
          setText("diqRouteIntelSub",clusterPreviewSubtitle(activeDisplayStops[0].clusterStops,activeDisplayStops[0].type));
        }else{
          setText("diqRouteIntelText","Food Scan Active");
          setText("diqRouteIntelSub",String(all.length) + " food options found • best access first");
        }
      }
      if(type === "wellness"){
        setText("diqRouteIntelText","Wellness Scan Active");
        setText("diqRouteIntelSub",String(all.length) + " smoothie / nutrition options found • Best stop highlighted");
      }
      if(type === "shopping"){
        setText("diqRouteIntelText","Shopping Scan Active");
        setText("diqRouteIntelSub",String(all.length) + " shopping options found • Best stop highlighted");
      }
      if(type === "scenic"){
        setText("diqRouteIntelText","Scenic Scan Active");
        setText("diqRouteIntelSub",String(all.length) + " scenic options found • Best stop highlighted");
      }
      toast(String(all.length) + " stops found • " + bestStopSummary(all[0],type));
      fitStops(activeDisplayStops && activeDisplayStops.length ? activeDisplayStops : all);
      if(!deliveredToAsk){
        maybeSpeakScanSummary(all,type);
      }
    }else{
      if(!deliveredToAsk){ deliverAskResults([]); }
      setText("diqModePill","COCKPIT");
      if(type === "food"){
        setText("diqSub","No food found nearby. Try Fuel or scan farther ahead.");
        toast("No food found nearby");
      }else if(type === "wellness"){
        setText("diqSub","No smoothie or nutrition spots found on this corridor. Try Coffee or Food.");
        toast("No smoothie or nutrition spots found on this corridor");
      }else if(type === "shopping"){
        setText("diqSub","No boutiques, malls, or gift shops found nearby. Try Scenic or Coffee.");
        toast("No local shopping spots found nearby");
      }else if(type === "scenic"){
        setText("diqSub","No scenic stops found nearby. Try a wider route or Travel mode.");
        toast("No scenic stops found nearby");
      }else{
        setText("diqSub","No " + type + " found nearby");
        toast("No " + type + " found nearby");
      }
      setText("diqSigTraffic","No " + type + " found");
      setText("diqSigRoute","Try another stop type");
    }
  }

  for(i = 0; i < points.length; i++){
    (function(point){
      var lane = String(point && point.lane || "ahead");

      pending += 1;
      fetchJson(placesUrl(point,type),function(data){
        addFeatures(data,lane);
        oneDone();
      });

      for(j = 0; j < texts.length; j++){
        (function(term){
          pending += 1;
          fetchJson(fallbackUrl(point,type,term),function(data){
            addFeatures(data,lane);
            oneDone();
          });
        })(texts[j]);
      }
    })(points[i]);
  }

  window.setTimeout(function(){
    if(token === scanToken && scanBusy){
      pending = 0;
      finish();
    }
  },9500);
}

function openStopSheet(){
  var sheet = byId("diqStopSheet");
  if(sheet){ sheet.className = "diqSheet on"; }
}

function closeStopSheet(){
  var sheet = byId("diqStopSheet");
  if(sheet){ sheet.className = "diqSheet"; }
}



function stopChipLabel(type){
  type = stopType(type);

  if(type === "fuel"){ return "⛽ Fuel"; }
  if(type === "food"){ return "🍔 Food"; }
  if(type === "coffee"){ return "☕ Coffee"; }
  if(type === "wellness"){ return "🥤 Wellness"; }
  if(type === "shopping"){ return "🛍️ Shopping"; }
  if(type === "scenic"){ return "🧭 Scenic"; }
  if(type === "parking"){ return "🅿️ Parking"; }
  if(type === "walmart"){ return "🛒 Walmart"; }
  if(type === "grocery"){ return "🛒 Groceries"; }
  if(type === "pharmacy"){ return "💊 Pharmacy"; }
  if(type === "hotel"){ return "🛏️ Hotel"; }
  if(type === "restroom"){ return "🚻 Restroom"; }
  if(type === "quiet"){ return "😴 Quiet"; }
  if(type === "ev"){ return "⚡ EV"; }
  if(type === "repair"){ return "🔧 Repair"; }

  return "• Stops";
}

function applyStopChipLabels(){
  var chips = document.querySelectorAll(".diqStopChip");
  var i;
  var chip;
  var type;

  for(i = 0; i < chips.length; i++){
    chip = chips[i];
    type = chip.getAttribute("data-stop") || "";
    chip.textContent = stopChipLabel(type);
  }
}


function runStopChip(type){
  closeStopSheet();
  scanStops(type || "stops");
}

function bindStopChipButton(chip){
  if(!chip){ return; }
  if(chip.__diqFindStopsBound){ return; }

  chip.__diqFindStopsBound = true;
  chip.onclick = function(){
    var type = this.getAttribute("data-stop") || "stops";
    runStopChip(type);
  };
}

function ensureStopChipAfter(afterType,type,label){
  var grid = document.querySelector(".diqStopGrid");
  var existing;
  var after;
  var btn;

  if(!grid){ return null; }

  existing = grid.querySelector('[data-stop="' + type + '"]');
  if(existing){
    existing.textContent = label || stopChipLabel(type);
    bindStopChipButton(existing);
    return existing;
  }

  btn = document.createElement("button");
  btn.className = "diqStopChip";
  btn.type = "button";
  btn.setAttribute("data-stop",type);
  btn.textContent = label || stopChipLabel(type);
  btn.setAttribute("aria-label",String(label || stopChipLabel(type)));

  after = grid.querySelector('[data-stop="' + afterType + '"]');
  if(after && after.parentNode === grid && after.nextSibling){
    grid.insertBefore(btn,after.nextSibling);
  }else if(after && after.parentNode === grid){
    grid.appendChild(btn);
  }else{
    grid.appendChild(btn);
  }

  bindStopChipButton(btn);
  return btn;
}

function ensureLifestyleChips(){
  ensureWellnessChip();
  ensureStopChipAfter("grocery","pharmacy",stopChipLabel("pharmacy"));
  ensureStopChipAfter("hotel","shopping",stopChipLabel("shopping"));
  ensureStopChipAfter("shopping","scenic",stopChipLabel("scenic"));
  ensureStopIqSearch();
}

function ensureWellnessChip(){
  var grid = document.querySelector(".diqStopGrid");
  var existing;
  var coffee;
  var btn;

  if(!grid){ return null; }

  existing = grid.querySelector('[data-stop="wellness"]');
  if(existing){
    existing.textContent = stopChipLabel("wellness");
    bindStopChipButton(existing);
    return existing;
  }

  btn = document.createElement("button");
  btn.className = "diqStopChip";
  btn.type = "button";
  btn.setAttribute("data-stop","wellness");
  btn.textContent = stopChipLabel("wellness");
  btn.setAttribute("aria-label","Wellness stops");

  /*
    Put Wellness directly after Coffee so Aurora users see it immediately,
    instead of appending it at the end of the stop grid.
  */
  coffee = grid.querySelector('[data-stop="coffee"]');
  if(coffee && coffee.parentNode === grid && coffee.nextSibling){
    grid.insertBefore(btn,coffee.nextSibling);
  }else if(coffee && coffee.parentNode === grid){
    grid.appendChild(btn);
  }else{
    grid.appendChild(btn);
  }

  bindStopChipButton(btn);
  return btn;
}

function bindAllStopChips(){
  var chips = document.querySelectorAll(".diqStopChip");
  var i;

  ensureLifestyleChips();

  chips = document.querySelectorAll(".diqStopChip");

  for(i = 0; i < chips.length; i++){
    bindStopChipButton(chips[i]);
  }
}

function wireFindStops(){
  var btn = byId("diqFindStops");
  var close = byId("diqStopSheetClose");

  bindAllStopChips();

  if(btn){
    btn.onclick = function(){
      ensureLifestyleChips();
      bindAllStopChips();
      ensureStopIqSearch();
      openStopSheet();
      window.setTimeout(function(){
        if(stopSearchInputEl){
          try{ stopSearchInputEl.focus(); }catch(e){}
        }
      },180);
    };
  }

  if(close){
    close.onclick = function(){
      closeStopSheet();
    };
  }

  /*
    Shopify / WebView can sometimes hydrate the sheet late.
    These small retries make sure Wellness appears even if the sheet
    becomes available after the asset first boots.
  */
  window.setTimeout(bindAllStopChips,350);
  window.setTimeout(bindAllStopChips,1200);
  window.setTimeout(bindAllStopChips,2600);
}

function boot(){
  relabelStopIqEntryPoints();
  wireFindStops();
  ensureStopIqSearch();
  bindAskIqBridge();

  window.setTimeout(relabelStopIqEntryPoints,350);
  window.setTimeout(relabelStopIqEntryPoints,1200);

  window.DRIVE_IQ_FIND_STOPS = {
    version:"v200-passive-stop-provider",
    redraw:function(){
      drawStopPins(activeStops || []);
    },
    clear:function(){
      clearStopPins();
    },
    ensureWellness:function(){
      ensureWellnessChip();
      bindAllStopChips();
    },
    scanWellness:function(){
      scanStops("wellness");
    },
    ensureLifestyle:function(){
      ensureLifestyleChips();
      bindAllStopChips();
      applyStopChipLabels();
    },
    scanShopping:function(){
      scanStops("shopping");
    },
    scanScenic:function(){
      scanStops("scenic");
    },
    search:function(query){
      scanTypedStop(query);
    },
    askSearch:function(detail){
      handleAskSearch({detail:detail || {}});
    },
    askBridgeStatus:function(){
      return {
        bound:askBridgeBound,
        pendingRequestId:askPendingRequestId,
        pendingQuery:askPendingQuery
      };
    },
    searchWalmart:function(){
      scanTypedStop("Walmart");
    },
    openStopIq:function(){
      ensureStopIqSearch();
      openStopSheet();
    },
    searchResults:function(){
      return stopSearchResults.slice(0);
    },
    score:function(stop,query){
      return stopSearchCompositeScore(stop,query);
    },
    reason:function(stop,index){
      return stopSearchReason(
        stop,
        Number(index || 0),
        stopSearchResults
      );
    },
    labelChips:function(){
      applyStopChipLabels();
    },
    stops:function(){
      return activeStops || [];
    },
    preferredStops:function(){
      return safeParse(lsGet(LS.PREFERRED_STOPS)) || {};
    }
  };
}

if(document.readyState === "loading"){
  document.addEventListener("DOMContentLoaded",boot,false);
}else{
  boot();
}

})();

/* DRIVE IQ — STOP SHEET EMOJI LABEL PATCH */
(function(){
"use strict";

if(window.__DIQ_STOP_SHEET_EMOJI_LABEL_PATCH__){ return; }
window.__DIQ_STOP_SHEET_EMOJI_LABEL_PATCH__ = true;

function labelStopChips(){
  var labels = {
    fuel:"⛽ Fuel",
    gas:"⛽ Fuel",
    food:"🍔 Food",
    coffee:"☕ Coffee",
    wellness:"🥤 Wellness",
    shopping:"🛍️ Shopping",
    scenic:"🧭 Scenic",
    parking:"🅿️ Parking",
    walmart:"🛒 Walmart",
    grocery:"🛒 Groceries",
    groceries:"🛒 Groceries",
    hotel:"🛏️ Hotel",
    restroom:"🚻 Restroom",
    quiet:"😴 Quiet",
    ev:"⚡ EV",
    repair:"🔧 Repair"
  };

  var chips = document.querySelectorAll(".diqStopChip");
  var i;
  var chip;
  var type;

  for(i = 0; i < chips.length; i++){
    chip = chips[i];
    type = String(chip.getAttribute("data-stop") || "").toLowerCase();

    if(labels[type]){
      chip.textContent = labels[type];
      chip.setAttribute("aria-label", labels[type]);
    }
  }
}

function run(){
  labelStopChips();
}

/* V200 PASSIVE: no recurring chip-label timer. */

if(document.readyState === "loading"){
  document.addEventListener("DOMContentLoaded",run,false);
}else{
  run();
}

document.addEventListener("click",function(e){
  if(e && e.target && e.target.id === "diqFindStops"){
    window.setTimeout(run,80);
    window.setTimeout(run,350);
    window.setTimeout(run,900);
  }
},false);

})();

/* ========================================================================
   DRIVE IQ — FIND STOPS V20.3 / NEED SOMETHING SPACING GUARD
   Purpose:
   - Keep mobile "Need Something?" useful below the instruction card.
   - Hide the duplicate chip on wide/tablet/desktop layouts where the bottom
     Find Stops button is already visible.
   - Prevent broad containers from being mistaken for the chip.
   - Keep Find Stops behavior unchanged.
   - ES5-safe. No routing, scan, Mapbox, StoreKit, or stop-result logic changed.
======================================================================== */
(function(){
"use strict";

if(window.__DRIVE_IQ_FIND_STOPS_V20_3_NEED_SOMETHING_SPACING_GUARD__){ return; }
window.__DRIVE_IQ_FIND_STOPS_V20_3_NEED_SOMETHING_SPACING_GUARD__ = true;

function byId(id){ return document.getElementById(id); }

function textOf(el){
  return String(el && el.textContent || "").replace(/\s+/g," ").trim();
}

function hasNeedSomethingText(el){
  var t = textOf(el).toLowerCase();
  return t.indexOf("need something") > -1;
}

function isWideViewport(){
  try{
    return window.matchMedia &&
      (window.matchMedia("(min-width: 700px)").matches || window.matchMedia("(orientation: landscape)").matches);
  }catch(e){}
  return Number(window.innerWidth || 0) >= 700;
}

function isRealFindStopsDock(el){
  if(!el){ return false; }
  if(el.id === "diqFindStops"){ return true; }
  try{
    if(el.closest && el.closest(".diqDock")){ return true; }
  }catch(e){}
  return false;
}

function isIgnoredContainer(el){
  if(!el){ return true; }
  if(el.id === "diqApp" || el.id === "diqMap"){ return true; }

  try{
    if(el === document.body || el === document.documentElement){ return true; }
  }catch(e1){}

  try{
    if(el.closest && (el.closest(".diqSheet") || el.closest(".diqReportSheet") || el.closest(".diqThemeSheet") || el.closest(".diqDock"))){
      return true;
    }
  }catch(e2){}

  return false;
}

function isLikelyNeedSomethingChip(el){
  var tag;
  var rect;
  var txt = textOf(el);

  if(!el || !hasNeedSomethingText(el) || isIgnoredContainer(el) || isRealFindStopsDock(el)){ return false; }

  tag = String(el.tagName || "").toLowerCase();
  if(tag !== "button" && tag !== "a" && tag !== "div" && tag !== "span"){ return false; }

  if(txt.length > 80){ return false; }

  try{
    rect = el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    if(rect){
      if(Number(rect.width || 0) > 390){ return false; }
      if(Number(rect.height || 0) > 96){ return false; }
    }
  }catch(e){}

  return true;
}

function injectGuardCss(){
  var style;

  if(byId("diqNeedSomethingGuardStyle")){ return; }

  style = document.createElement("style");
  style.id = "diqNeedSomethingGuardStyle";
  style.textContent = [
    ".diqNeedSomethingGuard{",
      "position:fixed!important;",
      "left:calc(14px + env(safe-area-inset-left,0px))!important;",
      "top:calc(268px + env(safe-area-inset-top,0px))!important;",
      "right:auto!important;",
      "bottom:auto!important;",
      "z-index:73!important;",
      "max-width:calc(100vw - 92px)!important;",
      "min-height:34px!important;",
      "height:auto!important;",
      "padding:8px 12px!important;",
      "border-radius:999px!important;",
      "display:inline-flex!important;",
      "align-items:center!important;",
      "justify-content:center!important;",
      "gap:7px!important;",
      "white-space:nowrap!important;",
      "overflow:hidden!important;",
      "text-overflow:ellipsis!important;",
      "font-size:11px!important;",
      "line-height:1!important;",
      "font-weight:1000!important;",
      "letter-spacing:.25px!important;",
      "color:#fff!important;",
      "background:linear-gradient(180deg,rgba(5,12,22,.92),rgba(5,12,22,.72))!important;",
      "border:1px solid rgba(120,255,220,.28)!important;",
      "box-shadow:0 14px 30px rgba(0,0,0,.42),0 0 26px rgba(40,255,190,.14)!important;",
      "backdrop-filter:blur(16px)!important;",
      "-webkit-backdrop-filter:blur(16px)!important;",
      "pointer-events:auto!important;",
      "transform:none!important;",
    "}",
    ".diqNeedSomethingGuard:active{",
      "transform:translateY(1px) scale(.99)!important;",
    "}",
    "@media (max-width:390px){",
      ".diqNeedSomethingGuard{",
        "top:calc(262px + env(safe-area-inset-top,0px))!important;",
        "font-size:10px!important;",
        "padding:8px 10px!important;",
      "}",
    "}",
    "@media (max-width:340px){",
      ".diqNeedSomethingGuard{",
        "display:none!important;",
      "}",
    "}",
    "@media (min-width:700px){",
      ".diqNeedSomethingGuard{",
        "display:none!important;",
      "}",
    "}",
    "@media (orientation:landscape){",
      ".diqNeedSomethingGuard{",
        "display:none!important;",
      "}",
    "}"
  ].join("");

  try{ document.head.appendChild(style); }catch(e){}
}

function openFindStopsSheet(){
  var btn = byId("diqFindStops");
  var sheet = byId("diqStopSheet");

  try{
    if(btn && typeof btn.click === "function"){
      btn.click();
      return;
    }
  }catch(e1){}

  if(sheet){ sheet.className = "diqSheet on"; }
}

function addGuardClass(el){
  el.className = String(el.className || "").replace(/\bdiqNeedSomethingGuard\b/g,"").replace(/\s+/g," ");
  el.className = (String(el.className || "") + " diqNeedSomethingGuard").replace(/\s+/g," ").trim();
}

function guardNeedSomethingChip(){
  var nodes;
  var i;
  var el;
  var parent;
  var kept = false;

  injectGuardCss();

  try{
    nodes = document.querySelectorAll("button,a,div,span");
  }catch(e){
    nodes = [];
  }

  for(i = 0; i < nodes.length; i++){
    el = nodes[i];

    if(!isLikelyNeedSomethingChip(el)){ continue; }

    addGuardClass(el);

    /*
      Wide/desktop layouts already have the bottom Find Stops button. Keep the
      duplicate chip hidden there so it cannot crowd the top horizon.
    */
    if(isWideViewport()){
      try{ el.style.display = "none"; }catch(wideErr){}
      continue;
    }

    /*
      If multiple legacy chips are injected, keep the first mobile chip and
      hide the rest. This prevents doubled arrows or repeated buttons.
    */
    if(kept){
      try{ el.style.display = "none"; }catch(dupErr){}
      continue;
    }

    kept = true;

    try{ el.style.display = ""; }catch(displayErr){}

    el.setAttribute("aria-label","Need something? Open Find Stops");
    el.onclick = function(ev){
      if(ev && ev.preventDefault){ ev.preventDefault(); }
      if(ev && ev.stopPropagation){ ev.stopPropagation(); }
      openFindStopsSheet();
      return false;
    };

    /*
      Keep the label short and stable. Do not include arrows here; some legacy
      chips add their own arrow/pseudo-element and that caused double arrows.
    */
    while(el.firstChild){
      try{ el.removeChild(el.firstChild); }catch(removeErr){ break; }
    }
    try{ el.appendChild(document.createTextNode("Need Something?")); }
    catch(textErr){ el.textContent = "Need Something?"; }

    parent = null;
    try{ parent = el.parentNode; }catch(parentErr){ parent = null; }

    if(parent && parent.className && String(parent.className).indexOf("diqSignal") > -1){
      try{ parent.style.overflow = "hidden"; }catch(e3){}
    }
  }
}

function runGuard(){
  guardNeedSomethingChip();
}

window.setTimeout(runGuard,80);
window.setTimeout(runGuard,350);
window.setTimeout(runGuard,900);
window.setTimeout(runGuard,1800);
/* V200 PASSIVE: no recurring spacing-guard timer. */

window.addEventListener("resize",function(){
  window.setTimeout(runGuard,80);
},false);

document.addEventListener("click",function(e){
  if(e && e.target && e.target.id === "diqFindStops"){
    window.setTimeout(runGuard,120);
    window.setTimeout(runGuard,500);
  }
},false);

if(document.readyState === "loading"){
  document.addEventListener("DOMContentLoaded",runGuard,false);
}else{
  runGuard();
}

})();
